/**
 * SOCCER DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated soccer drills organized into skill levels:
 * 1. Beginner Dribbling (20 minutes) - Basic ball control and dribbling
 * 2. Intermediate Dribbling (31.5 minutes) - Speed, control, and skill moves
 * 3. Advanced Dribbling (45 minutes) - Advanced techniques and combinations
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 1001 (soccer drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'soccer'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Dribbling')
 * - points: Point value for completion (30-45 points based on duration)
 * - instructions: Simple explanation in age-appropriate language
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (varies by drill)
 * - equipment: Required equipment (e.g., 'Soccer ball')
 *
 * ============================================================================
 * PROMPT FOR GENERATING NEW DRILLS
 * ============================================================================
 *
 * When creating a new drill, follow these guidelines:
 *
 * 1. DRILL DURATION (approximate):
 *    - Beginner: 20 minutes (1200 seconds)
 *    - Intermediate: 31.5 minutes (1890 seconds)
 *    - Advanced: 45 minutes (2700 seconds)
 *
 * 2. INTRODUCTION:
 *    - Keep it SHORT (15-30 seconds max)
 *    - Don't repeat what's in the instructions field
 *    - Start directly with the first exercise
 *    - Example: "Let's work on speed control!" (not "Hi! Ready for...")
 *
 * 3. EXERCISE STRUCTURE:
 *    - 4-6 main exercises per drill (4-6 minutes each)
 *    - Each exercise: Guided instruction throughout (not "listen then practice")
 *    - Continuous coaching: Guide them as they do it
 *    - Instructions every 5-15 seconds to maintain engagement
 *
 * 4. INSTRUCTION STYLE:
 *    - Be DESCRIPTIVE and CLEAR for complex moves
 *    - Use step-by-step guidance for new techniques
 *    - Example: "Move your right foot in a circle around the left side of the ball"
 *    - NOT: "Do scissors" (too vague)
 *    - Remind them what they're doing throughout practice
 *
 * 5. PRACTICE SESSIONS:
 *    - Give ample time to master each skill (20-30 seconds minimum)
 *    - Repeat complex combinations multiple times
 *    - Provide countdown reminders during practice
 *    - Example: "15 seconds left... keep making circles around the ball!"
 *
 * 6. SKILL COMBINATIONS:
 *    - Break down complex moves step-by-step
 *    - Explain each part before combining them
 *    - Give multiple practice sessions for combinations
 *    - Remind them of the technique during practice
 *
 * 7. CONCLUSION:
 *    - Keep it SHORT (15 seconds max)
 *    - Mention what they learned
 *    - Explain how it helps in their sport
 *    - End with encouragement
 *    - Example: "You've learned step overs and scissors! These skills will help you beat defenders!"
 *
 * 8. LANGUAGE REQUIREMENTS:
 *    - Age-appropriate for 6-10 year olds
 *    - Self-contained instructions (remind them what they're doing)
 *    - Natural, coach-like conversational tone
 *    - No repetitive filler or excessive encouragement
 *    - Focus on skill development, not just motivation
 *
 * 9. TIMING GUIDELINES:
 *    - Introduction: 15-30 seconds
 *    - Each exercise: 4-6 minutes with guided instruction throughout
 *    - Practice sessions: 20-30 seconds minimum
 *    - Conclusion: 15 seconds
 *    - Total: Match the drill duration exactly
 *
 * 10. COMMON MISTAKES TO AVOID:
 *     - Don't separate "instruction time" from "practice time"
 *     - Don't use vague terminology without explanation
 *     - Don't add repetitive filler at the end
 *     - Don't make introduction too long
 *     - Don't forget to remind them what they're doing during practice
 *
 * ============================================================================
 *
 * TIMED INSTRUCTIONS GUIDELINES:
 * - Introduction: 15-30 seconds max (quick setup, no filler)
 * - 4-6 main exercises per drill (4-6 minutes each)
 * - Each exercise: Guided instruction throughout (not "listen then practice")
 * - Continuous coaching: Guide them as they do it, don't separate instruction from practice
 * - Natural reminders: Include what they're doing in each instruction
 * - Example: "Now try with your other foot... roll the ball back and forth with your left foot"
 * - No repetitive filler at the end - focus on skill development
 * - Instructions every 5-15 seconds to maintain engagement
 * - Age-appropriate language for 6-10 year olds
 *
 * EXERCISE STRUCTURE:
 * - Tell them what they're doing (5-10 seconds)
 * - Guide them through it as they do it (2-3 minutes of continuous guidance)
 * - Keep coaching during practice (1-2 minutes of continued guidance)
 * - Progressive difficulty within each drill
 * - Build on previous exercises
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains simple drill introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 * - Natural speech flow with strategic pauses
 * - Coach-like conversational tone throughout
 *
 * DRILL REQUIREMENTS:
 * - All drills doable at home/indoors
 * - No head exercises (safety for 6-year-olds)
 * - Equipment limited to main tool (soccer ball) + wall
 * - No shooting/long passes against walls (prevents damage)
 * - Appropriate for 6-10 year olds
 * - Focus on basic skills over advanced techniques
 * - Progressive skill development within each drill
 * - Self-contained instructions (remind them what they're doing)
 * - No "instruction time" vs "practice time" - all guided instruction
 *
 * CREATED: [Date] - Soccer drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with guided drills
 */

const soccerDrills = [
  // BEGINNER DRIBBLING (30 minutes)
  {
    id: 2011,
    name: 'Beginner Individual Dribbling',
    categories: ['Dribbling', 'Ball Control', 'Individual Drill'],
    sports: ['soccer'],
    difficulty: 'Beginner',
    skill:
      'Fundamental dribbling techniques, close control, and changing direction.',
    instructions:
      "This 30-minute individual session is your first step to becoming a great dribbler! We'll focus on the basics of keeping the ball close, using different parts of your feet, and building confidence with the ball at your feet.",
    equipment: '1 soccer ball',
    timeTotal: 1800,
    timedInstructions: [
      {
        time: 0,
        text: "Hey there, soccer star! Ready to become a dribbling master? Let's start with 'Foundation Touches'. Just you and the ball. Let's go!",
      },
      {
        time: 8,
        text: 'Start by tapping the ball back and forth between the inside of your feet. Little taps. Tick-tock, tick-tock. Get a feel for the ball.',
      },
      {
        time: 16,
        text: "Keep the ball right underneath you. Don't let it get away. You're the boss of the ball!",
      },
      {
        time: 22,
        text: 'Good! Now, put the bottom of your foot on the ball and roll it forward a little, then roll it back. Get used to using the sole of your shoe.',
      },
      {
        time: 30,
        text: "Now roll it from side to side, from your right foot to your left foot. You're dancing with the ball!",
      },
      {
        time: 37,
        text: "That's it! Keep that side-to-side roll going. Be smooth with it.",
      },
      {
        time: 42,
        text: "Let's go back to the tick-tocks. Inside of your right, inside of your left. Keep it going.",
      },
      {
        time: 48,
        text: 'Try to do ten in a row. Count them! One, two, three...',
      },
      {
        time: 53,
        text: 'Keep going! Four, five, six... soft touches!',
      },
      {
        time: 58,
        text: 'Seven, eight, nine, ten! Awesome. Now back to the sole rolls, side to side.',
      },
      {
        time: 64,
        text: 'Can you do it a little faster? Quick feet!',
      },
      {
        time: 68,
        text: 'Great job. Keep your knees bent and stay bouncy.',
      },
      {
        time: 73,
        text: "One minute in! You're doing great. Let's go back to tick-tocks again.",
      },
      {
        time: 78,
        text: 'This time, try to look up for just a second. A quick peek!',
      },
      {
        time: 83,
        text: "It's tricky, but that's how you see the game. Keep practicing.",
      },
      {
        time: 88,
        text: 'Now back to the sole rolls. Forward and back this time.',
      },
      {
        time: 93,
        text: "Control that ball. Don't let it get too far away.",
      },
      {
        time: 98,
        text: 'Awesome. Get ready for the two-minute mark.',
      },
      {
        time: 102,
        text: "Two minutes in! Let's go back to the tick-tocks. Can you do 20 in a row without losing control?",
      },
      {
        time: 109,
        text: "Let's count together! One, two, three, four, five...",
      },
      {
        time: 114,
        text: "Keep it up! Six, seven, eight, nine, ten... you're halfway there!",
      },
      {
        time: 120,
        text: 'Eleven, twelve, thirteen, fourteen, fifteen... soft touches!',
      },
      {
        time: 126,
        text: 'Sixteen, seventeen, eighteen, nineteen, twenty! You did it! Awesome.',
      },
      {
        time: 133,
        text: "Let's try that again. Another 20 tick-tocks. Let's see it.",
      },
      {
        time: 138,
        text: 'Keep your body over the ball. It helps with balance.',
      },
      {
        time: 143,
        text: 'If you mess up, no problem! Just start your count over.',
      },
      {
        time: 148,
        text: "You're building great control. This is the most important skill.",
      },
      {
        time: 153,
        text: 'Keep those feet moving quickly. Tick-tock, tick-tock.',
      },
      {
        time: 158,
        text: 'Excellent focus. Prepare for the next challenge.',
      },
      {
        time: 162,
        text: 'Get ready to start moving with the ball.',
      },
      {
        time: 166,
        text: "Halfway! Let's try to do the tick-tocks while moving very, very slowly forward. Just a little shuffle.",
      },
      {
        time: 174,
        text: "That's it! Take a small step with each tap. Tap and step, tap and step.",
      },
      {
        time: 180,
        text: "Keep the ball right in front of you. Don't let it get ahead.",
      },
      {
        time: 185,
        text: 'Dribble forward a few steps, then turn around and come back. Still using tick-tocks.',
      },
      {
        time: 192,
        text: 'This is how you dribble with control. Lots of little touches.',
      },
      {
        time: 197,
        text: "Let's try to look up for a second while you're moving. Quick peek!",
      },
      {
        time: 203,
        text: "Three minutes down. You're doing great. Keep that shuffle going.",
      },
      {
        time: 208,
        text: "Let's see if you can go a tiny bit faster. Just a little jog.",
      },
      {
        time: 213,
        text: 'But keep the ball close! The speed of your feet should match the speed of the ball.',
      },
      {
        time: 219,
        text: 'Awesome. Now slow it back down to a walk.',
      },
      {
        time: 223,
        text: 'Good control. This is what great players do. They can change their speed.',
      },
      {
        time: 229,
        text: "Let's try the sole rolls while moving forward. Roll it with your right, then roll it with your left.",
      },
      {
        time: 236,
        text: "It's a bit trickier, but you can do it. Roll and step.",
      },
      {
        time: 241,
        text: "Keep that ball under your spell. You're the magician.",
      },
      {
        time: 246,
        text: "Let's go back to the moving tick-tocks. That's our foundation.",
      },
      {
        time: 251,
        text: "Keep your head up as much as you can. See where you're going.",
      },
      {
        time: 256,
        text: 'Excellent work. Prepare for the last minute of this drill.',
      },
      {
        time: 261,
        text: 'Get ready to have some fun with it.',
      },
      {
        time: 265,
        text: 'Last minute! Just have fun with it. Tap the ball around, use the bottom of your foot. Get to know your best friend, the soccer ball.',
      },
      {
        time: 274,
        text: "Try a little spin. Try a little hop. It's your time to be creative.",
      },
      {
        time: 279,
        text: 'Can you stop the ball, then quickly pull it back with your sole?',
      },
      {
        time: 284,
        text: 'Good! Now can you do a tick-tock, then a sole roll?',
      },
      {
        time: 289,
        text: "Mix and match all the touches we've learned. Be a dribbling artist.",
      },
      {
        time: 294,
        text: 'Thirty seconds left! Show me your best moves.',
      },
      {
        time: 298,
        text: "Keep the ball close. That's the most important rule.",
      },
      {
        time: 303,
        text: "Let's see a few more quick taps. Fast feet!",
      },
      {
        time: 308,
        text: 'Now a smooth roll. Excellent.',
      },
      {
        time: 312,
        text: 'Get ready for the countdown. Final push!',
      },
      {
        time: 316,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great start.',
      },
      {
        time: 326,
        text: "No break, straight into the next drill! We're going on a journey with the 'Inside-Outside Dribble'.",
      },
      {
        time: 332,
        text: "Let's start by dribbling in a straight line using only the INSIDE of your right foot. Tap, tap, tap.",
      },
      {
        time: 339,
        text: 'Keep the ball close! After a few steps, turn around and come back.',
      },
      {
        time: 345,
        text: "Okay, now let's try the INSIDE of your left foot. It might feel a little funny, and that's okay!",
      },
      {
        time: 352,
        text: 'Now for the OUTSIDE of your foot. Dribble forward using only the outside of your right foot. Little pushes.',
      },
      {
        time: 360,
        text: "Great! Now the OUTSIDE of your left foot. Keep that ball right next to you, like it's on a leash.",
      },
      {
        time: 367,
        text: "Let's do that again. Inside of the right foot. Go.",
      },
      {
        time: 372,
        text: 'Now inside of the left. Keep that control.',
      },
      {
        time: 377,
        text: 'Outside of the right foot. Good.',
      },
      {
        time: 382,
        text: 'Outside of the left. Awesome.',
      },
      {
        time: 387,
        text: "You're learning to use all the different parts of your feet to dribble.",
      },
      {
        time: 392,
        text: "Let's see the inside of the right foot one more time.",
      },
      {
        time: 397,
        text: 'And the inside of the left.',
      },
      {
        time: 402,
        text: 'Outside of the right.',
      },
      {
        time: 407,
        text: "And outside of the left. You're a natural!",
      },
      {
        time: 412,
        text: 'Keep practicing that. The more you do it, the easier it gets.',
      },
      {
        time: 417,
        text: 'Prepare to combine them.',
      },
      {
        time: 420,
        text: "Two minutes down. Now, let's try to combine them. Dribble forward using both feet, alternating between inside and outside touches.",
      },
      {
        time: 429,
        text: "So it's inside right, outside left, inside left, outside right. A little dance!",
      },
      {
        time: 435,
        text: "Take your time with this. It's a tricky rhythm.",
      },
      {
        time: 440,
        text: "Keep the ball close. That's the most important thing.",
      },
      {
        time: 445,
        text: "Let's see it again. Inside, outside, inside, outside. Good.",
      },
      {
        time: 450,
        text: "You're doing great. This is how you learn to move the ball around defenders.",
      },
      {
        time: 455,
        text: 'Keep that head up for a quick peek when you can.',
      },
      {
        time: 460,
        text: "Let's see a few more steps of that inside-outside dribble.",
      },
      {
        time: 465,
        text: "Awesome. You're looking like a real dribbler.",
      },
      {
        time: 470,
        text: 'Keep that focus. Every touch is important.',
      },
      {
        time: 475,
        text: 'Prepare for the halfway mark.',
      },
      {
        time: 480,
        text: 'Halfway! Try to keep your head up and peek forward every few touches. See the field!',
      },
      {
        time: 487,
        text: 'Dribble forward, take a peek. Dribble, peek. Good.',
      },
      {
        time: 492,
        text: 'This is a huge skill. It helps you find your teammates and see where the defenders are.',
      },
      {
        time: 498,
        text: "Keep practicing that. Don't stare at the ball the whole time.",
      },
      {
        time: 503,
        text: "Let's see five good peeks in a row. Go.",
      },
      {
        time: 508,
        text: "Excellent. You're becoming a smarter player.",
      },
      {
        time: 513,
        text: "Let's go back to the inside-outside dribble. Keep that head up.",
      },
      {
        time: 518,
        text: "You're doing great. This is tough stuff.",
      },
      {
        time: 523,
        text: "Keep that ball under control. You're the boss.",
      },
      {
        time: 528,
        text: "Let's see a few more good touches with your head up.",
      },
      {
        time: 533,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 538,
        text: 'Get ready to show me your best dribbling.',
      },
      {
        time: 540,
        text: "Last minute! Let's see your best dribbling in a straight line. Keep it under perfect control.",
      },
      {
        time: 547,
        text: 'Use any part of your foot you want. Just keep that ball glued to you.',
      },
      {
        time: 552,
        text: "Let's see some good speed, but don't lose control.",
      },
      {
        time: 557,
        text: 'Thirty seconds left! Push yourself. Faster feet!',
      },
      {
        time: 562,
        text: 'Keep it close, keep it close!',
      },
      {
        time: 567,
        text: "Final push now. Give me everything you've got.",
      },
      {
        time: 572,
        text: "You're looking like a superstar!",
      },
      {
        time: 577,
        text: 'Get ready for the countdown.',
      },
      {
        time: 580,
        text: 'Ten seconds! Keep it close! And FREEZE! Awesome control.',
      },
      {
        time: 585,
        text: "Keep going, straight into the next drill! Let's learn to change direction with the 'Dribbling Weave'.",
      },
      {
        time: 590,
        text: 'Imagine you have three shoes lined up in a row in front of you, each a few big steps apart.',
      },
      {
        time: 597,
        text: 'Your job is to dribble in and out of the imaginary shoes in a zig-zag pattern. This is called weaving.',
      },
      {
        time: 603,
        text: 'Use the outside of your right foot to go right, and the outside of your left foot to go left. Keep the ball close as you turn.',
      },
      {
        time: 610,
        text: "Take your time at first. It's more important to keep control than to go fast.",
      },
      {
        time: 720,
        text: 'Two minutes in. You should be getting smoother now. Try to make your turns a little bit sharper.',
      },
      {
        time: 780,
        text: "Halfway point! Let's try to use only one foot. Can you weave through the shoes using only your right foot? Then try only your left!",
      },
      {
        time: 840,
        text: "Last minute! Back to using both feet. Let's see if you can go a little faster now, but don't lose that control!",
      },
      {
        time: 870,
        text: 'Ten seconds left! One more weave! Make it your best one! And TIME! Excellent turning.',
      },
      {
        time: 875,
        text: "No rest, keep working! Next up, we're learning to change speed with the 'Stop & Go'.",
      },
      {
        time: 880,
        text: 'This is a super fun and important move. Start by dribbling forward slowly for a few steps.',
      },
      {
        time: 885,
        text: 'Then, suddenly, put the bottom of your foot on the ball and stop it completely. FREEZE.',
      },
      {
        time: 890,
        text: 'As soon as you stop it, take your foot off and explode forward with a big touch and dribble fast for a few steps.',
      },
      {
        time: 897,
        text: 'So the pattern is: Slow dribble, STOP, fast dribble! This move tricks defenders.',
      },
      {
        time: 902,
        text: "Let's practice that. Slow, slow, STOP, FAST! Then go back to slow.",
      },
      {
        time: 1020,
        text: "Two minutes in. The 'stop' has to be instant. The ball should not move at all.",
      },
      {
        time: 1080,
        text: "Halfway! The 'go' has to be an explosion. Push the ball forward and chase after it.",
      },
      {
        time: 1140,
        text: "Last minute! Let's see some real changes of speed. Make it look like you're going from walking to sprinting.",
      },
      {
        time: 1170,
        text: "Ten seconds! Last one! Make it your fastest 'go' yet! And REST! Great acceleration.",
      },
      {
        time: 1175,
        text: "Keep it up, two drills left! Now for the 'Drag-Back Turn'.",
      },
      {
        time: 1180,
        text: 'This is a great way to turn away from a defender. Start by dribbling forward.',
      },
      {
        time: 1185,
        text: 'Then, put the sole of your foot on the ball and pull it backwards, behind you.',
      },
      {
        time: 1190,
        text: 'As you pull it back, turn your body the same direction, and take the ball with the same foot to dribble the way you came.',
      },
      {
        time: 1197,
        text: "So it's: Dribble forward, sole drag-back, turn, dribble away. Let's practice that.",
      },
      {
        time: 1202,
        text: 'Try it with your right foot first. Then, after a few tries, try it with your left foot.',
      },
      {
        time: 1380,
        text: "Two minutes down. The turn should be quick and sharp. As soon as you drag it back, you're gone!",
      },
      {
        time: 1440,
        text: 'Halfway! This move is great for protecting the ball. Use your body to shield it as you turn.',
      },
      {
        time: 1520,
        text: "Last minute! Let's see some smooth drag-back turns. Make it look like one fluid motion.",
      },
      {
        time: 1530,
        text: 'Ten seconds! Keep concentrating! And TIME! You just made yourself a trickier player.',
      },
      {
        time: 1535,
        text: "Last drill! Amazing focus. It's time for the 'Dribbling Freestyle'!",
      },
      {
        time: 1540,
        text: 'This is your chance to put everything together. For the next five minutes, you can dribble anywhere you want.',
      },
      {
        time: 1545,
        text: 'Practice all the things we learned today. Do some tick-tocks, some weaving, some stop-and-gos, and some drag-back turns.',
      },
      {
        time: 1552,
        text: 'There are no rules here except to keep the ball close and have fun. Be creative!',
      },
      {
        time: 1557,
        text: 'Try to link moves together. Can you do a stop-and-go right into a drag-back turn?',
      },
      {
        time: 1562,
        text: "Let's see some of those foundation touches. The tick-tocks. Keep them sharp.",
      },
      {
        time: 1567,
        text: "Good. Now let's see a stop-and-go. Slow... STOP... FAST!",
      },
      {
        time: 1572,
        text: 'Awesome. Now dribble around and give me a sharp drag-back turn.',
      },
      {
        time: 1577,
        text: "Excellent. Let's see some of that inside-outside dribbling now.",
      },
      {
        time: 1582,
        text: "Keep that ball close. You're in complete control.",
      },
      {
        time: 1587,
        text: "Now, let's see you weave through some imaginary defenders.",
      },
      {
        time: 1592,
        text: "Good tight turns. That's what we want to see.",
      },
      {
        time: 1597,
        text: "You're using all the skills we've practiced. This is fantastic.",
      },
      {
        time: 1602,
        text: 'Keep your head up as much as you can. See the space.',
      },
      {
        time: 1607,
        text: "Let's see another stop-and-go. Really explode out of it.",
      },
      {
        time: 1612,
        text: 'Great power! Now a smooth drag-back.',
      },
      {
        time: 1617,
        text: "You're looking like a real player. Keep that confidence.",
      },
      {
        time: 1622,
        text: "Let's try to link two moves together. A stop-and-go into a weave.",
      },
      {
        time: 1627,
        text: "Awesome! That's creative thinking.",
      },
      {
        time: 1632,
        text: "Keep the ball moving. Don't let it stop.",
      },
      {
        time: 1637,
        text: "Let's see some of those tick-tocks while you're moving.",
      },
      {
        time: 1642,
        text: 'Good. Now a sharp turn. Your choice.',
      },
      {
        time: 1647,
        text: "Excellent. You're making smart decisions.",
      },
      {
        time: 1652,
        text: "Keep that energy high. You're doing great.",
      },
      {
        time: 1657,
        text: 'Prepare for the one-minute mark.',
      },
      {
        time: 1660,
        text: 'One minute in. Are you using both feet? Are you keeping your head up?',
      },
      {
        time: 1665,
        text: "Let's challenge your weak foot. Try a drag-back with your other foot.",
      },
      {
        time: 1670,
        text: 'Good effort! The more you try, the better it gets.',
      },
      {
        time: 1675,
        text: "Now back to your strong foot. Let's see a perfect move.",
      },
      {
        time: 1680,
        text: "Keep that ball close. Remember Messi's glue foot!",
      },
      {
        time: 1685,
        text: "Let's see a change of pace. Slow it down... now speed up!",
      },
      {
        time: 1690,
        text: "Excellent. You're in control of the tempo.",
      },
      {
        time: 1695,
        text: 'Keep working hard. Every touch makes you better.',
      },
      {
        time: 1700,
        text: "Let's see another weave. Nice and tight.",
      },
      {
        time: 1705,
        text: 'Good. Now explode out of it.',
      },
      {
        time: 1710,
        text: "You're looking sharp. Keep that focus.",
      },
      {
        time: 1715,
        text: 'Prepare for the two-minute mark.',
      },
      {
        time: 1720,
        text: "Two minutes in. Try to change your speed and direction often. Don't be predictable.",
      },
      {
        time: 1727,
        text: 'Dribble left, then quickly cut right. Keep the defender guessing.',
      },
      {
        time: 1732,
        text: 'Now dribble forward, then a sharp drag-back turn.',
      },
      {
        time: 1737,
        text: "Good. Let's see a stop-and-go. Make it explosive.",
      },
      {
        time: 1742,
        text: 'The more you can change your rhythm, the harder you are to defend.',
      },
      {
        time: 1747,
        text: "Let's see some of that inside-outside dribbling again.",
      },
      {
        time: 1752,
        text: 'Good. Now a sharp turn. Your choice.',
      },
      {
        time: 1757,
        text: "Excellent. You're thinking like a soccer player.",
      },
      {
        time: 1762,
        text: "Keep that ball moving. Don't let it stop. Constant motion.",
      },
      {
        time: 1768,
        text: "Let's see a drag-back turn right now! Sharp!",
      },
      {
        time: 1773,
        text: "Good. Now dribble out of it. Let's see a stop-and-go.",
      },
      {
        time: 1779,
        text: 'Slow... STOP... FAST! Awesome explosion!',
      },
      {
        time: 1785,
        text: 'Keep dribbling. Show me some of that inside-outside dribbling we practiced.',
      },
      {
        time: 1792,
        text: "You're doing great. Your confidence is growing with every touch.",
      },
      {
        time: 1798,
        text: 'Get ready for the halfway point.',
      },
      {
        time: 1800,
        text: "Halfway! This is all about building confidence. The more you dribble, the more comfortable you'll feel.",
      },
      {
        time: 1807,
        text: "Trust your skills. You've practiced these moves. Now use them!",
      },
      {
        time: 1812,
        text: "Let's see a confident drag-back turn. Own it.",
      },
      {
        time: 1817,
        text: 'Good. Now a confident stop-and-go. Show me that speed.',
      },
      {
        time: 1822,
        text: 'The more confident you are, the better your moves will be.',
      },
      {
        time: 1827,
        text: "Don't be afraid to make a mistake. It's all part of learning.",
      },
      {
        time: 1832,
        text: "Let's see a creative combination. Link two moves together.",
      },
      {
        time: 1837,
        text: "Awesome! You're a playmaker.",
      },
      {
        time: 1842,
        text: "Keep that ball close. That's where confidence comes from.",
      },
      {
        time: 1847,
        text: "Let's see another good weave. Tight control.",
      },
      {
        time: 1852,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1857,
        text: "Get ready to show me everything you've got.",
      },
      {
        time: 1860,
        text: 'Last minute! Empty the tank! Show me all your best moves one last time.',
      },
      {
        time: 1865,
        text: "This is your highlight reel. Let's see it.",
      },
      {
        time: 1870,
        text: 'A sharp turn! A burst of speed! A cool trick!',
      },
      {
        time: 1875,
        text: 'Keep that energy high until the very end.',
      },
      {
        time: 1880,
        text: "Thirty seconds! Let's see some amazing control!",
      },
      {
        time: 1885,
        text: 'Faster feet, faster ball. Push yourself.',
      },
      {
        time: 1890,
        text: "Come on, you're almost there. Don't stop now.",
      },
      {
        time: 1895,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1900,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1910,
        text: 'Fantastic work, dribbling star! Your ball control has improved so much today!',
      },
    ],
  },
  {
    id: 2012,
    name: 'Intermediate Individual Dribbling',
    categories: [
      'Dribbling',
      'Ball Control',
      'Individual Drill',
      'Skill Moves',
    ],
    sports: ['soccer'],
    difficulty: 'Intermediate',
    skill:
      'Advanced dribbling techniques, executing skill moves, and dynamic changes of direction.',
    instructions:
      "This 30-minute individual session is designed to take your dribbling to the next level. We'll move beyond the basics and into the world of skill moves, quick turns, and deceptive dribbling to help you beat defenders.",
    equipment: '1 soccer ball',
    timeTotal: 1912,
    timedInstructions: [
      {
        time: 0,
        text: "Alright, dribbler, let's elevate your game. First up: 'Rhythmic Dribbling'. Just you and the ball. Let's find a rhythm.",
      },
      {
        time: 5,
        text: 'Start with a pattern: Two touches with the inside of your right foot, then a sole roll across your body to your left foot. Then two touches with the inside of your left, and a sole roll back.',
      },
      {
        time: 12,
        text: 'The rhythm is: Tap, tap, roll. Tap, tap, roll. Keep it continuous while moving slowly forward.',
      },
      {
        time: 18,
        text: 'This drill is about combining different parts of your foot smoothly. No clunky touches.',
      },
      {
        time: 23,
        text: "Let's focus on that right foot first. Tap, tap with the inside, then roll it across with your sole.",
      },
      {
        time: 29,
        text: 'Good. Now the left foot takes over. Tap, tap with the inside, then roll it back.',
      },
      {
        time: 35,
        text: 'Keep that pattern going. Right foot: tap, tap, roll. Left foot: tap, tap, roll.',
      },
      {
        time: 41,
        text: 'Try to get your head up for a quick peek after each roll. Look around!',
      },
      {
        time: 46,
        text: "That's it! Your feet are getting quicker. Keep the ball close to you.",
      },
      {
        time: 51,
        text: "Make the 'taps' soft and gentle. Make the 'roll' smooth.",
      },
      {
        time: 56,
        text: "Let's see five perfect patterns in a row. Count them out!",
      },
      {
        time: 61,
        text: 'One... good. Two... nice and smooth.',
      },
      {
        time: 65,
        text: 'Three... keep it up! Four... almost there!',
      },
      {
        time: 69,
        text: "Five! Awesome job. Let's keep that rhythm.",
      },
      {
        time: 74,
        text: "One minute down! You're looking like a pro. Your feet are dancing.",
      },
      {
        time: 80,
        text: 'Can you move a little faster now? Just a little bit quicker with the taps and rolls.',
      },
      {
        time: 86,
        text: "Keep your body over the ball. Don't let it get too far in front of you.",
      },
      {
        time: 90,
        text: "Two minutes in! Let's change the pattern. Now it's two touches with the OUTSIDE of your foot, then a drag-back turn to switch directions.",
      },
      {
        time: 99,
        text: 'So, with your right foot: outside tap, outside tap, then pull the ball back with your sole and turn.',
      },
      {
        time: 107,
        text: 'Then do it with your left foot! Outside tap, outside tap, drag-back and turn.',
      },
      {
        time: 114,
        text: 'This one is tricky! It helps you protect the ball and change direction fast.',
      },
      {
        time: 120,
        text: "Let's focus on the drag-back. A quick, sharp pull to get away from that imaginary defender.",
      },
      {
        time: 127,
        text: "Keep the ball close on those outside taps. Don't let it run away.",
      },
      {
        time: 132,
        text: "You're doing great. This is an advanced rhythm. Keep practicing it.",
      },
      {
        time: 137,
        text: "Let's see three good ones in a row with your right foot.",
      },
      {
        time: 141,
        text: 'Now three with your left foot. Come on, you got this!',
      },
      {
        time: 145,
        text: 'Awesome! Get ready to speed things up.',
      },
      {
        time: 150,
        text: "Halfway! Let's see if you can speed up the rhythm. The ball should be dancing at your feet.",
      },
      {
        time: 155,
        text: 'Faster feet, faster touches. Push the pace now.',
      },
      {
        time: 160,
        text: 'Go back to the first pattern: inside, inside, roll. But faster!',
      },
      {
        time: 166,
        text: 'Good. Now the second pattern: outside, outside, drag-back. Faster!',
      },
      {
        time: 172,
        text: "Keep your head up even when you're going fast. A quick peek is all you need.",
      },
      {
        time: 178,
        text: "You're doing great. Keep that intensity high.",
      },
      {
        time: 183,
        text: "Keep that rhythm: outside, outside, drag-back. Let's see it.",
      },
      {
        time: 189,
        text: 'Good. Now switch feet. Left foot: outside, outside, drag-back.',
      },
      {
        time: 195,
        text: 'The turn has to be sharp. Explode out of the drag-back.',
      },
      {
        time: 200,
        text: "Let's go back to the first pattern. Inside, inside, roll. But at a higher speed now.",
      },
      {
        time: 206,
        text: "Don't let the ball get away from you when you speed up. Control is everything.",
      },
      {
        time: 211,
        text: 'Keep your knees bent, stay low. It helps with balance.',
      },
      {
        time: 216,
        text: "Three minutes down! You're working hard. Keep that focus.",
      },
      {
        time: 221,
        text: "Let's see a mix now. Do one pattern, then the other. Keep the defender guessing.",
      },
      {
        time: 227,
        text: 'Inside, inside, roll... then outside, outside, drag-back. Link them!',
      },
      {
        time: 233,
        text: 'Awesome! Prepare for the final minute of this drill. Get ready to freestyle.',
      },
      {
        time: 240,
        text: 'Last minute! Freestyle with your touches. Mix in sole rolls, inside taps, outside pushes. Keep the ball and your feet constantly moving.',
      },
      {
        time: 247,
        text: 'Show me your best stuff. Combine the patterns we learned.',
      },
      {
        time: 253,
        text: 'Can you do a roll into a drag-back? Get creative!',
      },
      {
        time: 259,
        text: 'Thirty seconds left! Empty the tank on this drill!',
      },
      {
        time: 264,
        text: 'Faster, faster! Quick feet!',
      },
      {
        time: 270,
        text: "Final push now! Give it everything you've got.",
      },
      {
        time: 275,
        text: 'Show me your best combination of touches.',
      },
      {
        time: 280,
        text: "Keep the ball moving until the very end. Don't stop.",
      },
      {
        time: 285,
        text: 'Get ready for the countdown... nearly there!',
      },
      {
        time: 290,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great warm-up.',
      },
      {
        time: 300,
        text: "Quick water break! Next up, a classic skill move: 'The Step-Over'.",
      },
      {
        time: 305,
        text: "Let's learn this move to trick defenders.",
      },
      {
        time: 310,
        text: 'Start by dribbling forward slowly. As you approach an imaginary defender, swing your right foot AROUND the front of the ball, from the inside to the outside.',
      },
      {
        time: 318,
        text: "After you fake with your right foot, take the ball away with the OUTSIDE of your left foot. It's a fake one way, go the other.",
      },
      {
        time: 325,
        text: "So it's: Dribble, step-over with right, take with left. Practice that motion.",
      },
      {
        time: 330,
        text: "Now let's try the other side. Step-over with your left foot, and take it away with the outside of your right.",
      },
      {
        time: 335,
        text: 'The step-over has to be a big, convincing fake. Really swing that leg.',
      },
      {
        time: 420,
        text: 'Two minutes down. The step-over needs to be more convincing. Sell the fake with your body! Dip your shoulder as you go around the ball.',
      },
      {
        time: 428,
        text: "The 'take' touch has to be quick and explosive. Get away from the defender.",
      },
      {
        time: 433,
        text: "Let's see five good step-overs with your right foot.",
      },
      {
        time: 335,
        text: 'The step-over has to be a big, convincing fake. Really swing that leg.',
      },
      {
        time: 340,
        text: "Good. Let's practice the motion. Dribble, step-over right, take left. Reset.",
      },
      {
        time: 345,
        text: 'Now the other way. Dribble, step-over left, take right. Reset.',
      },
      {
        time: 350,
        text: "The 'take' touch is just as important as the fake. It has to be an explosive push into space.",
      },
      {
        time: 355,
        text: "Let's focus on the body movement. Dip your shoulder as you swing your leg around the ball. Sell it!",
      },
      {
        time: 360,
        text: 'One minute in. Your fakes are looking more believable. Keep that up.',
      },
      {
        time: 365,
        text: "Let's see three sharp ones in a row with your right foot. Go.",
      },
      {
        time: 370,
        text: "Good. Now three sharp ones with your left. Don't neglect your weaker side.",
      },
      {
        time: 375,
        text: 'Remember to keep your head up before the move. You need to see the defender to know when to do it.',
      },
      {
        time: 380,
        text: 'The rhythm is key. Dribble, dribble, step-over, explode. Find that flow.',
      },
      {
        time: 385,
        text: "Don't let the ball get stuck under your feet. Keep it slightly in front to set up the move.",
      },
      {
        time: 390,
        text: "Let's try to do it a little faster now. Quicker feet, quicker fake.",
      },
      {
        time: 395,
        text: "That's it! The speed makes the move much harder to defend.",
      },
      {
        time: 400,
        text: 'Keep your balance. Your standing foot is your anchor. Stay strong on it.',
      },
      {
        time: 405,
        text: "Let's see another right-footed one. Make it your best yet.",
      },
      {
        time: 410,
        text: 'Now a left-footed one. Just as good!',
      },
      {
        time: 415,
        text: "Awesome. You're getting ready for the next level of this move.",
      },
      {
        time: 420,
        text: 'Two minutes down. The step-over needs to be more convincing. Sell the fake with your body! Dip your shoulder as you go around the ball.',
      },
      {
        time: 428,
        text: "The 'take' touch has to be quick and explosive. Get away from the defender.",
      },
      {
        time: 433,
        text: "Let's see five good step-overs with your right foot.",
      },
      {
        time: 438,
        text: 'Now five with your left. Keep the quality high.',
      },
      {
        time: 480,
        text: "Halfway! Let's try a double step-over. Right foot around, then left foot around, then take it away. This is advanced!",
      },
      {
        time: 488,
        text: "It's a quick one-two fake. It will really confuse the defender.",
      },
      {
        time: 493,
        text: 'Take your time with this one. Get the rhythm of the feet right.',
      },
      {
        time: 498,
        text: "Let's break it down. Right foot over, left foot over, then explode with the outside of your right foot.",
      },
      {
        time: 504,
        text: 'Good. Now try the other way. Left foot over, right foot over, explode with the outside of your left.',
      },
      {
        time: 510,
        text: 'This move is all about quick feet. The ball barely moves while your feet are a blur.',
      },
      {
        time: 516,
        text: 'Keep practicing that sequence. The more you do it, the smoother it will become.',
      },
      {
        time: 522,
        text: "Focus on staying balanced. Don't lean too far over the ball.",
      },
      {
        time: 528,
        text: "Let's see three good double step-overs in a row. Challenge yourself.",
      },
      {
        time: 534,
        text: 'Awesome. The speed is increasing. Prepare for the final minute of this drill.',
      },
      {
        time: 540,
        text: "Last minute! Let's see some explosive step-overs. The 'take' touch after the fake should be a burst of speed.",
      },
      {
        time: 547,
        text: 'Dribble with pace, then a sharp step-over. Game speed!',
      },
      {
        time: 552,
        text: 'Come on, push yourself. Faster, sharper!',
      },
      {
        time: 610,
        text: 'Ten seconds! Keep it sharp! And FREEZE! Excellent deception.',
      },
      {
        time: 615,
        text: "Grab a drink. Great job. Now for a sharp change of direction: 'The L-Turn'.",
      },
      {
        time: 620,
        text: 'This move is perfect for turning away from pressure. Start with the ball slightly in front of you.',
      },
      {
        time: 625,
        text: 'Put your right foot on the ball and drag it back towards your body. As it passes your standing leg, use the inside of the SAME foot to push it behind your standing leg.',
      },
      {
        time: 635,
        text: "The ball makes an 'L' shape. Drag back, push sideways. It's a quick 90-degree turn.",
      },
      {
        time: 642,
        text: 'Practice that motion. Drag, push. Drag, push. Then try it with your left foot.',
      },
      {
        time: 648,
        text: "It's a two-part move, but it should feel like one smooth action.",
      },
      {
        time: 653,
        text: "Let's focus on the 'drag' first. A soft pull with the sole of your foot.",
      },
      {
        time: 658,
        text: "Now the 'push'. A sharp tap with the inside of your foot behind your leg.",
      },
      {
        time: 663,
        text: "Let's put it together. Drag, push. Good. Reset.",
      },
      {
        time: 668,
        text: 'Now with the left foot. Drag, push. Keep your balance.',
      },
      {
        time: 673,
        text: 'The key is to hop on your standing foot as you do the push. It creates space.',
      },
      {
        time: 678,
        text: "Let's see five in a row with your right foot. Make them clean.",
      },
      {
        time: 683,
        text: 'Good. Now five with your left. The repetition is important.',
      },
      {
        time: 688,
        text: 'One minute in. Your turns are getting sharper.',
      },
      {
        time: 693,
        text: 'Try to keep your head up as you do it. A quick glance.',
      },
      {
        time: 698,
        text: 'This move is great for shielding the ball from a defender on your back.',
      },
      {
        time: 703,
        text: "Let's see a bit more speed now. A quicker drag, a quicker push.",
      },
      {
        time: 708,
        text: "That's it! The speed makes it much more effective.",
      },
      {
        time: 713,
        text: 'Keep that quality high. Prepare for the two-minute mark.',
      },
      {
        time: 720,
        text: "Two minutes in. The L-Turn should be one fluid motion. It's a great way to shield the ball while turning.",
      },
      {
        time: 726,
        text: 'Use your body to protect the ball as you execute the turn.',
      },
      {
        time: 780,
        text: 'Halfway point! Now, try to do it after a few dribbling steps. Dribble, then execute a sharp L-Turn to change direction.',
      },
      {
        time: 726,
        text: 'Use your body to protect the ball as you execute the turn.',
      },
      {
        time: 731,
        text: "Let's try to do it after a few dribbling steps now. Dribble, L-Turn, accelerate.",
      },
      {
        time: 737,
        text: "Good. The move is much more effective when you're already moving.",
      },
      {
        time: 742,
        text: 'Keep practicing that sequence. Dribble, turn, go.',
      },
      {
        time: 747,
        text: "Let's see it with your left foot now. Dribble, L-Turn left.",
      },
      {
        time: 752,
        text: 'The more you practice, the more natural it will feel in a game.',
      },
      {
        time: 757,
        text: 'Keep that focus. Every repetition counts.',
      },
      {
        time: 762,
        text: "Let's see a bit more speed on the entry dribble.",
      },
      {
        time: 767,
        text: 'Good. Now a sharp turn. Be decisive.',
      },
      {
        time: 772,
        text: "Excellent. You're almost at the halfway point.",
      },
      {
        time: 777,
        text: 'Prepare to add the dribbling element.',
      },
      {
        time: 780,
        text: 'Halfway point! Now, try to do it after a few dribbling steps. Dribble, then execute a sharp L-Turn to change direction.',
      },
      {
        time: 787,
        text: 'This makes it game-realistic. Dribble at a defender, then turn away into space.',
      },
      {
        time: 792,
        text: "Let's see a good, sharp turn now. No lazy movements.",
      },
      {
        time: 797,
        text: 'The first touch after the turn should be an explosion of speed.',
      },
      {
        time: 802,
        text: "Let's see five perfect ones. Dribble, turn, explode. Go.",
      },
      {
        time: 807,
        text: "Keep the quality high. Don't get sloppy.",
      },
      {
        time: 812,
        text: "That's it. You're looking like a real player.",
      },
      {
        time: 817,
        text: 'Keep your head up. Scan for space before you turn.',
      },
      {
        time: 822,
        text: 'This is how you create space for yourself on the field.',
      },
      {
        time: 827,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 832,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 837,
        text: 'Get ready to push the speed.',
      },
      {
        time: 840,
        text: "Last minute! Let's see some speed. How quickly can you execute the turn and accelerate into a new space?",
      },
      {
        time: 846,
        text: 'The first step after the turn should be an explosion. Go!',
      },
      {
        time: 851,
        text: 'Come on, push the pace. Faster turns, faster acceleration.',
      },
      {
        time: 856,
        text: 'Thirty seconds left. Empty the tank on this drill.',
      },
      {
        time: 900,
        text: 'Ten seconds left! One more! Make it your sharpest one yet! And TIME! Excellent control.',
      },
      {
        time: 905,
        text: "Quick drink! Next up, a legendary move: 'The Cruyff Turn'.",
      },
      {
        time: 910,
        text: 'This is a classic fake. Start by shaping your body like you are going to kick or pass the ball forward.',
      },
      {
        time: 916,
        text: "But instead of kicking it, bring your kicking foot behind your standing leg and use the inside of your foot to 'chop' the ball behind you.",
      },
      {
        time: 924,
        text: "As you chop it, you pivot your body and explode in the new direction. It's a 180-degree turn.",
      },
      {
        time: 930,
        text: "Let's practice that. Dribble forward, fake the kick, and chop it behind you. It's all about the surprise!",
      },
      {
        time: 935,
        text: 'Try with your other foot. Fake with the left, chop with the left.',
      },
      {
        time: 940,
        text: 'The fake is the most important part. You have to make the imaginary defender believe you are shooting.',
      },
      {
        time: 945,
        text: "Swing your arms and body like you're about to shoot.",
      },
      {
        time: 950,
        text: "The 'chop' needs to be precise, tucking the ball right behind your heel so you can run onto it.",
      },
      {
        time: 955,
        text: "Let's break it down. Dribble, plant your standing foot, fake the kick, chop, and pivot. All one motion.",
      },
      {
        time: 1000,
        text: 'Good. The pivot is just as important as the chop. You have to turn your body quickly.',
      },
      {
        time: 1005,
        text: "Let's see five good ones with your right foot. Focus on the fake.",
      },
      {
        time: 1010,
        text: 'Now five with your left. Make the fake just as convincing.',
      },
      {
        time: 1015,
        text: "Excellent. You're getting the hang of it. Let's add some speed.",
      },
      {
        time: 1020,
        text: "Two minutes in. Let's see some dynamic Cruyff turns. A big fake and a quick escape. Make it look smooth.",
      },
      {
        time: 1025,
        text: 'Dribble with a bit of pace before you execute the turn.',
      },
      {
        time: 1030,
        text: 'The change of direction should be sharp and sudden.',
      },
      {
        time: 1035,
        text: 'Keep practicing. The more you do it, the more it becomes a natural reaction.',
      },
      {
        time: 1080,
        text: "Halfway! Let's see five perfect Cruyff turns in a row. Dribble, fake, chop, explode.",
      },
      {
        time: 1085,
        text: 'The explosion after the turn is what makes the move so effective. Create that space.',
      },
      {
        time: 1090,
        text: "Don't just turn; turn with a purpose. Accelerate into the new space.",
      },
      {
        time: 1140,
        text: 'Last minute! Now at game speed. Dribble with pace, execute the turn sharply, and accelerate away.',
      },
      {
        time: 1145,
        text: 'Come on, push the tempo. This is how you beat players in a match.',
      },
      {
        time: 1150,
        text: "Thirty seconds left. Give me everything you've got.",
      },
      {
        time: 1155,
        text: 'Faster, cleaner, sharper!',
      },
      {
        time: 1200,
        text: 'Ten seconds! Last one! Make the defender look silly! And REST! Beautiful turn.',
      },
      {
        time: 1205,
        text: "Sip of water! Two drills left. Now for the 'Weave & Explode'.",
      },
      {
        time: 1210,
        text: 'Just like our beginner weave, imagine you are dribbling through a line of shoes.',
      },
      {
        time: 1215,
        text: 'But this time, as you make your final turn out of the weave, you must take a big, explosive touch and accelerate forward for five yards.',
      },
      {
        time: 1223,
        text: "This drill combines tight control with a sudden change of pace. You're lulling the defender to sleep in the weave, then exploding past them.",
      },
      {
        time: 1230,
        text: "So it's: Weave, weave, weave, EXPLODE! Then dribble back and do it again.",
      },
      {
        time: 1380,
        text: 'Two minutes down. The explosive touch should be with the outside of your foot, pushing the ball into space.',
      },
      {
        time: 1385,
        text: "Let's see five good repetitions. The contrast between slow and fast should be obvious.",
      },
      {
        time: 1440,
        text: "Halfway! Let's see a real difference in speed. Slow and controlled in the weave, then a lightning-fast burst.",
      },
      {
        time: 1445,
        text: "Try to keep your head up during the weave to see the space you're going to attack.",
      },
      {
        time: 1450,
        text: "This is a great drill for game situations where you're in a crowded area and then suddenly see an opening.",
      },
      {
        time: 1520,
        text: 'Last minute! Challenge yourself. How quickly can you get through the weave and how fast is your explosion?',
      },
      {
        time: 1525,
        text: 'Push the pace now. Every repetition should be at your maximum speed.',
      },
      {
        time: 1530,
        text: 'Thirty seconds left. Empty the tank on this drill!',
      },
      {
        time: 1535,
        text: 'Come on, one more good one! Weave and explode!',
      },
      {
        time: 1540,
        text: 'Ten seconds! Keep concentrating! And TIME! Great change of pace.',
      },
      {
        time: 1545,
        text: "Final break! Amazing focus. Last drill is the 'Skill Move Combo'!",
      },
      {
        time: 1550,
        text: 'This is your time to be a creator. For the next five minutes, you must link the skills we learned today into combinations.',
      },
      {
        time: 1558,
        text: 'Try a step-over that flows right into an L-Turn. Or a Cruyff turn that sets you up for a weave.',
      },
      {
        time: 1565,
        text: "There are no rules here except to be creative and fluid. Don't just do one move and stop. Make the moves flow together.",
      },
      {
        time: 1572,
        text: 'How many different moves can you link together in one continuous dribble? Challenge yourself!',
      },
      {
        time: 1577,
        text: "Let's start with a simple two-move combo. Step-over into an L-Turn. Go.",
      },
      {
        time: 1582,
        text: 'Good. Now try a Cruyff turn into a step-over.',
      },
      {
        time: 1587,
        text: 'The key is to make the last touch of the first move the setup for the second move.',
      },
      {
        time: 1592,
        text: "Let's see a double step-over into a burst of speed.",
      },
      {
        time: 1597,
        text: "Keep your head up. You're a playmaker, you need to see the field.",
      },
      {
        time: 1602,
        text: "Don't be afraid to try your weak foot. A weak foot L-Turn is a great skill.",
      },
      {
        time: 1607,
        text: "The ball is your dance partner. Don't step on its toes.",
      },
      {
        time: 1612,
        text: 'Keep that intensity. Your feet should be constantly moving, even between moves.',
      },
      {
        time: 1617,
        text: "Let's see a L-Turn into a Cruyff turn. A double turn!",
      },
      {
        time: 1622,
        text: "That's it! You're thinking like a creative player.",
      },
      {
        time: 1627,
        text: 'Keep the ball close. The best players have the ball on a string.',
      },
      {
        time: 1632,
        text: "Let's see another combo. Your choice this time. Surprise me.",
      },
      {
        time: 1637,
        text: 'Excellent. The more you practice linking moves, the more unpredictable you become.',
      },
      {
        time: 1642,
        text: 'Keep your body low and your center of gravity down. It helps with quick changes of direction.',
      },
      {
        time: 1647,
        text: "Let's see a step-over with your weak foot now.",
      },
      {
        time: 1652,
        text: 'Good. Now link it with another move.',
      },
      {
        time: 1657,
        text: 'Prepare for the one-minute mark. Keep that quality high.',
      },
      {
        time: 1660,
        text: 'One minute in. The transition between moves is key. Your last touch of one move should be the first touch of the next.',
      },
      {
        time: 1665,
        text: "Let's see a combo. Cruyff turn into an L-Turn. Make it smooth.",
      },
      {
        time: 1672,
        text: 'Good. Now a step-over into a burst of speed. Explode out of the move.',
      },
      {
        time: 1678,
        text: "Keep your head up. Even when you're linking moves, you need to see the field.",
      },
      {
        time: 1685,
        text: "Don't be afraid to try your weak foot. A weak foot step-over is a great tool.",
      },
      {
        time: 1692,
        text: 'The ball is your dance partner. Lead the dance.',
      },
      {
        time: 1700,
        text: 'Keep that intensity. Your feet should be constantly moving.',
      },
      {
        time: 1710,
        text: "Let's see a double step-over now. Quick feet!",
      },
      {
        time: 1720,
        text: "Two minutes in. Try to perform the moves at game speed. Don't be afraid to make mistakes. That's how you learn.",
      },
      {
        time: 1728,
        text: "Mistakes are proof that you're trying. Push your limits.",
      },
      {
        time: 1735,
        text: "Let's link three moves. Step-over, L-Turn, explode. Go.",
      },
      {
        time: 1745,
        text: 'Keep your body low and balanced as you transition between the moves.',
      },
      {
        time: 1755,
        text: "Now, freestyle again. What's your favorite combo? Show it to me.",
      },
      {
        time: 1765,
        text: 'Sell the fakes! Make the imaginary defender look silly.',
      },
      {
        time: 1775,
        text: 'The defender is so confused, thought you were going to the left, but you went to the right.',
      },
      {
        time: 1800,
        text: 'Halfway! This is where you build the muscle memory to use these skills in a real game without thinking.',
      },
      {
        time: 1808,
        text: 'Every repetition is programming your brain and your feet to work together.',
      },
      {
        time: 1815,
        text: "Let's see a Cruyff turn with your strong foot. Fake a shot - swing your kicking leg back as if you're going to kick the ball. Instead of kicking, use the inside of your foot to pull the ball behind your standing leg. Then, push the ball forward with your strong foot. You're now facing the opposite direction. This move tricks defenders!",
      },
      {
        time: 1830,
        text: "Try it again. Fake a shot - swing your kicking leg back as if you're going to kick the ball. Instead of kicking, use the inside of your foot to pull the ball behind your standing leg. Then, push the ball forward with your strong foot. You're now facing the opposite direction. This move tricks defenders! Combine a turn with a straight-line dribble. Turn, then explode. Practice this move again and again til you've got it down.",
      },
      {
        time: 1850,
        text: 'Keep that ball on a string. Perfect control. Keep going.',
      },
      {
        time: 1860,
        text: 'Last minute! Empty the tank! Show me your best combination of the day.',
      },
      {
        time: 1867,
        text: "Let's see those combinations... Fantastic job!",
      },
      {
        time: 1874,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1880,
        text: "Thirty seconds! Let's see some real flair and confidence!",
      },
      {
        time: 1885,
        text: 'Link three moves together now, any moves your want! Go!',
      },
      {
        time: 1890,
        text: "Keep it going! Don't stop moving!",
      },
      {
        time: 1895,
        text: 'Final fifteen seconds! Make it a masterpiece!',
      },
      {
        time: 1900,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1910,
        text: "Incredible work! You've added some serious weapons to your dribbling arsenal. Be proud of that effort. Amazing job today!",
      },
    ],
  },
  {
    id: 2013,
    name: 'Advanced Individual Dribbling',
    categories: [
      'Dribbling',
      'Ball Control',
      'Individual Drill',
      'Skill Moves',
      'Deception',
    ],
    sports: ['soccer'],
    difficulty: 'Advanced',
    skill:
      'Mastering elite skill moves, deceptive dribbling, and reactive decision-making at speed.',
    instructions:
      "This 30-minute individual session is for the elite dribbler looking to master the game's most effective and deceptive skill moves. We'll focus on flawless execution at speed, quick cognitive reactions, and the ability to combine moves to become truly unstoppable.",
    equipment: '1 soccer ball',
    timeTotal: 1920,
    timedInstructions: [
      {
        time: 0,
        text: "First up: 'Freestyle Flow'. For five minutes, the ball never stops moving. Let's see your amazing touch!",
      },
      {
        time: 5,
        text: "Let's start flowing. Use every part of your feet: inside, outside, bottom, and even your laces. Talk to the ball with your feet.",
      },
      {
        time: 10,
        text: "Let's link your touches. A roll with the bottom of your foot right into a push with the outside. Make it smooth.",
      },
      {
        time: 15,
        text: 'Keep your body low and your feet buzzing. You and the ball are a team.',
      },
      {
        time: 20,
        text: "Let's change the speed. Dribble slow, slow, then EXPLODE forward for three steps! Good, now bring it back under control.",
      },
      {
        time: 25,
        text: 'Keep your head up! A quick peek every few seconds to see the whole field.',
      },
      {
        time: 30,
        text: "Let's try a combo: roll it with your right sole, then tap it with the outside of your left. Awesome!",
      },
      {
        time: 35,
        text: "Good. Now a drag-back pull into an inside tap. Make it look like you're dancing.",
      },
      {
        time: 40,
        text: 'Stay light on your feet. No stomping! Be a soccer ninja.',
      },
      {
        time: 45,
        text: "Keep that ball super close. Imagine it's on a short string from your shoe.",
      },
      {
        time: 50,
        text: "Let's see another burst of speed, then a quick stop with the sole of your foot. Freeze it!",
      },
      {
        time: 55,
        text: 'Now a pattern: inside tap right, inside tap left, outside push right, go!',
      },
      {
        time: 60,
        text: 'One minute in! The ball is part of you now. Perfect control.',
      },
      {
        time: 65,
        text: 'Keep scanning the field. Head up, eyes open.',
      },
      {
        time: 70,
        text: 'Challenge time! How many times can you tap the ball in 5 seconds? Go!',
      },
      {
        time: 75,
        text: 'Good work! Now focus on changing direction. Every five touches, a sharp cut left or right.',
      },
      {
        time: 80,
        text: "Push the pace now. We're getting warmer.",
      },
      {
        time: 85,
        text: 'Keep those arms out for balance. It helps you stay steady when you turn.',
      },
      {
        time: 90,
        text: "Two minutes in! Let's turn up the heat. Faster touches, faster feet. Let's build that muscle memory.",
      },
      {
        time: 95,
        text: "Don't let the ball get away! Own the space around your feet.",
      },
      {
        time: 100,
        text: 'How fast can you move your feet while keeping the ball perfectly still? Try it!',
      },
      {
        time: 105,
        text: "Let's see a creative combo. Three different touches in a row.",
      },
      {
        time: 110,
        text: 'Keep that intensity. Your heart should be pumping!',
      },
      {
        time: 115,
        text: 'Now slow it right down. Super slow motion. Control every single roll of the ball.',
      },
      {
        time: 120,
        text: 'Then EXPLODE! A sudden burst of speed for five yards!',
      },
      {
        time: 125,
        text: 'Good. Bring it back under your spell. Soft, quiet touches.',
      },
      {
        time: 130,
        text: "Let's practice that again. Slow, slow, slow... then BOOM! Explode forward.",
      },
      {
        time: 135,
        text: 'The change of pace is what tricks the best defenders. Master it.',
      },
      {
        time: 140,
        text: 'Keep the ball moving now. Find that flow again. Constant touches.',
      },
      {
        time: 145,
        text: 'Get ready to challenge your other foot. Prepare yourself.',
      },
      {
        time: 150,
        text: 'Halfway! Time to use your other foot. For every three touches with your favorite foot, do one with your other foot.',
      },
      {
        time: 155,
        text: "Don't just tap with your weak foot. Try a sole roll. Try a drag-back. Wake it up!",
      },
      {
        time: 160,
        text: 'The goal is to make both feet your favorite foot!',
      },
      {
        time: 165,
        text: "Let's see a weak-foot-only challenge for 10 seconds. Go!",
      },
      {
        time: 175,
        text: 'Great. Now back to both feet. Keep the rhythm flowing.',
      },
      {
        time: 180,
        text: "Let's see a combo. Inside touch, outside touch, sole roll. Link them together.",
      },
      {
        time: 185,
        text: 'Keep that body low, knees bent. An athletic stance is key.',
      },
      {
        time: 190,
        text: 'Now focus on your weak foot again for 10 seconds. Only weak foot touches. Go!',
      },
      {
        time: 200,
        text: "Good. Back to both feet. Feel the difference? That's your brain getting smarter.",
      },
      {
        time: 205,
        text: 'Change direction sharply now. A sudden cut to the left.',
      },
      {
        time: 210,
        text: 'Now a sharp cut to the right. Keep the imaginary defender guessing.',
      },
      {
        time: 215,
        text: "Let's see a burst of speed. Explode forward for five steps.",
      },
      {
        time: 220,
        text: 'Good. Now bring it back under control. Slow the tempo down.',
      },
      {
        time: 225,
        text: 'Now build it back up. Faster feet, faster touches.',
      },
      {
        time: 230,
        text: "We're approaching the last minute. Prepare to empty the tank.",
      },
      {
        time: 235,
        text: 'Get ready for full speed. Deep breath.',
      },
      {
        time: 240,
        text: 'Last minute! Full speed. Let the ball flow. This should look like a dance. Effortless, controlled, and constant.',
      },
      {
        time: 245,
        text: 'Thirty seconds left. Empty the tank on this warm-up. Quick feet, quick touches!',
      },
      {
        time: 290,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great start.',
      },
      {
        time: 295,
        text: "No break, straight into the next drill! Let's learn a move from the Spanish magicians: 'The La Croqueta'.",
      },
      {
        time: 300,
        text: 'This move is a super fast way to dodge a defender. Start by dribbling forward.',
      },
      {
        time: 305,
        text: 'As you get to a defender, use the inside of your right foot to tap the ball to your left foot. As you do it, take a little hop to the side.',
      },
      {
        time: 312,
        text: "Then, use the inside of your left foot to tap the ball forward past the defender. It's a quick tap-tap to get by.",
      },
      {
        time: 318,
        text: "The hop and the two quick taps should feel like one big, explosive move. Let's practice it standing still first.",
      },
      {
        time: 325,
        text: "Now let's try it the other way. Tap from your left foot to your right. You need to be a master on both sides.",
      },
      {
        time: 330,
        text: 'Focus on the timing. The hop is the secret ingredient that makes it work.',
      },
      {
        time: 335,
        text: "Let's see five good ones to the left. Go.",
      },
      {
        time: 340,
        text: 'Now five to the right. Repetition makes you a champion.',
      },
      {
        time: 345,
        text: 'Keep that rhythm. Tap-tap, hop. Tap-tap, hop.',
      },
      {
        time: 350,
        text: 'The hop is your secret weapon. It moves your body past the defender.',
      },
      {
        time: 355,
        text: "Let's focus on the speed of your feet. Quick, quick, quick!",
      },
      {
        time: 360,
        text: 'One minute in. Your La Croqueta is looking sharper.',
      },
      {
        time: 365,
        text: "Let's see a really explosive one now. Dribble in with pace.",
      },
      {
        time: 370,
        text: "Good! Now another one. Don't let the quality drop.",
      },
      {
        time: 375,
        text: 'Remember to keep your head up before the move. See the defender, then beat them.',
      },
      {
        time: 380,
        text: "Let's try to make the two taps almost silent. A soft, quick touch.",
      },
      {
        time: 385,
        text: "That's it! You're making it look easy.",
      },
      {
        time: 390,
        text: "Now, let's try to add a little fake with your head as you do it. Look one way, go the other.",
      },
      {
        time: 395,
        text: 'The more you can trick the defender, the better the move is.',
      },
      {
        time: 400,
        text: 'Keep that balance. Your core should be tight as you hop.',
      },
      {
        time: 405,
        text: "Let's see one more perfect one with your right foot.",
      },
      {
        time: 410,
        text: 'Now one with your left. Challenge yourself.',
      },
      {
        time: 415,
        text: 'Awesome. Get ready to add the body feint.',
      },
      {
        time: 420,
        text: "Two minutes down. Now, let's add a body fake. As you do the first tap, dip your shoulder to trick the defender even more.",
      },
      {
        time: 428,
        text: 'The fake makes the move extra sneaky. Deceive them with your whole body.',
      },
      {
        time: 433,
        text: 'Keep practicing. Dribble, La Croqueta, and then dribble away. Keep the quality high.',
      },
      {
        time: 438,
        text: "Let's see that sequence again. Dribble in, quick La Croqueta, and accelerate out.",
      },
      {
        time: 443,
        text: 'The quality of your dribble sets up the move. Keep it close and controlled.',
      },
      {
        time: 448,
        text: 'Now, focus on the exit. The first touch after the move should be explosive.',
      },
      {
        time: 453,
        text: "Let's see three in a row where you really explode out of the move. Go.",
      },
      {
        time: 458,
        text: "That's one! Good speed. Reset.",
      },
      {
        time: 463,
        text: "That's two! Even faster. Keep it clean.",
      },
      {
        time: 468,
        text: "That's three! Awesome power. You're making the move dangerous.",
      },
      {
        time: 473,
        text: "Keep that intensity. We're approaching the halfway mark of this drill.",
      },
      {
        time: 478,
        text: 'Get ready to focus on the soft touch.',
      },
      {
        time: 480,
        text: 'Halfway! The taps have to be soft. Just enough to move the ball from one foot to the other. Not too hard!',
      },
      {
        time: 487,
        text: 'Focus on that second tap, the one that pushes you past the defender. It should be an explosion of speed.',
      },
      {
        time: 492,
        text: "Let's see five in a row with perfect form. You can do it.",
      },
      {
        time: 497,
        text: 'One... good. Two... keep that quality.',
      },
      {
        time: 502,
        text: 'Three... nice and sharp. Four... almost there.',
      },
      {
        time: 507,
        text: 'Five! Excellent work. Keep that rhythm.',
      },
      {
        time: 512,
        text: "Let's try to do it even faster now. Quick, quick, quick!",
      },
      {
        time: 517,
        text: "The speed of the move is what makes it so effective. Let's push it.",
      },
      {
        time: 522,
        text: 'Dribble in with pace, execute the move, and accelerate out.',
      },
      {
        time: 527,
        text: "That's it! You're looking like a pro.",
      },
      {
        time: 532,
        text: 'Keep that focus. Final minute of this drill coming up.',
      },
      {
        time: 537,
        text: 'Prepare to go at game speed.',
      },
      {
        time: 540,
        text: "Last minute! Let's see it at game speed. Dribble with pace, then a lightning-fast La Croqueta, and explode into space.",
      },
      {
        time: 547,
        text: 'Come on, push the pace. Make it sharp, make it clean.',
      },
      {
        time: 552,
        text: 'Thirty seconds left. Empty the tank on this drill.',
      },
      {
        time: 580,
        text: 'Ten seconds! Keep it sharp! And FREEZE! Beautiful skill.',
      },
      {
        time: 585,
        text: "Straight into the next one! No rest. Time for a legendary Brazilian trick: 'The Elastico'.",
      },
      {
        time: 590,
        text: 'This move is all about foot speed. Start with the ball in front of your right foot.',
      },
      {
        time: 596,
        text: 'In one super fast motion, push the ball to the right with the OUTSIDE of your foot, then quickly snap your foot around and pull it back to the left with the INSIDE of your foot.',
      },
      {
        time: 605,
        text: "It's an out-and-in 'flip-flap' that happens in a flash. It fakes the defender one way and you go the other.",
      },
      {
        time: 612,
        text: "Practice the motion slowly. It's all in your ankle. Push out, then snap back. Now try it with your left foot.",
      },
      {
        time: 618,
        text: 'This is a superstar move. Be patient with yourself.',
      },
      {
        time: 720,
        text: "Two minutes in. The Elastico is tough! Don't worry if it's not perfect. Focus on that whip-like motion of your foot.",
      },
      {
        time: 725,
        text: "Let's see five attempts with your right foot. Focus on the technique, not the speed yet.",
      },
      {
        time: 730,
        text: 'Now five with your left. Build that muscle memory.',
      },
      {
        time: 735,
        text: "That's it. Now let's try to get a little quicker with the snap.",
      },
      {
        time: 740,
        text: "The 'out' and 'in' touches should be almost at the same time.",
      },
      {
        time: 745,
        text: "Keep your body over the ball. Don't lean back.",
      },
      {
        time: 750,
        text: "Let's see three good ones in a row with your right foot.",
      },
      {
        time: 755,
        text: 'Now three with your left. The repetition is building your skill.',
      },
      {
        time: 760,
        text: "You're getting the hang of it. This is a superstar move.",
      },
      {
        time: 765,
        text: 'Remember to sell the fake with your body. A little dip of the shoulder.',
      },
      {
        time: 770,
        text: 'Awesome. Now get ready to try it on the move.',
      },
      {
        time: 775,
        text: 'Prepare for the halfway point.',
      },
      {
        time: 780,
        text: 'Halfway point! Try to do it after a few dribbling touches. A slow dribble into a sudden, explosive Elastico.',
      },
      {
        time: 786,
        text: 'The change of pace is what makes this move so effective.',
      },
      {
        time: 791,
        text: "Let's see that sequence. Dribble, Elastico, explode. Go.",
      },
      {
        time: 796,
        text: 'Keep practicing it. The more you do it, the more natural it feels.',
      },
      {
        time: 801,
        text: "Let's see it with your left foot now. Dribble, Elastico, explode.",
      },
      {
        time: 806,
        text: "Don't get frustrated. This is one of the hardest moves in soccer.",
      },
      {
        time: 811,
        text: "Focus on the snap of your ankle. That's the secret.",
      },
      {
        time: 816,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 821,
        text: "Awesome! You're looking like a Brazilian superstar.",
      },
      {
        time: 826,
        text: 'Keep that confidence. You can do this move.',
      },
      {
        time: 831,
        text: "Let's see one more before the final minute.",
      },
      {
        time: 836,
        text: 'Excellent. Prepare for game speed.',
      },
      {
        time: 840,
        text: "Last minute! Let's see your best effort. The key is making the 'out' and 'in' touches look like one single, fluid movement.",
      },
      {
        time: 845,
        text: 'Push the speed now. Keep going for 20 seconds!',
      },
      {
        time: 865,
        text: 'Ten seconds left! One more! Make it your fastest one yet! And TIME! Incredible technique.',
      },
      {
        time: 875,
        text: "Keep moving, no breaks! Next up, an advanced turn: 'The V-Turn'.",
      },
      {
        time: 880,
        text: 'This is a great move to create space. Start with the ball in front of you.',
      },
      {
        time: 885,
        text: 'Put your right foot on the ball and pull it back towards you. But instead of turning, use the inside of that SAME foot to push the ball out diagonally forward to your right.',
      },
      {
        time: 894,
        text: "The ball makes a 'V' shape. You pull it back to escape a tackle, then push it forward into new space. It's a pull-push motion.",
      },
      {
        time: 902,
        text: "Practice the V-Turn on the spot, then try it with your left foot. The explosion after the 'push' is crucial.",
      },
      {
        time: 908,
        text: "Let's focus on the two parts: the pull, and the push. Pull it back soft, push it out hard.",
      },
      {
        time: 913,
        text: "Let's practice just the pull. Dribble, and pull it back with your sole.",
      },
      {
        time: 918,
        text: "Good. Now let's practice the push. From a still ball, push it forward diagonally.",
      },
      {
        time: 923,
        text: "Now let's link them. Pull, then push. Make the V shape.",
      },
      {
        time: 928,
        text: 'Keep practicing that motion. Repetition is everything.',
      },
      {
        time: 933,
        text: "Let's see five in a row with your right foot.",
      },
      {
        time: 938,
        text: 'Now five with your left. Build that confidence.',
      },
      {
        time: 1020,
        text: 'Two minutes in. This move is deadly in tight spaces. It invites pressure, then escapes it in an instant.',
      },
      {
        time: 1025,
        text: "Make sure you're hopping slightly on your standing foot to create the space for the push.",
      },
      {
        time: 1080,
        text: 'Halfway! Now, perform it on the move. Dribble forward, then execute a sharp V-Turn to branch off in a new direction.',
      },
      {
        time: 1085,
        text: "Let's see a dribble, V-Turn, and a three-step acceleration. Go.",
      },
      {
        time: 1090,
        text: 'Keep practicing that sequence. Repetition builds consistency.',
      },
      {
        time: 1140,
        text: "Last minute! Let's see some power. The 'push' of the V should be a firm touch that you can sprint onto.",
      },
      {
        time: 1145,
        text: "Imagine you're beating a defender and creating space for a shot. That's the intensity we need.",
      },
      {
        time: 1170,
        text: 'Ten seconds! Last one! Make the defender miss! And REST! Excellent space creation.',
      },
      {
        time: 1175,
        text: "Keep going, two drills left! Now for 'Reactive Dribbling'.",
      },
      {
        time: 1180,
        text: 'This drill is about thinking fast. You will dribble freely, but you must react to your own commands.',
      },
      {
        time: 1185,
        text: "As you dribble, randomly call out one of the moves we've learned: 'Cruyff!', 'Step-over!', 'V-Turn!', 'Elastico!'. You must then do that move within two seconds.",
      },
      {
        time: 1195,
        text: "This is like a real game. You don't plan your moves; you react! Here, you're reacting to your own brain.",
      },
      {
        time: 1200,
        text: "Don't be predictable. Surprise yourself. The goal is to make your mind and your feet work together instantly.",
      },
      {
        time: 1205,
        text: "Let's go. Dribble... 'V-TURN!'... Good. Keep dribbling... 'CROQUETA!'... Sharp.",
      },
      {
        time: 1210,
        text: "Keep the ball moving. What's next? Call it out! 'ELASTICO!'",
      },
      {
        time: 1215,
        text: 'Good. Remember the Elastico is out-in with one foot. Quick snap.',
      },
      {
        time: 1220,
        text: "Keep dribbling freely. Now, call out another move. 'STEP-OVER!'",
      },
      {
        time: 1225,
        text: 'Good. Fake with one foot, take with the other. Explode out of it.',
      },
      {
        time: 1230,
        text: "Don't just do the move, make it effective. The touch after the move is key.",
      },
      {
        time: 1235,
        text: "Let's see another call. What will it be?",
      },
      {
        time: 1240,
        text: "'CRUYFF TURN!' Excellent. Sell the fake shot.",
      },
      {
        time: 1245,
        text: 'Keep that intensity. Your brain should be working as hard as your feet.',
      },
      {
        time: 1250,
        text: "Let's see another one. Call it loud and clear.",
      },
      {
        time: 1255,
        text: "'LA CROQUETA!' Good. Quick tap-tap.",
      },
      {
        time: 1300,
        text: "Keep the ball under control between moves. Don't get sloppy.",
      },
      {
        time: 1305,
        text: "Let's see another call. Your choice.",
      },
      {
        time: 1310,
        text: "Good execution. Now keep dribbling. Don't stop.",
      },
      {
        time: 1315,
        text: "The faster you can react to your own call, the faster you'll react in a game.",
      },
      {
        time: 1320,
        text: "Let's see a 'V-TURN' again. Pull-push, explode.",
      },
      {
        time: 1325,
        text: "Good. Now an 'ELASTICO'. Out-in, quick snap.",
      },
      {
        time: 1330,
        text: 'Keep that quality high. Every move matters.',
      },
      {
        time: 1335,
        text: "Let's see a 'STEP-OVER'. Big fake, explosive take.",
      },
      {
        time: 1340,
        text: "Good. Now a 'CRUYFF TURN'. Fake the shot, chop it back.",
      },
      {
        time: 1345,
        text: "You're building a great toolbox of skills.",
      },
      {
        time: 1350,
        text: "Keep that focus. Don't let your mind wander.",
      },
      {
        time: 1355,
        text: "Let's see another call. What's it going to be?",
      },
      {
        time: 1360,
        text: 'Good. Now keep dribbling. Prepare for the two-minute mark.',
      },
      {
        time: 1365,
        text: "Let's see one more call before then. Make it a good one.",
      },
      {
        time: 1370,
        text: "Excellent. You're looking sharp.",
      },
      {
        time: 1375,
        text: 'Get ready to push the pace.',
      },
      {
        time: 1380,
        text: 'Two minutes down. The execution of the move after the call must be at game speed. No hesitation.',
      },
      {
        time: 1385,
        text: "Let's see it. Dribble with pace, then call a move. 'V-TURN!'",
      },
      {
        time: 1390,
        text: 'Was that at game speed? Do it again, but faster!',
      },
      {
        time: 1395,
        text: "Good. Keep dribbling. Now, call out a different one. 'CRUYFF!'",
      },
      {
        time: 1400,
        text: 'Remember the fake on the Cruyff. Sell the shot!',
      },
      {
        time: 1405,
        text: 'Keep that intensity. This is how you train for real matches.',
      },
      {
        time: 1410,
        text: "Let's see another call. Make it quick, make it sharp.",
      },
      {
        time: 1415,
        text: "'ELASTICO!' Good. That was fast.",
      },
      {
        time: 1420,
        text: "Keep the ball moving. Don't admire your work. Next move.",
      },
      {
        time: 1425,
        text: "What's it going to be now? Surprise me.",
      },
      {
        time: 1430,
        text: 'Good choice. Excellent execution.',
      },
      {
        time: 1435,
        text: 'Prepare for the halfway mark. Get ready to challenge your weak foot.',
      },
      {
        time: 1440,
        text: 'Halfway! Challenge yourself. Can you call out a move for your weak foot and execute it cleanly?',
      },
      {
        time: 1445,
        text: "Let's try it. Dribble, then call 'V-TURN' for your weak foot.",
      },
      {
        time: 1450,
        text: "Good effort. It's tough, but this is how you become a two-footed player.",
      },
      {
        time: 1455,
        text: "Let's try another one. 'LA CROQUETA' with your weak foot leading.",
      },
      {
        time: 1500,
        text: 'Keep working on it. Every try makes you better.',
      },
      {
        time: 1505,
        text: "Now back to your strong foot. Feel the difference? Let's see a sharp move.",
      },
      {
        time: 1510,
        text: 'Call it out and do it. Game speed.',
      },
      {
        time: 1515,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1520,
        text: "Last minute! Let's see rapid-fire reactions. Dribble, call, execute. Dribble, call, execute. Be sharp.",
      },
      {
        time: 1525,
        text: 'Come on, push the pace. How many moves can you do in 30 seconds?',
      },
      {
        time: 1530,
        text: 'Ten seconds! Keep that brain working! And TIME! Great soccer IQ.',
      },
      {
        time: 1535,
        text: "Last drill! Amazing focus. It's time for 'The Gauntlet Combination'!",
      },
      {
        time: 1540,
        text: 'This is the ultimate test. For the next five minutes, you must create cool combinations of at least THREE different skill moves.',
      },
      {
        time: 1548,
        text: 'For example: A step-over, into a La Croqueta, into a V-Turn. Or an Elastico, into a Cruyff turn, into another Elastico.',
      },
      {
        time: 1555,
        text: "This is not about just doing moves. It's about making them flow together like a dance. The last touch of one move must be the setup for the next.",
      },
      {
        time: 1562,
        text: "This is where you show off your skills. Can you perform these superstar moves when you're tired and link them together? Go!",
      },
      {
        time: 1660,
        text: 'One minute in. The transitions must be seamless. No extra touches between moves.',
      },
      {
        time: 1665,
        text: "Let's see a combo. Cruyff turn into an L-Turn. Make it smooth.",
      },
      {
        time: 1672,
        text: 'Good. Now a step-over into a burst of speed. Explode out of the move.',
      },
      {
        time: 1678,
        text: "Keep your head up. Even when you're linking moves, you need to see the field.",
      },
      {
        time: 1685,
        text: "Don't be afraid to try your weak foot. A weak foot step-over is a great tool.",
      },
      {
        time: 1692,
        text: 'The ball is your dance partner. Lead the dance.',
      },
      {
        time: 1700,
        text: 'Keep that intensity. Your feet should be constantly moving.',
      },
      {
        time: 1710,
        text: "Let's see a double step-over now. Quick feet!",
      },
      {
        time: 1720,
        text: 'Two minutes in. Perform the sequence at the highest speed you can maintain while keeping control. This is where superstars are made.',
      },
      {
        time: 1728,
        text: "Mistakes are proof that you're trying. Push your limits.",
      },
      {
        time: 1735,
        text: "Let's link three moves. Step-over, L-Turn, explode. Go.",
      },
      {
        time: 1745,
        text: 'Keep your body low and balanced as you transition between the moves.',
      },
      {
        time: 1755,
        text: "Now, freestyle again. What's your favorite combo? Show it to me.",
      },
      {
        time: 1765,
        text: 'Sell the fakes! Make the imaginary defender look silly.',
      },
      {
        time: 1775,
        text: "The quality can't drop, even when you're tired. Stay sharp.",
      },
      {
        time: 1800,
        text: "Halfway! Don't just repeat the same combo. Challenge yourself to create new sequences on the fly.",
      },
      {
        time: 1808,
        text: 'Every repetition is programming your brain and your feet to work together.',
      },
      {
        time: 1815,
        text: "Let's see a Cruyff turn with your weak foot. Challenge yourself.",
      },
      {
        time: 1822,
        text: "Good. Now back to your strong foot. Feel the difference? That's practice paying off.",
      },
      {
        time: 1830,
        text: 'Combine a turn with a straight-line dribble. Turn, then explode.',
      },
      {
        time: 1840,
        text: 'Keep that ball on a string. Perfect control.',
      },
      {
        time: 1850,
        text: 'Prepare for the final push. Deep breaths.',
      },
      {
        time: 1860,
        text: 'Last minute! Empty the tank! Show me your most creative, most amazing combination of the day.',
      },
      {
        time: 1867,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1874,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1880,
        text: "Thirty seconds! Let's see a masterpiece!",
      },
      {
        time: 1885,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1890,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1895,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1900,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1910,
        text: 'That was an elite display of technical skill and creativity. You have the tools to be a truly special player. Be proud of that work.',
      },
      {
        time: 1920,
        text: "Cool down properly. You've trained at the highest level today. Phenomenal job!",
      },
    ],
  },
  {
    id: 2014,
    name: 'Beginner Individual Ball Control',
    categories: [
      'Ball Control',
      'Individual Drill',
      'First Touch',
      'Trapping',
      'Tricks',
    ],
    sports: ['soccer'],
    difficulty: 'Beginner',
    skill:
      'Fundamental ball trapping, first touch, and body control with fun, skill-building moves.',
    instructions:
      'This 30-minute individual session is all about making the ball your best friend! We will focus on the basics of stopping the ball, controlling it out of the air, and learning fun moves inspired by the pros to build your confidence.',
    equipment: '1 soccer ball',
    timeTotal: 1800,
    timedInstructions: [
      {
        time: 0,
        text: "Let's start with 'Messi's Glue Foot'. Just you and your ball. Let's begin!",
      },
      {
        time: 4,
        text: 'Start by tapping the ball back and forth between the inside of your feet. Tiny little taps. Keep it close.',
      },
      {
        time: 13,
        text: 'Imagine the ball is glued to your shoes, just like Messi! Keep it super close.',
      },
      {
        time: 18,
        text: 'Good! Now stop the ball dead with the bottom of your shoe. Perfect.',
      },
      {
        time: 23,
        text: "Let's get a rhythm. Tap, tap, tap, stop. Tap, tap, tap, stop.",
      },
      {
        time: 29,
        text: 'Keep your knees bent and stay bouncy on your feet. Be ready to move.',
      },
      {
        time: 34,
        text: "Now, let's just use your right foot. Tap it and stop it only with your right foot.",
      },
      {
        time: 40,
        text: "Great! Now only your left foot. Tap and stop. It feels a bit funny, but that's how we get better!",
      },
      {
        time: 47,
        text: 'Back to both feet. Tick-tock, tick-tock. Keep that glue on your feet!',
      },
      {
        time: 52,
        text: "Let's see ten in a row. Count them! One, two, three...",
      },
      {
        time: 57,
        text: 'Keep going! Four, five, six... soft touches!',
      },
      {
        time: 62,
        text: "Seven, eight, nine, ten! Awesome. Now let's use the sole of our foot. Roll the ball forward and backward.",
      },
      {
        time: 69,
        text: "Good. Now roll it side-to-side, from right to left. You're dancing with the ball!",
      },
      {
        time: 75,
        text: 'Can you do it a little faster? Quick feet!',
      },
      {
        time: 80,
        text: 'Great job. Keep your knees bent and stay bouncy.',
      },
      {
        time: 85,
        text: "One minute in! You're doing great. Let's go back to tick-tocks again.",
      },
      {
        time: 90,
        text: 'This time, try to look up for just a second. A quick peek!',
      },
      {
        time: 95,
        text: "It's tricky, but that's how you see the game. Keep practicing.",
      },
      {
        time: 100,
        text: 'Now back to the sole rolls. Forward and back this time.',
      },
      {
        time: 105,
        text: "Control that ball. Don't let it get too far away.",
      },
      {
        time: 110,
        text: "Let's go back to the tick-tocks. Can you do 20 in a row without losing control?",
      },
      {
        time: 116,
        text: "Let's count together! One, two, three, four, five...",
      },
      {
        time: 121,
        text: "Keep it up! Six, seven, eight, nine, ten... you're halfway there!",
      },
      {
        time: 127,
        text: 'Eleven, twelve, thirteen, fourteen, fifteen... soft touches!',
      },
      {
        time: 133,
        text: 'Sixteen, seventeen, eighteen, nineteen, twenty! You did it! Awesome.',
      },
      {
        time: 140,
        text: "Let's try that again. Another 20 tick-tocks. Let's see it.",
      },
      {
        time: 145,
        text: 'Keep your body over the ball. It helps with balance.',
      },
      {
        time: 150,
        text: 'If you mess up, no problem! Just start your count over.',
      },
      {
        time: 155,
        text: "You're building great control. This is the most important skill.",
      },
      {
        time: 160,
        text: 'Keep those feet moving quickly. Tick-tock, tick-tock.',
      },
      {
        time: 165,
        text: "Excellent focus. Let's try to do the tick-tocks while moving very, very slowly forward. Just a little shuffle.",
      },
      {
        time: 173,
        text: "That's it! Take a small step with each tap. Tap and step, tap and step.",
      },
      {
        time: 179,
        text: "Keep the ball right in front of you. Don't let it get ahead.",
      },
      {
        time: 184,
        text: 'Dribble forward a few steps, then turn around and come back. Still using tick-tocks.',
      },
      {
        time: 191,
        text: 'This is how you dribble with control. Lots of little touches.',
      },
      {
        time: 196,
        text: "Let's try to look up for a second while you're moving. Quick peek!",
      },
      {
        time: 202,
        text: "Three minutes down. You're doing great. Keep that shuffle going.",
      },
      {
        time: 207,
        text: "Let's see if you can go a tiny bit faster. Just a little jog.",
      },
      {
        time: 212,
        text: 'But keep the ball close! The speed of your feet should match the speed of the ball.',
      },
      {
        time: 218,
        text: 'Awesome. Now slow it back down to a walk.',
      },
      {
        time: 222,
        text: 'Good control. This is what great players do. They can change their speed.',
      },
      {
        time: 228,
        text: "Let's try the sole rolls while moving forward. Roll it with your right, then roll it with your left.",
      },
      {
        time: 235,
        text: "It's a bit trickier, but you can do it. Roll and step.",
      },
      {
        time: 240,
        text: "Keep that ball under your spell. You're the magician.",
      },
      {
        time: 245,
        text: "Let's go back to the moving tick-tocks. That's our foundation.",
      },
      {
        time: 250,
        text: "Keep your head up as much as you can. See where you're going.",
      },
      {
        time: 255,
        text: 'Excellent work. Prepare for the last minute of this drill.',
      },
      {
        time: 260,
        text: 'Get ready to have some fun with it.',
      },
      {
        time: 264,
        text: 'Last minute! Just have fun with it. Tap the ball around, use the bottom of your foot. Get to know your best friend, the soccer ball.',
      },
      {
        time: 273,
        text: "Try a little spin. Try a little hop. It's your time to be creative.",
      },
      {
        time: 278,
        text: 'Can you stop the ball, then quickly pull it back with your sole?',
      },
      {
        time: 283,
        text: 'Good! Now can you do a tick-tock, then a sole roll?',
      },
      {
        time: 288,
        text: "Mix and match all the touches we've learned. Be a dribbling artist.",
      },
      {
        time: 293,
        text: 'Thirty seconds left! Show me your best moves.',
      },
      {
        time: 297,
        text: "Keep the ball close. That's the most important rule.",
      },
      {
        time: 302,
        text: "Let's see a few more quick taps. Fast feet!",
      },
      {
        time: 307,
        text: 'Now a smooth roll. Excellent.',
      },
      {
        time: 311,
        text: 'Get ready for the countdown. Final push!',
      },
      {
        time: 315,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great start.',
      },
      {
        time: 325,
        text: "No break, straight into the next drill! We're learning to control a bouncing ball with 'Toss and Trap'.",
      },
      {
        time: 332,
        text: 'Toss the ball gently into the air in front of you, so it bounces just once.',
      },
      {
        time: 337,
        text: "As the ball comes up from the bounce, your job is to 'trap' it with one touch. Stop it completely.",
      },
      {
        time: 343,
        text: "Use the inside of your foot like a soft wall. Meet the ball and guide it down. Don't kick at it!",
      },
      {
        time: 350,
        text: "Let's try that again. Toss, bounce, trap. Perfect.",
      },
      {
        time: 355,
        text: "Now let's try to trap it with the top of your foot, where your shoelaces are. Lift your foot to meet the ball.",
      },
      {
        time: 362,
        text: 'Your foot should be like a pillow, taking all the energy out of the ball. Be soft!',
      },
      {
        time: 368,
        text: "Let's see five good traps in a row. Take your time.",
      },
      {
        time: 373,
        text: 'One... good. Two... nice and soft.',
      },
      {
        time: 378,
        text: "Three... you're a natural. Four... almost there.",
      },
      {
        time: 383,
        text: 'Five! Awesome. Keep practicing that.',
      },
      {
        time: 388,
        text: 'Remember to get your body behind the ball. Move your feet!',
      },
      {
        time: 393,
        text: "Let's try to alternate. One with the inside, one with the laces.",
      },
      {
        time: 398,
        text: 'This is great for your coordination. Keep it up.',
      },
      {
        time: 403,
        text: "You're doing great. The more you practice, the easier it gets.",
      },
      {
        time: 408,
        text: "Let's see another one with the inside of your foot.",
      },
      {
        time: 413,
        text: 'Now one with the laces. Perfect.',
      },
      {
        time: 418,
        text: "Two minutes down. Let's try a challenge. Can you trap it with your right foot, then your left foot? Alternate... Keep going!",
      },
      {
        time: 426,
        text: 'Toss, bounce, trap with the right. Good. Now toss, bounce, trap with the left.',
      },
      {
        time: 432,
        text: 'This is tricky, but it will make you a much better player. Keep trying.',
      },
      {
        time: 437,
        text: 'Focus on your balance. Keep your arms out to the side.',
      },
      {
        time: 442,
        text: "Let's see three good traps with your right foot.",
      },
      {
        time: 447,
        text: 'Now three with your left. Come on, you can do it!',
      },
      {
        time: 452,
        text: "Awesome. You're building that weak foot.",
      },
      {
        time: 457,
        text: 'Keep that concentration. Every trap matters.',
      },
      {
        time: 462,
        text: "Let's see one more with your right.",
      },
      {
        time: 467,
        text: 'And one more with your left. Excellent.',
      },
      {
        time: 472,
        text: "You're doing great. Prepare for the halfway mark.",
      },
      {
        time: 477,
        text: "Halfway! Let's see how still you can make the ball after your trap. It shouldn't roll at all.",
      },
      {
        time: 484,
        text: 'Toss, bounce, and FREEZE it with your foot. Perfect.',
      },
      {
        time: 489,
        text: 'The ball is your friend. Treat it gently.',
      },
      {
        time: 494,
        text: "Let's see another one. A perfect, dead stop.",
      },
      {
        time: 499,
        text: 'Awesome. Now try with your other foot. A perfect dead stop.',
      },
      {
        time: 504,
        text: "You're a ball tamer! Keep that up.",
      },
      {
        time: 509,
        text: "Let's see three more perfect traps. Any foot you want.",
      },
      {
        time: 514,
        text: "One... good. Two... excellent. Three... you're a pro!",
      },
      {
        time: 519,
        text: 'Keep that focus. The softer your touch, the better player you are.',
      },
      {
        time: 524,
        text: "Let's see one more. Make it your best one yet.",
      },
      {
        time: 529,
        text: 'Awesome. Get ready for the final minute.',
      },
      {
        time: 534,
        text: 'Last minute! Make the tosses a little trickier. A little to the left, a little to the right. Move your feet to get behind it.',
      },
      {
        time: 542,
        text: "This is like a real game. The ball doesn't always come right to you.",
      },
      {
        time: 547,
        text: 'Move your feet quickly to get in position, then a soft touch to trap.',
      },
      {
        time: 552,
        text: "That's it! Good footwork.",
      },
      {
        time: 557,
        text: 'Thirty seconds left! Keep challenging yourself.',
      },
      {
        time: 562,
        text: 'A wider toss now. Can you get there?',
      },
      {
        time: 567,
        text: 'Yes! Great hustle. Now a soft touch.',
      },
      {
        time: 572,
        text: 'Final push. Give me your best effort.',
      },
      {
        time: 577,
        text: "You're looking like a real soccer player.",
      },
      {
        time: 582,
        text: 'Get ready for the countdown.',
      },
      {
        time: 585,
        text: 'Ten seconds! Keep it sharp! And FREEZE! Awesome control.',
      },
      {
        time: 595,
        text: "No break! Keep working! Let's learn a cool move: 'Ronaldo's Stop & Go'.",
      },
      {
        time: 600,
        text: 'This move is all about tricking defenders by changing your speed.',
      },
      {
        time: 605,
        text: 'Start by dribbling forward very slowly. Just a few easy taps.',
      },
      {
        time: 610,
        text: 'Then, suddenly, put the bottom of your foot right on top of the ball. Stop it completely!',
      },
      {
        time: 616,
        text: 'The moment you stop it, take your foot off and explode forward with a big touch! Go from slow to fast!',
      },
      {
        time: 623,
        text: "Let's practice the rhythm: Slow, slow, STOP, FAST! Then dribble back and do it again.",
      },
      {
        time: 630,
        text: "Let's see it again. Slow dribble... stop it dead... now explode!",
      },
      {
        time: 635,
        text: "Good! The fake stop is the trick. You have to make the defender think you're stopping.",
      },
      {
        time: 640,
        text: "Let's try it with your other foot. Slow dribble, stop with your left, explode.",
      },
      {
        time: 645,
        text: "Awesome. You're a two-footed trickster.",
      },
      {
        time: 650,
        text: "Keep practicing that sequence. The more you do it, the quicker you'll get.",
      },
      {
        time: 655,
        text: "Let's see five good ones in a row. Any foot you want.",
      },
      {
        time: 700,
        text: "Two minutes in. The 'stop' has to be convincing. Really sell the fake!",
      },
      {
        time: 706,
        text: 'A little dip of your shoulder can make the fake even better.',
      },
      {
        time: 711,
        text: "Let's see it. Dribble, fake stop, and go!",
      },
      {
        time: 716,
        text: 'Excellent. The defender would be frozen.',
      },
      {
        time: 721,
        text: 'Keep that quality high. Every move should be sharp.',
      },
      {
        time: 726,
        text: "Let's see another one. Make it your best fake yet.",
      },
      {
        time: 731,
        text: "Awesome. You're looking like a real trickster.",
      },
      {
        time: 736,
        text: "Keep that focus. We're building great habits.",
      },
      {
        time: 741,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 746,
        text: 'Good. Now get ready for the halfway mark.',
      },
      {
        time: 750,
        text: "Halfway point! The 'go' has to be a real burst of speed. Push the ball and chase it!",
      },
      {
        time: 757,
        text: 'The first touch after the stop should be big and powerful, into open space.',
      },
      {
        time: 762,
        text: "Let's see that explosion. Dribble, stop, and BOOM! You're gone.",
      },
      {
        time: 768,
        text: "That's it! Leave that imaginary defender in the dust.",
      },
      {
        time: 773,
        text: "Let's see three more with a big explosion. Go.",
      },
      {
        time: 778,
        text: 'One... great speed. Two... even faster.',
      },
      {
        time: 783,
        text: "Three! You're a rocket ship!",
      },
      {
        time: 788,
        text: 'Keep that intensity. This is a game-winning move.',
      },
      {
        time: 793,
        text: "Let's see one more. Make it your fastest yet.",
      },
      {
        time: 798,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 803,
        text: 'Get ready to put it all together.',
      },
      {
        time: 807,
        text: "Last minute! Let's see some powerful explosions. Imagine you're leaving a defender in the dust.",
      },
      {
        time: 813,
        text: 'A good fake, and a powerful explosion. Show me.',
      },
      {
        time: 818,
        text: "Thirty seconds left! Give me everything you've got.",
      },
      {
        time: 823,
        text: 'Faster, sharper, more explosive!',
      },
      {
        time: 828,
        text: "Come on, you're almost there. Don't stop now.",
      },
      {
        time: 833,
        text: 'Final push. Make this last one your best one.',
      },
      {
        time: 838,
        text: 'Get ready for the countdown.',
      },
      {
        time: 842,
        text: 'Ten seconds left! One more! Make it your fastest one yet! And TIME! Excellent.',
      },
      {
        time: 852,
        text: "No break! Straight into a classic turn: 'Zidane's Drag-Back'.",
      },
      {
        time: 858,
        text: 'This is a beautiful and simple way to turn away from trouble.',
      },
      {
        time: 863,
        text: 'Start by dribbling forward slowly.',
      },
      {
        time: 867,
        text: 'Now, put the sole of your right foot on the ball and pull it straight back towards you.',
      },
      {
        time: 873,
        text: 'As you pull it back, turn your body to the right, and take the ball away with the outside of that same foot.',
      },
      {
        time: 880,
        text: "So it's: Dribble, drag-back, turn and go. Let's try it again.",
      },
      {
        time: 886,
        text: "Now let's try with the left foot. Drag it back with your left sole, turn your body to the left, and take it away.",
      },
      {
        time: 894,
        text: "The turn should be one smooth motion. It's like you're dancing.",
      },
      {
        time: 900,
        text: "Let's see five good ones with your right foot. Focus on the smooth turn.",
      },
      {
        time: 905,
        text: 'Now five with your left. Make it just as graceful.',
      },
      {
        time: 910,
        text: 'This move is great for protecting the ball. Use your body as a shield.',
      },
      {
        time: 915,
        text: 'As you turn, keep your body between the defender and the ball.',
      },
      {
        time: 920,
        text: "Let's see a sharp one now. Quick pull, quick turn.",
      },
      {
        time: 925,
        text: 'Excellent. The speed makes it even better.',
      },
      {
        time: 930,
        text: 'Keep that focus. Every turn should be perfect.',
      },
      {
        time: 935,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 940,
        text: "Awesome. You're looking like a pro.",
      },
      {
        time: 945,
        text: "Keep that quality high. We're building great habits.",
      },
      {
        time: 950,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 955,
        text: "Let's practice that turn again. Dribble forward...",
      },
      {
        time: 960,
        text: 'Now, drag it back with your sole and turn your body. Sharp!',
      },
      {
        time: 965,
        text: "Excellent! Let's see it with your other foot now. Left foot drag-back.",
      },
      {
        time: 971,
        text: 'Remember to turn your body the same way you pull the ball.',
      },
      {
        time: 976,
        text: "You're doing great. This is a super useful move in a game.",
      },
      {
        time: 981,
        text: "Let's see one more with your favorite foot. Make it your best one yet.",
      },
      {
        time: 987,
        text: "Awesome! You're really starting to master the Zidane turn.",
      },
      {
        time: 993,
        text: 'Get ready for the next step.',
      },
      {
        time: 997,
        text: 'Halfway! This move is great for protecting the ball. Use your body as a shield as you turn.',
      },
      {
        time: 1004,
        text: 'As you drag the ball back, step in front of it to keep the defender away.',
      },
      {
        time: 1009,
        text: "Let's see that. Dribble, pull, shield, and turn. Good.",
      },
      {
        time: 1014,
        text: 'This is a smart move for when a defender is right on your back.',
      },
      {
        time: 1019,
        text: "Let's see three more good shielding turns. Go.",
      },
      {
        time: 1024,
        text: 'One... good protection. Two... stay strong.',
      },
      {
        time: 1029,
        text: "Three! Excellent. You're a rock.",
      },
      {
        time: 1034,
        text: 'Keep that intensity. This is a game-saving move.',
      },
      {
        time: 1039,
        text: "Let's see one more. Make it your strongest shield yet.",
      },
      {
        time: 1044,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1049,
        text: 'Get ready to put it all together.',
      },
      {
        time: 1053,
        text: "Last minute! Let's see some sharp drag-back turns. Make it quick!",
      },
      {
        time: 1059,
        text: 'Dribble with a bit of pace, then a sudden, sharp turn. Surprise them!',
      },
      {
        time: 1065,
        text: "Thirty seconds left! Give me everything you've got.",
      },
      {
        time: 1070,
        text: 'Faster, cleaner, sharper!',
      },
      {
        time: 1075,
        text: "Come on, you're almost there. Don't stop now.",
      },
      {
        time: 1080,
        text: 'Final push. Make this last one your best one.',
      },
      {
        time: 1085,
        text: 'Get ready for the countdown.',
      },
      {
        time: 1089,
        text: 'Ten seconds! Last one! Make it look easy! And REST! Great movement.',
      },
      {
        time: 1099,
        text: "Keep it up, two drills left! Now for the very important 'Weak Foot Workout'.",
      },
      {
        time: 1105,
        text: "This whole drill is with your 'other' foot, your non-dominant one. It feels weird, but it makes you a much better player.",
      },
      {
        time: 1113,
        text: "Let's start with Messi's Glue Foot, but only with your weak foot. Tap, tap, tap. Keep it close.",
      },
      {
        time: 1120,
        text: 'Now, try stopping it with the sole of your weak foot. Be patient with yourself.',
      },
      {
        time: 1126,
        text: "Let's try the Toss and Trap drill. You MUST use your weak foot to control the bouncing ball.",
      },
      {
        time: 1133,
        text: 'Toss, bounce, and a soft trap with that weak foot. Good.',
      },
      {
        time: 1138,
        text: "It's okay if it's not perfect! The more you practice, the stronger that foot will get.",
      },
      {
        time: 1144,
        text: "Let's see five good traps with that weak foot. You can do it.",
      },
      {
        time: 1149,
        text: "Now, let's try some weak-foot tick-tocks. Just tapping the ball back and forth with the inside of that foot.",
      },
      {
        time: 1154,
        text: "Let's see three more traps with that weak foot. Be a pillow!",
      },
      {
        time: 1159,
        text: 'Good. Now ten tick-tocks with that weak foot. Count them out!',
      },
      {
        time: 1165,
        text: "Keep it going, don't stop. You're building a new skill.",
      },
      {
        time: 1170,
        text: "Awesome work. Let's try another trap. Make it your softest one yet.",
      },
      {
        time: 1176,
        text: 'Excellent. Now a few more tick-tocks. Quick feet!',
      },
      {
        time: 1181,
        text: "You're doing great. That foot is getting smarter every second.",
      },
      {
        time: 1186,
        text: "Two minutes down. Let's try the Zidane Drag-Back, but only with your weak foot. Drag, turn, go.",
      },
      {
        time: 1193,
        text: 'This is a big challenge. Break it down. Drag it back with your sole...',
      },
      {
        time: 1198,
        text: 'Now turn your body and take it away with the outside of that foot. Good try!',
      },
      {
        time: 1203,
        text: "Let's try it again. The more you do it, the more your brain learns the movement.",
      },
      {
        time: 1208,
        text: "Excellent. You're making that weak foot smarter.",
      },
      {
        time: 1213,
        text: "Let's go back to the weak foot traps. Toss, bounce, trap.",
      },
      {
        time: 1218,
        text: 'The softer the touch, the better. Be a pillow.',
      },
      {
        time: 1223,
        text: 'Now some weak foot tick-tocks. Keep that ball close.',
      },
      {
        time: 1228,
        text: "You're doing great. This is hard work, but it will pay off.",
      },
      {
        time: 1233,
        text: "Let's see one more weak foot drag-back. Make it your best one.",
      },
      {
        time: 1238,
        text: 'Awesome! Prepare for the halfway mark.',
      },
      {
        time: 1242,
        text: 'Halfway! This is tough, but every single touch you make with this foot is making you stronger. Keep going!',
      },
      {
        time: 1250,
        text: "Let's try the Ronaldo Stop & Go, but only with your weak foot.",
      },
      {
        time: 1255,
        text: 'Dribble slowly with your weak foot, stop it with your weak foot, and explode forward.',
      },
      {
        time: 1261,
        text: "Let's see that again. Slow, stop, fast! Good try.",
      },
      {
        time: 1266,
        text: "It's all about coordination. You can do it.",
      },
      {
        time: 1271,
        text: "Focus on the 'stop'. Make it a good, clean stop.",
      },
      {
        time: 1276,
        text: "Now focus on the 'go'. A big, powerful push.",
      },
      {
        time: 1281,
        text: "Let's try the whole thing one more time. Slow, stop, FAST!",
      },
      {
        time: 1286,
        text: "Excellent! You're building confidence in that foot.",
      },
      {
        time: 1291,
        text: "Let's go back to some weak foot tick-tocks. Keep it working.",
      },
      {
        time: 1296,
        text: 'Good. Now a weak foot trap. Toss, bounce, trap.',
      },
      {
        time: 1301,
        text: 'This is a great way to build confidence in that foot. Keep trying.',
      },
      {
        time: 1306,
        text: "Let's see three good attempts. Slow, stop, fast!",
      },
      {
        time: 1311,
        text: 'Excellent. Now back to the weak foot tick-tocks. Keep that foot working.',
      },
      {
        time: 1316,
        text: "You're building a brand new skill. Be proud of your effort.",
      },
      {
        time: 1321,
        text: "Let's see another weak foot stop-and-go. Make it sharp.",
      },
      {
        time: 1326,
        text: "Awesome. You're looking more and more comfortable.",
      },
      {
        time: 1331,
        text: 'Keep that focus. Final minute of this drill coming up.',
      },
      {
        time: 1336,
        text: 'Prepare to push yourself.',
      },
      {
        time: 1340,
        text: "Last minute! Just focus on making clean contact. Don't worry about speed, just good, solid touches.",
      },
      {
        time: 1347,
        text: 'Freestyle with your weak foot. Tick-tocks, sole rolls, traps. Anything you want.',
      },
      {
        time: 1353,
        text: "Thirty seconds left. Give me everything you've got with that weak foot.",
      },
      {
        time: 1358,
        text: "Come on, you're almost there. Don't stop now.",
      },
      {
        time: 1363,
        text: 'Final push. Make these last touches your best ones.',
      },
      {
        time: 1368,
        text: 'Get ready for the countdown.',
      },
      {
        time: 1372,
        text: 'Ten seconds! Keep concentrating! And TIME! You just made your weak foot stronger.',
      },
      {
        time: 1377,
        text: "Last drill! Amazing focus. It's time for the 'Superstar Freestyle'!",
      },
      {
        time: 1382,
        text: 'This is your chance to put everything together. For the next five minutes, you can dribble anywhere you want.',
      },
      {
        time: 1388,
        text: 'Practice all the things we learned today. Do some tick-tocks, some weaving, some stop-and-gos, and some drag-back turns.',
      },
      {
        time: 1395,
        text: 'There are no rules here except to keep the ball close and have fun. Be creative!',
      },
      {
        time: 1400,
        text: 'Try to link moves together. Can you do a stop-and-go right into a drag-back turn?',
      },
      {
        time: 1405,
        text: 'This is your time to be a soccer artist. The field is your canvas, and the ball is your paintbrush. Go!',
      },
      {
        time: 1410,
        text: "Let's see some of those foundation touches. The tick-tocks. Keep them sharp.",
      },
      {
        time: 1415,
        text: "Good. Now let's see a stop-and-go. Slow... STOP... FAST!",
      },
      {
        time: 1420,
        text: 'Awesome. Now dribble around and give me a sharp drag-back turn.',
      },
      {
        time: 1425,
        text: "Excellent. Let's see some of that inside-outside dribbling now.",
      },
      {
        time: 1430,
        text: "Keep that ball close. You're in complete control.",
      },
      {
        time: 1435,
        text: "Now, let's see you weave through some imaginary defenders.",
      },
      {
        time: 1440,
        text: "Good tight turns. That's what we want to see.",
      },
      {
        time: 1445,
        text: "You're using all the skills we've practiced. This is fantastic.",
      },
      {
        time: 1450,
        text: 'Keep your head up as much as you can. See the space.',
      },
      {
        time: 1455,
        text: "Let's see another stop-and-go. Really explode out of it.",
      },
      {
        time: 1500,
        text: 'Great power! Now a smooth drag-back.',
      },
      {
        time: 1505,
        text: "You're looking like a real player. Keep that confidence.",
      },
      {
        time: 1510,
        text: "Let's try to link two moves together. A stop-and-go into a weave.",
      },
      {
        time: 1515,
        text: "Awesome! That's creative thinking.",
      },
      {
        time: 1520,
        text: "Keep the ball moving. Don't let it stop.",
      },
      {
        time: 1525,
        text: "Let's see some of those tick-tocks while you're moving.",
      },
      {
        time: 1530,
        text: 'Good. Now a sharp turn. Your choice.',
      },
      {
        time: 1535,
        text: "Excellent. You're making smart decisions.",
      },
      {
        time: 1540,
        text: "Keep that energy high. You're doing great.",
      },
      {
        time: 1545,
        text: 'Prepare for the one-minute mark.',
      },
      {
        time: 1548,
        text: 'One minute in. Are you using both feet? Are you keeping your head up?',
      },
      {
        time: 1553,
        text: "Let's challenge your weak foot. Try a drag-back with your other foot.",
      },
      {
        time: 1558,
        text: 'Good effort! The more you try, the better it gets.',
      },
      {
        time: 1563,
        text: "Let's go back to your strong foot now. Show me a perfect Stop & Go.",
      },
      {
        time: 1568,
        text: 'Excellent. Now a perfect Drag-Back.',
      },
      {
        time: 1573,
        text: "You're switching between moves so well.",
      },
      {
        time: 1578,
        text: 'Keep that ball on a string. Perfect control.',
      },
      {
        time: 1583,
        text: "Let's see a change of pace. Slow it down... now speed up!",
      },
      {
        time: 1588,
        text: 'Awesome. Keep that creativity flowing.',
      },
      {
        time: 1593,
        text: "You're working hard and it's showing.",
      },
      {
        time: 1598,
        text: "Now back to your strong foot. Let's see a perfect move.",
      },
      {
        time: 1603,
        text: "Keep that ball close. Remember Messi's glue foot!",
      },
      {
        time: 1608,
        text: "Let's see a change of pace. Slow it down... now speed up!",
      },
      {
        time: 1613,
        text: "Excellent. You're in control of the tempo.",
      },
      {
        time: 1618,
        text: 'Keep working hard. Every touch makes you better.',
      },
      {
        time: 1623,
        text: "Let's see another weave. Nice and tight.",
      },
      {
        time: 1628,
        text: 'Good. Now explode out of it.',
      },
      {
        time: 1633,
        text: "You're looking sharp. Keep that focus.",
      },
      {
        time: 1638,
        text: 'Prepare for the two-minute mark.',
      },
      {
        time: 1642,
        text: "Two minutes in. Try to change your speed and direction often. Don't be predictable.",
      },
      {
        time: 1649,
        text: 'Dribble left, then quickly cut right. Keep the defender guessing.',
      },
      {
        time: 1654,
        text: 'Now dribble forward, then a sharp drag-back turn.',
      },
      {
        time: 1659,
        text: "Good. Let's see a stop-and-go. Make it explosive.",
      },
      {
        time: 1664,
        text: 'The more you can change your rhythm, the harder you are to defend.',
      },
      {
        time: 1669,
        text: "Let's see some of that inside-outside dribbling again.",
      },
      {
        time: 1674,
        text: 'Good. Now a sharp turn. Your choice.',
      },
      {
        time: 1679,
        text: "Excellent. You're thinking like a soccer player.",
      },
      {
        time: 1684,
        text: "Keep that ball moving. Don't let it stop. Constant motion.",
      },
      {
        time: 1690,
        text: "Let's see a drag-back turn right now! Sharp!",
      },
      {
        time: 1695,
        text: "Good. Now dribble out of it. Let's see a stop-and-go.",
      },
      {
        time: 1701,
        text: 'Slow... STOP... FAST! Awesome explosion!',
      },
      {
        time: 1707,
        text: 'Keep dribbling. Show me some of that inside-outside dribbling we practiced.',
      },
      {
        time: 1714,
        text: "You're doing great. Your confidence is growing with every touch.",
      },
      {
        time: 1720,
        text: 'Get ready for the halfway point.',
      },
      {
        time: 1723,
        text: "Halfway! This is all about building confidence. The more you dribble, the more comfortable you'll feel.",
      },
      {
        time: 1730,
        text: "Trust your skills. You've practiced these moves. Now use them!",
      },
      {
        time: 1735,
        text: "Let's see a confident drag-back turn. Own it.",
      },
      {
        time: 1740,
        text: 'Good. Now a confident stop-and-go. Show me that speed.',
      },
      {
        time: 1745,
        text: 'The more confident you are, the better your moves will be.',
      },
      {
        time: 1750,
        text: "Don't be afraid to make a mistake. It's all part of learning.",
      },
      {
        time: 1755,
        text: "Let's see a creative combination. Link two moves together.",
      },
      {
        time: 1760,
        text: 'How about a drag-back turn right into a stop-and-go? Try it!',
      },
      {
        time: 1766,
        text: 'Awesome! Now keep dribbling. What other combo can you think of?',
      },
      {
        time: 1772,
        text: 'Maybe a weave into a stop-and-go? Be creative!',
      },
      {
        time: 1777,
        text: "You're thinking like a real soccer player, linking your moves.",
      },
      {
        time: 1783,
        text: "Keep that ball close. Even when you're doing tricks, control is number one.",
      },
      {
        time: 1789,
        text: "Let's see one more amazing combo before we finish up.",
      },
      {
        time: 1794,
        text: "Excellent! You're looking sharp. Get ready for the final push.",
      },
      {
        time: 1800,
        text: "Awesome! You're a playmaker.",
      },
      {
        time: 1805,
        text: "Keep that ball close. That's where confidence comes from.",
      },
      {
        time: 1810,
        text: "Let's see another good weave. Tight control.",
      },
      {
        time: 1815,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1820,
        text: "Get ready to show me everything you've got.",
      },
      {
        time: 1823,
        text: 'Last minute! Empty the tank! Show me all your best moves one last time.',
      },
      {
        time: 1828,
        text: "This is your highlight reel. Let's see it.",
      },
      {
        time: 1833,
        text: 'A sharp turn! A burst of speed! A cool trick!',
      },
      {
        time: 1838,
        text: 'Keep that energy high until the very end.',
      },
      {
        time: 1843,
        text: "Thirty seconds! Let's see some amazing control!",
      },
      {
        time: 1848,
        text: 'Faster feet, faster ball. Push yourself.',
      },
      {
        time: 1853,
        text: "Come on, you're almost there. Don't stop now.",
      },
      {
        time: 1858,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1862,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1872,
        text: 'Fantastic work, dribbling star! Your ball control has improved so much today. You should be proud!',
      },
    ],
  },
  {
    id: 2015,
    name: 'Intermediate Individual Ball Control',
    categories: [
      'Ball Control',
      'Dribbling',
      'Juggling',
      'Tricks',
      'Individual Drill',
    ],
    sports: ['soccer'],
    difficulty: 'Intermediate',
    skill: 'Ball Control and Creative Dribbling',
    points: 40,
    instructions:
      'This drill will improve your first touch and ability to control the ball in more challenging situations. We will focus on fluid movements, aerial control, and quick combinations to prepare you for faster-paced games.',
    equipment: '1 soccer ball',
    timeTotal: 2105,
    timedInstructions: [
      {
        time: 0,
        text: "Alright soccer star, let's get to work! First up: 'Inside-Outside Footwork'. Standing still, let's wake up our feet. Go!",
      },
      {
        time: 8,
        text: 'Start by tapping the ball with the inside of your right foot, then the outside of your right foot. Keep it quick and light.',
      },
      {
        time: 15,
        text: 'Inside, outside! Inside, outside! The ball should barely move from its spot.',
      },
      {
        time: 21,
        text: "Let's see ten in a row. One, two, three, four, five, six, seven, eight, nine, ten! Good.",
      },
      {
        time: 30,
        text: 'Now try with your left foot. Inside, outside! Inside, outside! Keep that ball glued to your foot.',
      },
      {
        time: 38,
        text: 'It might feel a little tricky, but this is how you make both feet superstars!',
      },
      {
        time: 43,
        text: "Let's see ten on the left side now. Count them out!",
      },
      {
        time: 48,
        text: "Awesome! Now, let's start moving forward. Take a few steps using inside and outside touches with your right foot. Go!",
      },
      {
        time: 57,
        text: 'Inside, outside, inside, outside! Keep the ball moving with you. Now switch feet and come back with your left.',
      },
      {
        time: 66,
        text: 'Inside, outside with the left! Keep going! This is a great way to improve your speed and control.',
      },
      {
        time: 73,
        text: "Let's do that again. Dribble forward with your right foot, inside-outside.",
      },
      {
        time: 79,
        text: 'Good. Now come back with your left foot, inside-outside.',
      },
      {
        time: 85,
        text: 'Keep your knees bent and your touches soft. Be a ninja on the ball.',
      },
      {
        time: 90,
        text: 'Now, can you go a little bit faster? Push the pace!',
      },
      {
        time: 95,
        text: "That's it! Quicker feet, quicker touches.",
      },
      {
        time: 100,
        text: "Keep that ball under your spell. Don't let it get away.",
      },
      {
        time: 105,
        text: 'Keep switching feet every time you turn. Right foot forward, left foot back.',
      },
      {
        time: 110,
        text: "Let's see a little burst of speed for five steps. Go!",
      },
      {
        time: 115,
        text: 'Good. Now slow it down. Control is number one.',
      },
      {
        time: 120,
        text: "Two minutes in! You're doing great. Let's keep working.",
      },
      {
        time: 125,
        text: 'Now, try to mix them. One right foot inside-outside, then one left foot inside-outside. Alternating.',
      },
      {
        time: 132,
        text: "This is tricky for your brain, but it's a great challenge. Keep it up.",
      },
      {
        time: 137,
        text: "Keep that ball close. The closer you keep it, the better you're doing.",
      },
      {
        time: 142,
        text: "Let's go back to just the right foot. Make it perfect.",
      },
      {
        time: 147,
        text: 'Now just the left foot. Make it just as good.',
      },
      {
        time: 152,
        text: "Halfway! Let's add a new challenge. While you dribble inside-outside, try to look up for a split second.",
      },
      {
        time: 160,
        text: 'Dribble, dribble, peek! Dribble, dribble, peek! See the room around you.',
      },
      {
        time: 166,
        text: 'This is how the best players know where their teammates are. They take quick peeks.',
      },
      {
        time: 172,
        text: "Keep practicing that. It's a tough skill, but you can do it.",
      },
      {
        time: 177,
        text: "Let's see five good peeks while you dribble with your right foot.",
      },
      {
        time: 182,
        text: 'Now five with your left. Keep that head moving.',
      },
      {
        time: 187,
        text: "You're looking like a real playmaker. Keep it up.",
      },
      {
        time: 192,
        text: "Don't forget to keep the touches soft and close.",
      },
      {
        time: 197,
        text: "Let's see another burst of speed now. Go!",
      },
      {
        time: 202,
        text: 'Awesome. Now slow it down again. Control that speed.',
      },
      {
        time: 207,
        text: "You're doing great. Keep that focus high.",
      },
      {
        time: 212,
        text: "Let's go back to alternating feet. Right foot, then left foot.",
      },
      {
        time: 217,
        text: 'The rhythm should be getting smoother now.',
      },
      {
        time: 222,
        text: "Keep working hard. You're building a great foundation.",
      },
      {
        time: 227,
        text: "Let's see one more good dribble forward with your right foot.",
      },
      {
        time: 232,
        text: 'And one more back with your left. Excellent.',
      },
      {
        time: 237,
        text: 'Prepare for the final minute of this drill.',
      },
      {
        time: 240,
        text: "Last minute! Let's see your best inside-outside dribbling. Push the pace!",
      },
      {
        time: 247,
        text: 'Faster feet, faster touches, but always in control.',
      },
      {
        time: 252,
        text: "Let's see a burst of speed for 5 seconds. Go!",
      },
      {
        time: 257,
        text: 'Good. Now slow it down, keep control.',
      },
      {
        time: 262,
        text: 'Thirty seconds left! Empty the tank on this drill.',
      },
      {
        time: 267,
        text: "Come on, keep pushing! Don't stop now.",
      },
      {
        time: 272,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 277,
        text: 'Faster, faster, faster!',
      },
      {
        time: 282,
        text: 'Keep that ball glued to your feet!',
      },
      {
        time: 287,
        text: 'Push it! Last few seconds!',
      },
      {
        time: 292,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 300,
        text: 'Great work on that first exercise. Next up is the Juggling Challenge.',
      },
      {
        time: 304,
        text: 'Start by tossing the ball up. Try to get two taps with your right foot before you catch it. Go!',
      },
      {
        time: 313,
        text: 'Two taps, then catch! Two taps, then catch! The first touch should pop it up, the second should be a soft control.',
      },
      {
        time: 320,
        text: "Let's see five good ones in a row with your right foot.",
      },
      {
        time: 325,
        text: 'Now switch to your left foot. This is a great challenge!',
      },
      {
        time: 330,
        text: 'Two taps with your left, then catch! Two taps, then catch! Perfect!',
      },
      {
        time: 337,
        text: "The key is a soft touch. Don't kick it too high. Keep it in your little bubble.",
      },
      {
        time: 342,
        text: "Now let's try to alternate feet. Tap once with your right, once with your left, then catch. Go!",
      },
      {
        time: 350,
        text: 'Right, left, catch! Right, left, catch! Keep your eyes on the ball and your feet relaxed.',
      },
      {
        time: 357,
        text: 'This is how you get comfortable with the ball in the air. Keep it up.',
      },
      {
        time: 362,
        text: "Let's see if you can get a streak of five. Right, left, catch. That's one!",
      },
      {
        time: 368,
        text: 'Keep trying! If you mess up, just start again. No problem!',
      },
      {
        time: 373,
        text: "You're doing great. Your touch is getting softer.",
      },
      {
        time: 378,
        text: "Let's go back to two taps with your favorite foot. See if it feels easier now.",
      },
      {
        time: 383,
        text: 'Awesome! Now two with your other foot.',
      },
      {
        time: 388,
        text: "Let's try for a record. How many juggles can you get in a row without catching? Any feet you want.",
      },
      {
        time: 395,
        text: 'Try to beat your record! Can you get to five? Ten?',
      },
      {
        time: 400,
        text: 'Keep that ball up! Use your thighs if you need to.',
      },
      {
        time: 405,
        text: 'Come on, keep that ball dancing.',
      },
      {
        time: 410,
        text: "Don't let it drop! Come on, fight for it!",
      },
      {
        time: 415,
        text: "If it drops, start your count over. Let's see a new record.",
      },
      {
        time: 420,
        text: "Two minutes in! You're building amazing touch. This is a key skill.",
      },
      {
        time: 425,
        text: 'Keep your knees bent and your body balanced.',
      },
      {
        time: 430,
        text: "Let's see another good streak. Go for it.",
      },
      {
        time: 435,
        text: "Focus on keeping the ball below your waist. That's a sign of great control.",
      },
      {
        time: 440,
        text: "Let's try to use only your weak foot for a few taps. Challenge it!",
      },
      {
        time: 445,
        text: 'Good. Now back to both feet. Keep that juggle going.',
      },
      {
        time: 450,
        text: "Halfway! Let's set a new record right now. Go!",
      },
      {
        time: 455,
        text: 'Count them out loud! One, two, three...',
      },
      {
        time: 460,
        text: 'Keep it alive! Soft touches, quick feet.',
      },
      {
        time: 465,
        text: "Don't let it get too far away from you. Stay over the ball.",
      },
      {
        time: 470,
        text: "You're doing great. Juggling is all about practice, and you're working hard.",
      },
      {
        time: 476,
        text: "Let's try another record attempt. Start from zero. Go!",
      },
      {
        time: 480,
        text: 'Three minutes in! Keep that focus. Watch the ball all the way to your foot.',
      },
      {
        time: 485,
        text: "Let's try to use your thighs. Pop it up with your foot, then a thigh touch.",
      },
      {
        time: 490,
        text: 'Foot, thigh, foot, thigh. Can you get a rhythm?',
      },
      {
        time: 495,
        text: 'This is a great way to build your all-around control.',
      },
      {
        time: 500,
        text: "Now back to just your feet. Let's see those quick taps.",
      },
      {
        time: 505,
        text: 'The more you juggle, the better your first touch will be in a game.',
      },
      {
        time: 510,
        text: "Keep that ball up. Don't give up on it!",
      },
      {
        time: 515,
        text: "Let's see one more big effort for a new record. Go for it!",
      },
      {
        time: 520,
        text: "Come on, you've got this. Every touch counts.",
      },
      {
        time: 525,
        text: 'Keep it going, keep it going!',
      },
      {
        time: 530,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 535,
        text: 'Get ready for one last push.',
      },
      {
        time: 540,
        text: "Last minute of juggling! Let's see how many you can get in a row now. Final record attempt.",
      },
      {
        time: 547,
        text: 'Push yourself. Can you get one more than last time?',
      },
      {
        time: 552,
        text: 'Thirty seconds left! Keep that ball dancing.',
      },
      {
        time: 557,
        text: "Don't let it drop! Come on, fight for it!",
      },
      {
        time: 562,
        text: 'Final push! How many can you get?',
      },
      {
        time: 567,
        text: 'This is where champions are made. Keep working.',
      },
      {
        time: 572,
        text: 'Come on, a few more seconds!',
      },
      {
        time: 577,
        text: "Don't stop now, almost there!",
      },
      {
        time: 582,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 587,
        text: 'Get ready for the countdown!',
      },
      {
        time: 592,
        text: 'Five, four, three, two, one... and catch! Great work!',
      },
      {
        time: 600,
        text: "Excellent juggling! That's the end of this drill. Next up is the Thigh Control Challenge.",
      },
      {
        time: 604,
        text: 'Toss the ball up gently, about to your chest. Cushion it on your right thigh, then let it drop and tap it with your right foot before it bounces.',
      },
      {
        time: 615,
        text: 'Thigh... then foot! Thigh... then foot! The thigh touch should be super soft, like a pillow.',
      },
      {
        time: 623,
        text: "Let's see three in a row on your right side. Thigh-foot, thigh-foot, thigh-foot. Good.",
      },
      {
        time: 631,
        text: 'Now try with your left thigh and foot. Cushion it with your left thigh, then control it with your left foot.',
      },
      {
        time: 639,
        text: 'Thigh... then foot! Thigh... then foot! This is how you control a long pass from the air.',
      },
      {
        time: 647,
        text: 'Now three on your left. Keep your body balanced and your touches gentle.',
      },
      {
        time: 654,
        text: 'You are getting so much better at controlling the ball from the air!',
      },
      {
        time: 660,
        text: "One minute in! Let's keep that quality high. Right side again.",
      },
      {
        time: 666,
        text: "Remember to pull your thigh back a little bit as the ball hits it. That's the secret to a soft touch.",
      },
      {
        time: 674,
        text: "Let's see that soft touch now. Toss, thigh, foot. Perfect.",
      },
      {
        time: 680,
        text: 'Now the left side. Pull that thigh back to cushion the ball.',
      },
      {
        time: 686,
        text: 'Excellent. The ball should drop right at your feet.',
      },
      {
        time: 691,
        text: 'Keep alternating now. Right side... then left side.',
      },
      {
        time: 697,
        text: 'Find a good rhythm. Toss, control, reset.',
      },
      {
        time: 702,
        text: 'Keep your eyes on the ball all the way down.',
      },
      {
        time: 707,
        text: "You're doing great. This is a very important skill for a midfielder.",
      },
      {
        time: 714,
        text: "Let's see another clean one on your right.",
      },
      {
        time: 719,
        text: 'And another on your left. Awesome.',
      },
      {
        time: 720,
        text: 'Two minutes in! Your control is looking much smoother.',
      },
      {
        time: 725,
        text: "Let's try a challenge. Can you do thigh, foot, and then juggle it once before you catch?",
      },
      {
        time: 733,
        text: 'Thigh, foot, tap, catch! Awesome! Try it again.',
      },
      {
        time: 739,
        text: "This is advanced stuff. You're doing great.",
      },
      {
        time: 744,
        text: "Let's try it on your left side. Thigh, foot, tap, catch.",
      },
      {
        time: 750,
        text: "Don't worry if you mess up. Just reset and try again.",
      },
      {
        time: 756,
        text: 'This is how you push yourself to get better.',
      },
      {
        time: 761,
        text: "Let's go back to the basic thigh-foot. Make it perfect.",
      },
      {
        time: 767,
        text: "Let's see five in a row on your favorite leg. Go.",
      },
      {
        time: 773,
        text: 'Now five on your other leg. You can do it.',
      },
      {
        time: 778,
        text: 'Keep that focus. Watch the ball all the way onto your body.',
      },
      {
        time: 780,
        text: "Halfway! Let's try to speed it up. A quick thigh-foot combo.",
      },
      {
        time: 786,
        text: "That's it. The faster you can control it, the better.",
      },
      {
        time: 791,
        text: "Let's see another one. Make it sharp.",
      },
      {
        time: 796,
        text: "Excellent. Let's see three quick ones in a row on your right.",
      },
      {
        time: 802,
        text: 'Now three quick ones on your left.',
      },
      {
        time: 806,
        text: 'The speed is looking good. Keep that quality.',
      },
      {
        time: 811,
        text: "Remember to stay balanced. Don't lean too far back.",
      },
      {
        time: 817,
        text: "Let's see another fast one. Thigh-foot, quick!",
      },
      {
        time: 822,
        text: "Awesome. You're controlling the ball like a pro.",
      },
      {
        time: 827,
        text: "Keep that energy up. We're almost there.",
      },
      {
        time: 832,
        text: "Let's see one more on each side. Right... and left.",
      },
      {
        time: 838,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 840,
        text: "Last minute of this drill. Let's see your best control.",
      },
      {
        time: 845,
        text: 'Alternate legs now. Right thigh-foot, then left thigh-foot.',
      },
      {
        time: 851,
        text: 'Keep that focus. Watch the ball all the way onto your body.',
      },
      {
        time: 857,
        text: 'Thirty seconds left! Push yourself.',
      },
      {
        time: 861,
        text: 'Can you do it without catching the ball in between? Toss, right thigh-foot, left thigh-foot, then catch.',
      },
      {
        time: 870,
        text: "That's a huge challenge! Give it a try.",
      },
      {
        time: 874,
        text: "Final push! Let's see your best control.",
      },
      {
        time: 879,
        text: 'Come on, keep working hard!',
      },
      {
        time: 883,
        text: "Almost there, don't stop now.",
      },
      {
        time: 887,
        text: 'Get ready for the countdown.',
      },
      {
        time: 890,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 895,
        text: 'Five, four, three, two, one... and time! Excellent!',
      },
      {
        time: 900,
        text: 'Great work on your thigh control! Get ready for the next drill. Next up is the Sole Roll & Drag Back combo. This move is a great way to get past a defender especially when you are in a tight space.',
      },
      {
        time: 906,
        text: 'Start by dribbling forward. Then, put the bottom of your right foot on the ball and roll it to your left.',
      },
      {
        time: 913,
        text: 'As soon as you roll it, take it with your left foot and immediately do a drag-back, pulling it behind you and turning.',
      },
      {
        time: 921,
        text: "So it's: Sole roll... and drag back! One move flows right into the next.",
      },
      {
        time: 927,
        text: "Let's try it the other way. Roll with your left, then drag-back with your right.",
      },
      {
        time: 933,
        text: 'This is a great move to get past a defender. Keep it smooth.',
      },
      {
        time: 938,
        text: "Let's see five good combos in a row. Take your time at first.",
      },
      {
        time: 943,
        text: 'The key is to keep the ball close as you do the moves.',
      },
      {
        time: 948,
        text: "You're looking like a tricky player! Keep it up.",
      },
      {
        time: 953,
        text: "Let's see a bit more speed now. A quicker roll, a quicker drag-back.",
      },
      {
        time: 958,
        text: "That's it! The speed makes the move even better.",
      },
      {
        time: 963,
        text: "Keep those feet moving fast! Let's see how many combos you can do in 30 seconds. Go!",
      },
      {
        time: 993,
        text: "Keep that quality high. Don't get sloppy.",
      },
      {
        time: 998,
        text: 'Come on, push yourself. Faster, faster!',
      },
      {
        time: 1003,
        text: "Final fifteen seconds of that burst. Give me everything you've got.",
      },
      {
        time: 1008,
        text: 'Five, four, three, two, one... and rest the speed. Good work.',
      },
      {
        time: 1013,
        text: "Two minutes in! Let's break it down again. Just the sole roll. Right foot, roll it across.",
      },
      {
        time: 1019,
        text: 'Now just the left foot sole roll. Make it smooth.',
      },
      {
        time: 1024,
        text: 'Good. Now just the drag-back. Dribble, then pull it back with your right foot and turn.',
      },
      {
        time: 1030,
        text: 'Now a drag-back with your left. Sharp turn!',
      },
      {
        time: 1035,
        text: "Now let's put them together again. Roll with the right, drag with the left.",
      },
      {
        time: 1041,
        text: 'And the other way. Roll with the left, drag with the right.',
      },
      {
        time: 1046,
        text: "You're building that coordination. It's looking great.",
      },
      {
        time: 1051,
        text: 'Keep your head up for a peek between the moves.',
      },
      {
        time: 1056,
        text: "Let's see a bit more of a dribble before you start the combo.",
      },
      {
        time: 1101,
        text: 'Three or four touches, then... combo! Good.',
      },
      {
        time: 1106,
        text: 'This makes it more like a real game.',
      },
      {
        time: 1111,
        text: 'Keep that focus. Every touch matters.',
      },
      {
        time: 1116,
        text: "Let's see another one. Dribble, then the combo. Sharp!",
      },
      {
        time: 1121,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 1125,
        text: "Last minute! Let's see the combo at your top speed.",
      },
      {
        time: 1130,
        text: 'Imagine a defender is right in front of you. Beat them with this move!',
      },
      {
        time: 1136,
        text: 'Roll, drag, and explode away! Create that space.',
      },
      {
        time: 1141,
        text: 'Thirty seconds left! Empty the tank on this drill.',
      },
      {
        time: 1146,
        text: 'Come on, faster, cleaner, sharper!',
      },
      {
        time: 1151,
        text: "Don't stop now, you're almost there.",
      },
      {
        time: 1156,
        text: 'Final push. Give me your best one yet.',
      },
      {
        time: 1161,
        text: 'Get ready for the countdown.',
      },
      {
        time: 1165,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1170,
        text: 'Five, four, three, two, one... and time! Awesome job! Next up is Laces Juggling. This drill will help you improve your control of the ball when it is in the air. Ready?',
      },
      {
        time: 1176,
        text: 'Toss the ball up gently and try to keep it in the air using only your laces, the top of your foot. Go for 30 seconds.',
      },
      {
        time: 1193,
        text: 'Tap with your laces, tap with your laces! Try to get at least three taps before you catch it.',
      },
      {
        time: 1199,
        text: 'The key is a soft touch and a relaxed foot. Point your toes down a little.',
      },
      {
        time: 1205,
        text: 'Try to keep the ball from flying too high. Keep it in your control bubble.',
      },
      {
        time: 1210,
        text: "Let's see if you can get five in a row with your right foot.",
      },
      {
        time: 1215,
        text: 'Now five with your left. Challenge that other foot!',
      },
      {
        time: 1220,
        text: 'Laces juggling is crucial for handling passes that come from the air.',
      },
      {
        time: 1225,
        text: "Let's try to alternate feet now. Right lace, left lace, right lace. See how many you can get.",
      },
      {
        time: 1232,
        text: "This is tough, but you're doing great. Keep your eyes on the ball.",
      },
      {
        time: 1237,
        text: "Let's see your record! How many can you get in a row right now?",
      },
      {
        time: 1242,
        text: "Come on, keep it up! Don't let it drop!",
      },
      {
        time: 1247,
        text: 'If it drops, no worries! Just start your count again. Every try makes you better.',
      },
      {
        time: 1253,
        text: 'Focus on your balance. Keep your knees slightly bent.',
      },
      {
        time: 1258,
        text: "Let's see another good streak. Go for it!",
      },
      {
        time: 1263,
        text: "You're building amazing touch. Keep that concentration.",
      },
      {
        time: 1268,
        text: "Let's try to use only your weak foot for a few taps. Challenge it!",
      },
      {
        time: 1273,
        text: 'Good. Now back to both feet. Keep that juggle going.',
      },
      {
        time: 1278,
        text: 'Two minutes in! Your laces control is improving.',
      },
      {
        time: 1283,
        text: "Let's try a little movement. Can you take a small step forward while you juggle?",
      },
      {
        time: 1289,
        text: 'Good. Now a small step back. Keep the ball under control.',
      },
      {
        time: 1294,
        text: 'This is how you learn to move with the ball in the air.',
      },
      {
        time: 1299,
        text: "Let's go back to standing still. Let's go for a new record. Go!",
      },
      {
        time: 1304,
        text: 'Count them out loud! One, two, three, four, five...',
      },
      {
        time: 1309,
        text: 'Keep it alive! Soft touches, quick feet.',
      },
      {
        time: 1314,
        text: "Don't let it get too far away from you. Stay over the ball.",
      },
      {
        time: 1319,
        text: "You're doing great. Keep working hard.",
      },
      {
        time: 1324,
        text: "Let's try another record attempt. Start from zero. Go!",
      },
      {
        time: 1329,
        text: 'Halfway! Keep that focus. Watch the ball all the way to your foot.',
      },
      {
        time: 1334,
        text: "Let's add a challenge. Can you do a laces juggle, then a thigh, then back to your laces?",
      },
      {
        time: 1341,
        text: "Lace, thigh, lace. It's a great combo!",
      },
      {
        time: 1346,
        text: 'This is how you build all-around control. Keep it up.',
      },
      {
        time: 1351,
        text: "Now back to just your laces. Let's see those quick taps.",
      },
      {
        time: 1356,
        text: 'The more you juggle, the better your first touch will be in a game.',
      },
      {
        time: 1361,
        text: "Keep that ball up. Don't give up on it!",
      },
      {
        time: 1366,
        text: "Let's see one more big effort for a new record. Go for it!",
      },
      {
        time: 1371,
        text: "Come on, you've got this. Every touch counts.",
      },
      {
        time: 1376,
        text: 'Keep it going, keep it going!',
      },
      {
        time: 1381,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 1386,
        text: 'Get ready for one last push.',
      },
      {
        time: 1390,
        text: 'Last minute of this drill. Push yourself.',
      },
      {
        time: 1395,
        text: "Thirty seconds left! Let's see a final flurry of juggling.",
      },
      {
        time: 1400,
        text: 'Can you beat your record one more time?',
      },
      {
        time: 1405,
        text: 'Final push! Keep it up!',
      },
      {
        time: 1410,
        text: 'This is where champions are made. Keep working.',
      },
      {
        time: 1415,
        text: 'Come on, a few more seconds!',
      },
      {
        time: 1420,
        text: "Don't stop now, almost there!",
      },
      {
        time: 1425,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 1430,
        text: 'Get ready for the countdown!',
      },
      {
        time: 1435,
        text: 'Five, four, three, two, one... and catch! Fantastic!',
      },
      {
        time: 1440,
        text: "Great laces juggling! Congratulations you have completed Advanced Ball Control! You're a ball master!",
      },
    ],
  },
  {
    id: 2016,
    name: 'Advanced Ball Control',
    categories: ['Ball Control', 'advanced'],
    sports: ['soccer'],
    difficulty: 'advanced',
    skill: 'Ball Control',
    points: 60,
    instructions:
      'This drill will take your ball control to the highest level, focusing on precision under pressure. We will practice controlling the ball at game speed, mastering deceptive touches, and linking moves together seamlessly without the use of cones.',
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstar, ready for a real challenge? First up: 'Shadow Dribbling & Explosions'. Imagine a line in front of you. Let's perform quick, sharp dribbles from side to side across this line for five minutes. Go!",
      },
      {
        time: 9,
        text: 'Fast feet! Fast feet! Use quick inside-outside touches to dance across that imaginary line.',
      },
      {
        time: 16,
        text: "Keep the ball on a string. It shouldn't get more than a foot away from you.",
      },
      {
        time: 21,
        text: "Now, after a few seconds of this quick dribbling, explode forward in a sprint for a few steps, as if you've just beaten a defender. Go!",
      },
      {
        time: 30,
        text: 'Quick dribble... and SPRINT! Quick dribble... and SPRINT! This is all about changing from slow to fast in a heartbeat.',
      },
      {
        time: 38,
        text: "Let's see it again. Five quick side-to-side touches, then explode forward. That's the pattern.",
      },
      {
        time: 44,
        text: 'Good. Now bring it back and repeat. The difference between your quick dribble and your sprint should be huge.',
      },
      {
        time: 51,
        text: 'Keep those touches tight and your head up! A quick peek every few seconds.',
      },
      {
        time: 57,
        text: "Feel that explosive power out of the turns! That's what leaves defenders behind.",
      },
      {
        time: 63,
        text: "Let's see another one. Quick, quick, quick... then BOOM! Explode.",
      },
      {
        time: 69,
        text: "You're looking sharp. Keep that intensity high.",
      },
      {
        time: 74,
        text: 'Now, try to use only your weak foot for the quick side-to-side dribbling. Challenge yourself.',
      },
      {
        time: 81,
        text: 'Good. Now explode out of it. Still using that weak foot for the first touch.',
      },
      {
        time: 87,
        text: "Back to both feet. Let's see another powerful explosion. Push that ball out and chase it.",
      },
      {
        time: 93,
        text: 'One more run! Make it your best one yet. Quick feet, then a big burst.',
      },
      {
        time: 99,
        text: 'Excellent! Keep that pattern going. Quick dribble, then sprint.',
      },
      {
        time: 104,
        text: 'This is how you navigate tight spaces in a game and then break free.',
      },
      {
        time: 110,
        text: "Let's see a little more speed in your side-to-side dribble now. Faster feet!",
      },
      {
        time: 116,
        text: 'Good. Now a bigger explosion. Really push yourself.',
      },
      {
        time: 120,
        text: "Two minutes in! You're doing great. Keep that focus.",
      },
      {
        time: 125,
        text: "Let's change the pattern. Now, do a sole roll across the line, then explode.",
      },
      {
        time: 131,
        text: 'Roll with your right, explode. Good. Now roll with your left, explode.',
      },
      {
        time: 137,
        text: 'This adds a bit of trickery to your movement. Keep the defender guessing.',
      },
      {
        time: 143,
        text: "Let's see five good roll-and-explodes in a row. Go.",
      },
      {
        time: 148,
        text: 'Keep that quality high. A smooth roll, a powerful sprint.',
      },
      {
        time: 153,
        text: "Halfway! Let's go back to the inside-outside touches, but faster now.",
      },
      {
        time: 159,
        text: 'Your feet should be a blur. Quick, quick, quick... then SPRINT!',
      },
      {
        time: 165,
        text: 'Remember to keep your head up. A quick peek before you explode.',
      },
      {
        time: 171,
        text: "This is tough work, but it's making you a better player. Keep it up.",
      },
      {
        time: 177,
        text: "Let's see another one with your weak foot leading the dribble.",
      },
      {
        time: 182,
        text: "Excellent. You're building that confidence.",
      },
      {
        time: 187,
        text: "Now back to both feet. Let's see that beautiful rhythm.",
      },
      {
        time: 192,
        text: 'Keep pushing yourself. The harder you work now, the easier the game will be.',
      },
      {
        time: 198,
        text: "Let's see another powerful explosion. Leave everyone behind!",
      },
      {
        time: 203,
        text: 'Good. Now back to the controlled dribble. Master both speeds.',
      },
      {
        time: 208,
        text: "You're doing great. Keep that focus all the way to the end.",
      },
      {
        time: 213,
        text: "Let's see another sole roll and explode. Make it tricky.",
      },
      {
        time: 218,
        text: "Awesome. You're looking like a real pro.",
      },
      {
        time: 223,
        text: "Keep that intensity high. We're in the final stretch of this exercise.",
      },
      {
        time: 228,
        text: "Let's see one more perfect rep. Quick dribble... and sprint!",
      },
      {
        time: 233,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 238,
        text: "Get ready to give it everything you've got.",
      },
      {
        time: 240,
        text: 'Last minute! Your choice now. Freestyle! Mix the inside-outside dribbles with the sole rolls. But always explode out!',
      },
      {
        time: 249,
        text: 'Be creative. Show me your best combination of quick feet and explosive speed.',
      },
      {
        time: 255,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 261,
        text: 'Faster, sharper, more powerful!',
      },
      {
        time: 266,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 271,
        text: 'Final fifteen seconds. Give me your best sprint yet.',
      },
      {
        time: 276,
        text: "Let's see that power!",
      },
      {
        time: 281,
        text: 'Almost there, keep working!',
      },
      {
        time: 286,
        text: 'Five, four, three, two, one... and time! Excellent work.',
      },
      {
        time: 291,
        text: 'Great first exercise. Next up is Instep Juggling. This drill will help you improve your control of the ball when it is in the air.',
      },
      {
        time: 300,
        text: 'For this exercise. Keep the ball in the air for as long as you can using only the tops of your feet, your laces. Go!',
      },
      {
        time: 310,
        text: 'Tap with your laces, tap with your laces! Aim for a soft, controlled touch so the ball does not go too high.',
      },
      {
        time: 318,
        text: 'Try to get a sequence of at least five touches before you lose control. Count them as you go!',
      },
      {
        time: 325,
        text: 'Good. Try to beat your highest number. The key is to keep your feet relaxed and your body balanced.',
      },
      {
        time: 332,
        text: "Keep that ball in a controlled space in front of you. Don't let it get away.",
      },
      {
        time: 338,
        text: 'Alternate feet as much as you can! Right, left, right, left.',
      },
      {
        time: 344,
        text: "This is tough, but it's how you build a world-class touch.",
      },
      {
        time: 349,
        text: "Let's see a new record. Go for it!",
      },
      {
        time: 354,
        text: 'Come on, keep it up! Fight for every touch.',
      },
      {
        time: 359,
        text: 'If it drops, start again immediately. No problem.',
      },
      {
        time: 364,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 369,
        text: "Let's see another good streak. How many can you get?",
      },
      {
        time: 374,
        text: "Excellent. You're getting the hang of it.",
      },
      {
        time: 379,
        text: "Let's try to add a little spin to the ball. A light flick with your laces.",
      },
      {
        time: 385,
        text: 'This is an advanced technique. It helps you control the ball even better.',
      },
      {
        time: 391,
        text: 'Keep practicing. The more you do it, the more natural it will feel.',
      },
      {
        time: 397,
        text: "Let's see another record attempt. Go for it!",
      },
      {
        time: 402,
        text: 'Come on, push yourself. Can you get one more than last time?',
      },
      {
        time: 408,
        text: "Keep that ball dancing. You're in control.",
      },
      {
        time: 413,
        text: "Let's see some work with your weak foot only. Five taps. Go.",
      },
      {
        time: 418,
        text: 'Good. Now back to both feet. Keep that rally going.',
      },
      {
        time: 423,
        text: "You're building an amazing touch. This is what separates good players from great players.",
      },
      {
        time: 429,
        text: 'Keep your eyes on the ball. Watch it all the way onto your laces.',
      },
      {
        time: 434,
        text: "Let's see another good streak. Don't let it drop.",
      },
      {
        time: 439,
        text: 'Excellent focus. Prepare for the halfway mark.',
      },
      {
        time: 444,
        text: "Halfway! Let's try to keep the ball below your knees. This is a sign of amazing control.",
      },
      {
        time: 451,
        text: "Low juggles are much harder. It requires a softer touch. Let's see it.",
      },
      {
        time: 457,
        text: "Keep your ankle locked but your leg relaxed. It's a tricky balance.",
      },
      {
        time: 463,
        text: "Let's see a streak of ten low juggles. Go!",
      },
      {
        time: 468,
        text: 'Keep it down, keep it down. Soft touches.',
      },
      {
        time: 473,
        text: "You're doing great. This is a professional-level skill.",
      },
      {
        time: 478,
        text: "Let's go back to normal juggling. Feels easier now, right?",
      },
      {
        time: 483,
        text: "Let's see another record attempt. Go for it.",
      },
      {
        time: 488,
        text: 'Come on, push that number higher!',
      },
      {
        time: 493,
        text: "Keep that focus. You're in the zone.",
      },
      {
        time: 498,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 503,
        text: 'Get ready for one last push.',
      },
      {
        time: 507,
        text: "Last minute of this drill. Let's see your best juggling yet.",
      },
      {
        time: 512,
        text: "Thirty seconds left! Empty the tank. Don't let it drop.",
      },
      {
        time: 517,
        text: 'Come on, push yourself. A few more seconds.',
      },
      {
        time: 522,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 527,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 532,
        text: 'Five, four, three, two, one... and catch! Fantastic!',
      },
      {
        time: 538,
        text: "Now let's take juggling to the next level. Use your feet, thighs, chest, and head to keep the ball in the air. Go!",
      },
      {
        time: 547,
        text: 'Try to get a sequence using multiple body parts. Tap with your foot, cushion with your thigh, tap with your head. Keep the ball off the ground!',
      },
      {
        time: 556,
        text: 'Good. The key is a soft touch on every body part. Focus on making clean connections.',
      },
      {
        time: 562,
        text: "Let's see a combo: Foot, thigh, foot, head, catch. Try it!",
      },
      {
        time: 568,
        text: "This is all about touch and timing. You're doing great.",
      },
      {
        time: 573,
        text: "Now, let's see how long you can keep it up without catching. Use your whole body!",
      },
      {
        time: 579,
        text: 'Try to get a touch with every part. Foot, thigh, chest, head.',
      },
      {
        time: 584,
        text: "This is what the pros do to warm up. You're training like a champion.",
      },
      {
        time: 590,
        text: "Keep that ball alive. Don't let it hit the ground.",
      },
      {
        time: 595,
        text: "If it drops, start again. Let's see a good streak.",
      },
      {
        time: 600,
        text: "You're looking like a natural. Keep that focus.",
      },
      {
        time: 605,
        text: "Let's see a sequence. Foot, thigh, chest, thigh, foot. Can you do it?",
      },
      {
        time: 611,
        text: 'This is a great challenge for your coordination. Keep trying.',
      },
      {
        time: 616,
        text: 'Remember to be soft with every touch. You are a ball magnet.',
      },
      {
        time: 621,
        text: "Let's see another good run. How many touches can you get?",
      },
      {
        time: 626,
        text: 'Excellent. Your control is improving with every touch.',
      },
      {
        time: 631,
        text: 'Keep your body balanced and your eyes on the ball.',
      },
      {
        time: 636,
        text: "Let's see another combo. Your choice this time. Be creative.",
      },
      {
        time: 641,
        text: "Awesome! You're thinking like a pro.",
      },
      {
        time: 646,
        text: "Keep that intensity. We're in the final stretch of this exercise.",
      },
      {
        time: 651,
        text: "Let's try to get a touch with your shoulder. A little pop up.",
      },
      {
        time: 656,
        text: 'Good. Now back to your feet and thighs. Keep it alive.',
      },
      {
        time: 661,
        text: "You're using your whole body to control the ball. This is fantastic.",
      },
      {
        time: 666,
        text: "Let's see a new record for total touches. Go for it.",
      },
      {
        time: 671,
        text: 'Count them out loud! One, two, three, four, five...',
      },
      {
        time: 676,
        text: "Keep it going! Don't let it drop.",
      },
      {
        time: 681,
        text: 'Excellent focus. Prepare for the halfway mark.',
      },
      {
        time: 686,
        text: "Halfway! Let's focus on a specific pattern. Foot, thigh, chest. Then catch. Let's see it.",
      },
      {
        time: 693,
        text: "Good. Let's try it again. Foot, thigh, chest, catch. Make it smooth.",
      },
      {
        time: 699,
        text: 'Now with your other foot and thigh. Left foot, left thigh, chest, catch.',
      },
      {
        time: 706,
        text: 'This is great for building your coordination on both sides.',
      },
      {
        time: 711,
        text: "Let's see five good reps of that pattern. Any side you want.",
      },
      {
        time: 716,
        text: 'Keep the quality high. Soft touches on every part of your body.',
      },
      {
        time: 721,
        text: "You're doing great. This is a very advanced skill.",
      },
      {
        time: 726,
        text: "Let's go back to freestyle juggling. Use any body part you want.",
      },
      {
        time: 731,
        text: "Let's see a new record now. Go for it!",
      },
      {
        time: 736,
        text: 'Come on, push that number higher!',
      },
      {
        time: 741,
        text: "Keep that focus. You're in the zone.",
      },
      {
        time: 746,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 751,
        text: 'Get ready for one last push.',
      },
      {
        time: 755,
        text: 'Last minute of this drill. Show me your best all-around control.',
      },
      {
        time: 800,
        text: 'Thirty seconds left! Empty the tank. Keep it up!',
      },
      {
        time: 805,
        text: "Come on, a few more touches. Don't stop now.",
      },
      {
        time: 810,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 815,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 820,
        text: 'Five, four, three, two, one... and catch! Excellent!',
      },
      {
        time: 826,
        text: 'Now we move to a wall exercise. Throw the ball against the wall. As it rebounds, receive it with a soft first touch that also sets up your next move, like a turn. Make sure your first touch is clean and purposeful. Practice this for 20 seconds!',
      },
      {
        time: 849,
        text: 'Throw, receive, turn! Throw, receive, turn! Your first touch should take the ball into space, away from an imaginary defender.',
      },
      {
        time: 857,
        text: 'Good. Try using different parts of your body to receive the ball: your foot, your thigh, your chest. One touch, and turn!',
      },
      {
        time: 865,
        text: 'This is great practice for controlling a ball under pressure and being deceptive.',
      },
      {
        time: 871,
        text: "Let's see a thigh control into a quick turn. Good.",
      },
      {
        time: 876,
        text: 'Now a chest control into a turn. Excellent.',
      },
      {
        time: 881,
        text: 'Keep your eyes on the ball and your body balanced.',
      },
      {
        time: 886,
        text: 'Your first touch is everything! Make it clean and purposeful.',
      },
      {
        time: 891,
        text: "Let's see another one. Throw, receive with your foot, and a sharp turn.",
      },
      {
        time: 896,
        text: "Awesome. You're looking like a pro.",
      },
      {
        time: 901,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 906,
        text: "Let's try to use your weak foot for the turn. Challenge yourself.",
      },
      {
        time: 911,
        text: 'Good. Now back to your strong foot. Keep that rhythm.',
      },
      {
        time: 916,
        text: 'The turn should be explosive. Get away from that imaginary defender.',
      },
      {
        time: 921,
        text: "Let's see three sharp turns in a row. Go.",
      },
      {
        time: 926,
        text: "Excellent. You're building great habits.",
      },
      {
        time: 931,
        text: "Keep that focus. We're almost at the two-minute mark.",
      },
      {
        time: 936,
        text: "Two minutes in! Let's make the turn a specific move. After you control the ball, do a Cruyff turn.",
      },
      {
        time: 943,
        text: "So it's: throw, control, Cruyff turn. Good. Remember to fake the kick.",
      },
      {
        time: 949,
        text: "Let's try it again. Control, Cruyff. Make it smooth.",
      },
      {
        time: 954,
        text: "Now, let's try a different turn. Control the ball, then do a drag-back.",
      },
      {
        time: 960,
        text: "Throw, control, drag-back. Excellent. You're using your whole toolbox.",
      },
      {
        time: 966,
        text: "Let's see five good reps with the Cruyff turn.",
      },
      {
        time: 971,
        text: 'Now five good reps with the drag-back.',
      },
      {
        time: 976,
        text: "You're thinking like a player, linking your control to your next move.",
      },
      {
        time: 981,
        text: 'Keep that intensity high. Every touch should be sharp.',
      },
      {
        time: 986,
        text: "Let's see another Cruyff turn. Make it your best one yet.",
      },
      {
        time: 991,
        text: 'Now another drag-back. Make it just as good.',
      },
      {
        time: 996,
        text: 'Awesome. Prepare for the halfway mark.',
      },
      {
        time: 1001,
        text: "Halfway! Let's add another layer. Your first touch must be deceptive.",
      },
      {
        time: 1007,
        text: 'As the ball comes back, let it run across your body, like a dummy, then take your touch.',
      },
      {
        time: 1014,
        text: "So it's: throw, dummy the rebound, then control and turn.",
      },
      {
        time: 1020,
        text: 'This is a high-level skill. It completely fools defenders.',
      },
      {
        time: 1025,
        text: "Let's see it again. Throw, let it run, then a sharp turn.",
      },
      {
        time: 1030,
        text: 'You have to be quick to get to the ball after the dummy. Fast feet!',
      },
      {
        time: 1035,
        text: "Let's see three good ones in a row. Go.",
      },
      {
        time: 1040,
        text: "Excellent. You're adding layers of deception to your game.",
      },
      {
        time: 1045,
        text: 'Keep that quality high. Every touch matters.',
      },
      {
        time: 1050,
        text: "Let's go back to the simple control and turn. Make it perfect.",
      },
      {
        time: 1055,
        text: 'Now try the dummy and turn again. Challenge yourself.',
      },
      {
        time: 1060,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1065,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1070,
        text: "Last minute of this drill. Let's see your best first touch yet.",
      },
      {
        time: 1075,
        text: 'Your choice now. Simple turn or dummy turn. Surprise me.',
      },
      {
        time: 1080,
        text: 'Thirty seconds left! Empty the tank. Quick turns!',
      },
      {
        time: 1085,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1090,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1095,
        text: 'Faster, sharper, more deceptive!',
      },
      {
        time: 1100,
        text: 'Five, four, three, two, one... and time! Fantastic!',
      },
      {
        time: 1105,
        text: 'Next lets practice a very advanced move called the Zidane Roulette. Place your foot on top of the ball, spin your body over it, and use your other foot to pull the ball away from an imaginary defender. Practice this for 20 seconds!',
      },
      {
        time: 1108,
        text: 'Foot on the ball, spin, pull! Foot on the ball, spin, pull! The key is a smooth, continuous motion, keeping the ball connected to you the whole time. Keep going...',
      },
      {
        time: 1116,
        text: 'Foot on the ball, spin, pull! Foot on the ball, spin, pull! The key is a smooth, continuous motion, keeping the ball connected to you the whole time.',
      },
      {
        time: 1125,
        text: "Let's try it in both directions. Spin to your right, then spin to your left.",
      },
      {
        time: 1131,
        text: "This move is pure deception and close control. You're shielding the ball with your body.",
      },
      {
        time: 1137,
        text: 'Keep your body low and balanced through the spin. It helps you stay in control.',
      },
      {
        time: 1143,
        text: "Let's see five good spins to the right. Go.",
      },
      {
        time: 1148,
        text: 'Now five to the left. Build that muscle memory.',
      },
      {
        time: 1153,
        text: "You're looking like a magician out there. Keep it up.",
      },
      {
        time: 1158,
        text: 'The pull with the second foot has to be quick and sharp.',
      },
      {
        time: 1203,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 1208,
        text: "Excellent. You're mastering the move.",
      },
      {
        time: 1213,
        text: "Let's try it after a few dribbling touches. Dribble, then roulette.",
      },
      {
        time: 1218,
        text: 'This makes it more game-like. Good.',
      },
      {
        time: 1223,
        text: 'Keep practicing that sequence. Dribble, spin, pull.',
      },
      {
        time: 1228,
        text: "Let's see one with your weak foot initiating the spin.",
      },
      {
        time: 1233,
        text: 'Excellent effort. Keep challenging yourself.',
      },
      {
        time: 1238,
        text: "Two minutes in. Let's perfect that spin. It should be fast and tight.",
      },
      {
        time: 1243,
        text: 'Keep the ball under your body as you spin. Protect it.',
      },
      {
        time: 1248,
        text: "Let's see another one. Dribble, spin, pull. Smooth.",
      },
      {
        time: 1253,
        text: 'Now the other way. Keep alternating to become a two-sided player.',
      },
      {
        time: 1258,
        text: "You're doing great. This is a very advanced skill.",
      },
      {
        time: 1303,
        text: "Halfway! Let's add a little hop as you spin. It makes the move quicker.",
      },
      {
        time: 1309,
        text: "Foot on ball, hop and spin, then pull. It's a small detail, but it makes a big difference.",
      },
      {
        time: 1316,
        text: "Let's see three good ones with the hop. Go.",
      },
      {
        time: 1321,
        text: "Excellent. You're making the move more dynamic.",
      },
      {
        time: 1326,
        text: 'Keep that focus. Every repetition is making you better.',
      },
      {
        time: 1331,
        text: "Let's see another one. Make it your best one yet.",
      },
      {
        time: 1336,
        text: "Awesome. You're looking like a pro.",
      },
      {
        time: 1341,
        text: "Keep that quality high. We're building great habits.",
      },
      {
        time: 1346,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 1351,
        text: 'Good. Now get ready for the final minute.',
      },
      {
        time: 1355,
        text: "Last minute of this drill. Let's see it at game speed.",
      },
      {
        time: 1400,
        text: 'Thirty seconds left! Empty the tank. Quick spins!',
      },
      {
        time: 1405,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1410,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1415,
        text: 'Five, four, three, two, one... and time! Fantastic!',
      },
      {
        time: 1420,
        text: "No break! Now for 'Volley & Half-Volley Wall Passes'. Pass the ball hard against a wall. As it returns, try to hit it back to the wall on the first touch (a volley), or let it bounce once and hit it back (a half-volley). Go!",
      },
      {
        time: 1434,
        text: 'Pass, and volley back! Pass, and half-volley back! Keep your eye on the ball and your touch clean.',
      },
      {
        time: 1441,
        text: 'Good. Try to hit the ball back with both your laces for power and your instep for control.',
      },
      {
        time: 1448,
        text: 'This is how you build quick feet and passing accuracy!',
      },
      {
        time: 1453,
        text: "Let's see a volley with your right foot. Good.",
      },
      {
        time: 1458,
        text: 'Now a half-volley with your right. Excellent.',
      },
      {
        time: 1503,
        text: 'Now your left foot. A volley. Good.',
      },
      {
        time: 1508,
        text: 'And a half-volley with your left. Awesome.',
      },
      {
        time: 1513,
        text: "Get your body over the ball for a clean strike! Don't lean back.",
      },
      {
        time: 1518,
        text: "Let's see how many you can get in a row. Keep the rally going.",
      },
      {
        time: 1523,
        text: 'This is a great way to improve your speed of play.',
      },
      {
        time: 1528,
        text: 'Keep that focus. Every pass matters.',
      },
      {
        time: 1533,
        text: "Let's see a powerful laces volley now. Ping it!",
      },
      {
        time: 1538,
        text: 'Good. Now a controlled instep half-volley. Soft touch.',
      },
      {
        time: 1543,
        text: "You're mastering both types of passes. Excellent.",
      },
      {
        time: 1548,
        text: "Keep that rally going. Don't let the ball stop.",
      },
      {
        time: 1553,
        text: 'Two minutes in. Your reactions are getting quicker.',
      },
      {
        time: 1558,
        text: "Let's try to move a little closer to the wall. This makes it even faster!",
      },
      {
        time: 1604,
        text: 'Good. Now a little further away. This challenges your power.',
      },
      {
        time: 1609,
        text: "You're adapting well. This is a sign of a great player.",
      },
      {
        time: 1614,
        text: "Let's focus on weak foot only for 30 seconds. Go!",
      },
      {
        time: 1644,
        text: 'Excellent work. Back to both feet. Keep that rally alive.',
      },
      {
        time: 1649,
        text: "Halfway! Let's see a streak of ten in a row. Your choice of volley or half-volley.",
      },
      {
        time: 1655,
        text: 'Come on, you can do it. Focus on a clean strike every time.',
      },
      {
        time: 1700,
        text: "Keep that quality high. Don't get sloppy, even when you're tired.",
      },
      {
        time: 1705,
        text: "Let's see another good rally. Push yourself.",
      },
      {
        time: 1710,
        text: "Final minute of this drill. Let's see your best passes yet.",
      },
      {
        time: 1715,
        text: 'Thirty seconds left! Empty the tank. Quick feet, quick passes!',
      },
      {
        time: 1720,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1725,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1730,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 1735,
        text: "Last drill! The 'Ultimate Control Combination'. Let's combine everything. Start with the ball.",
      },
      {
        time: 1742,
        text: 'Perform a quick zig-zag dribble across an imaginary line. Now, explode out with a burst of speed! Go!',
      },
      {
        time: 1750,
        text: 'Good. Now, toss the ball up, control it with a one-touch on your thigh, and then perform a Zidane Roulette. Go!',
      },
      {
        time: 1758,
        text: 'Control, and spin! Control, and spin! This is the essence of fluid, advanced control.',
      },
      {
        time: 1765,
        text: "Now, let's do a full sequence: dribble quickly, perform a Cruyff turn, and explode out with a burst of speed! Go!",
      },
      {
        time: 1773,
        text: 'Quick dribble... Cruyff Turn... Sprint! Keep that momentum going!',
      },
      {
        time: 1779,
        text: "Now let's add the wall pass. Wall pass, receive, and do a Maradona turn! Go!",
      },
      {
        time: 1786,
        text: "Pass, receive, and turn! That's it! Keep practicing this for the next few minutes.",
      },
      {
        time: 1792,
        text: "Let's see that sequence again. Dribble, Cruyff, explode.",
      },
      {
        time: 1797,
        text: 'Good. Now the wall pass into a Maradona turn.',
      },
      {
        time: 1802,
        text: "You're linking skills together like a pro. Keep it up.",
      },
      {
        time: 1807,
        text: "This is the last part of the drill. Give it everything you've got!",
      },
      {
        time: 1812,
        text: 'Dribble, turn, sprint! Pass, receive, turn! Last few reps!',
      },
      {
        time: 1817,
        text: "Come on, push yourself. Don't stop now.",
      },
      {
        time: 1822,
        text: 'Final push. Make these last ones your best ones.',
      },
      {
        time: 1827,
        text: "Let's see one more full combination. Make it perfect.",
      },
      {
        time: 1832,
        text: "Excellent work. You're a true ball master.",
      },
      {
        time: 1837,
        text: 'Keep that focus for the final minute.',
      },
      {
        time: 1842,
        text: "Last minute! Freestyle! Combine any of the moves we've done today. Show me your creativity.",
      },
      {
        time: 1848,
        text: 'Thirty seconds left! Empty the tank. Give me your best combo.',
      },
      {
        time: 1853,
        text: 'Come on, faster, sharper, more creative!',
      },
      {
        time: 1858,
        text: 'Final fifteen seconds. Make it a masterpiece.',
      },
      {
        time: 1863,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great job! You have mastered these advanced combinations!',
      },
      {
        time: 1873,
        text: 'Fantastic work today! You put it all together: quick dribbling, precise juggling, one-touch control, and deceptive turns in combination. Keep practicing, and your ball control will become second nature!',
      },
    ],
    timeTotal: 1880,
    equipment: 'Soccer ball, wall',
  },
  {
    id: 2017,
    name: 'Beginner Wall Kicking',
    categories: ['Passing', 'Wall Kicking', 'beginner'],
    sports: ['soccer'],
    difficulty: 'beginner',
    skill: 'Wall Kicking',
    points: 20,
    instructions:
      'This drill is all about getting comfortable passing the ball against a wall and controlling the rebound. Focus on striking the ball with the inside of your foot for accuracy and receiving the ball with a soft touch.',
    equipment: '1 soccer ball, 1 wall',
    timeTotal: 1800,
    timedInstructions: [
      {
        time: 0,
        text: "Let's start with stationary wall passes. Stand a few feet from a wall. Pass the ball to the wall with the inside of your right foot and receive it with the same foot. Go!",
      },
      {
        time: 9,
        text: 'Pass, receive! Pass, receive! Focus on your form: keep your standing foot next to the ball and point your toe towards the wall.',
      },
      {
        time: 18,
        text: 'Lock your ankle and follow through with your foot. A firm, clean strike! Pass, receive!',
      },
      {
        time: 26,
        text: "Great. Now let's switch to your left foot. Pass with your left, receive with your left. Keep it steady.",
      },
      {
        time: 34,
        text: 'Pass, receive! Pass, receive! Get a feel for the rhythm of the ball coming back to you.',
      },
      {
        time: 41,
        text: "Let's do five in a row with your right foot. Count them out!",
      },
      {
        time: 46,
        text: 'Now five in a row with your left. Build that confidence.',
      },
      {
        time: 51,
        text: 'Keep your eyes on the ball and your body balanced.',
      },
      {
        time: 56,
        text: "Let's focus on a clean, soft touch with the inside of your foot.",
      },
      {
        time: 62,
        text: 'Imagine your foot is a pillow. Make the ball land softly.',
      },
      {
        time: 68,
        text: "Let's go back to the right foot. Pass and receive.",
      },
      {
        time: 73,
        text: 'Now the left foot. Keep that quality high.',
      },
      {
        time: 78,
        text: "You're doing great. This is the most important skill in soccer.",
      },
      {
        time: 83,
        text: 'Keep that rhythm going. Pass, receive, pass, receive.',
      },
      {
        time: 88,
        text: "Two minutes in! Keep that focus. Let's see another ten good passes with your right foot.",
      },
      {
        time: 95,
        text: 'And now ten with your left. Keep working that other foot.',
      },
      {
        time: 100,
        text: 'Excellent. Keep alternating every ten passes.',
      },
      {
        time: 105,
        text: 'Remember to stay on your toes. Be ready for the ball.',
      },
      {
        time: 110,
        text: "Let's see a little more power in the pass now. A firm strike.",
      },
      {
        time: 115,
        text: 'Good. Now a soft one. Control that power.',
      },
      {
        time: 120,
        text: "Halfway! Let's try to pass it a little harder now. A firm pass.",
      },
      {
        time: 125,
        text: 'Good. Now a soft pass. Control your power.',
      },
      {
        time: 130,
        text: "Let's see another firm pass with your right foot.",
      },
      {
        time: 135,
        text: 'And a firm pass with your left. Excellent.',
      },
      {
        time: 140,
        text: 'Keep alternating feet now. Right, then left.',
      },
      {
        time: 145,
        text: 'This is building your coordination. Keep it up.',
      },
      {
        time: 150,
        text: "Don't forget to lock that ankle. It's the key to a good pass.",
      },
      {
        time: 155,
        text: "You're looking like a pro. Keep that focus.",
      },
      {
        time: 160,
        text: "Let's see another five in a row with your right foot.",
      },
      {
        time: 165,
        text: "And five with your left. You've got this.",
      },
      {
        time: 170,
        text: 'Keep that quality high. Every pass matters.',
      },
      {
        time: 175,
        text: "Let's see a little more pace now. Quicker passes.",
      },
      {
        time: 180,
        text: "Three minutes in! You're doing great. Keep that energy up.",
      },
      {
        time: 185,
        text: "Let's focus on your first touch. Make it perfect every time.",
      },
      {
        time: 190,
        text: 'A soft touch will set you up for a great pass.',
      },
      {
        time: 195,
        text: "Let's see that pillow touch. Good.",
      },
      {
        time: 200,
        text: 'Keep working hard. This is how champions are made.',
      },
      {
        time: 205,
        text: "Let's see another ten in a row. Go for it.",
      },
      {
        time: 210,
        text: 'Excellent. Keep that rhythm.',
      },
      {
        time: 215,
        text: "Final minute of this drill. Let's see your best passes yet.",
      },
      {
        time: 220,
        text: 'Thirty seconds left! Empty the tank. Keep kicking!',
      },
      {
        time: 225,
        text: "Come on, a few more passes. Don't stop now.",
      },
      {
        time: 230,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 235,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Great job.',
      },
      {
        time: 245,
        text: "No break, straight into 'One-Touch Pass'. When the ball comes back from the wall, hit it back immediately with a single touch. Go!",
      },
      {
        time: 255,
        text: 'One-touch pass! One-touch pass! Use the inside of your right foot. Try not to let the ball stop.',
      },
      {
        time: 262,
        text: 'Keep your toes up and your ankle firm. This requires a lot of focus and fast feet!',
      },
      {
        time: 269,
        text: 'Good! Now try with your left foot. One-touch pass! One-touch pass!',
      },
      {
        time: 276,
        text: 'This is all about a clean, controlled strike without hesitation.',
      },
      {
        time: 281,
        text: "Let's see ten in a row with your right foot. Count them out!",
      },
      {
        time: 286,
        text: 'Now ten with your left. This is a great challenge.',
      },
      {
        time: 291,
        text: "Stay on your toes and be ready for the rebound! Don't be flat-footed.",
      },
      {
        time: 297,
        text: 'Keep that rhythm going! Quick, light touches.',
      },
      {
        time: 302,
        text: "You're doing great. This is how you play fast in a game.",
      },
      {
        time: 307,
        text: "Let's see another good streak with your right foot.",
      },
      {
        time: 312,
        text: 'And another with your left. Excellent.',
      },
      {
        time: 317,
        text: 'Keep your body over the ball. It helps you keep your passes low.',
      },
      {
        time: 322,
        text: "Let's see a little more power now. A firm one-touch pass.",
      },
      {
        time: 327,
        text: 'Good. Now a soft one. Control that power.',
      },
      {
        time: 332,
        text: "You're looking like a natural. Keep that focus.",
      },
      {
        time: 337,
        text: "Two minutes in! Let's see if you can get 20 in a row. Go for it.",
      },
      {
        time: 342,
        text: "Keep counting. Don't lose that rhythm.",
      },
      {
        time: 347,
        text: 'If you mess up, just start again. No worries.',
      },
      {
        time: 352,
        text: "This is a tough drill. You're doing great.",
      },
      {
        time: 357,
        text: "Let's see another good streak. Come on, you can do it.",
      },
      {
        time: 362,
        text: 'Halfway! Keep that energy high. Quick feet, quick passes.',
      },
      {
        time: 368,
        text: "Let's try to alternate feet now. Right, left, right, left. This is tricky!",
      },
      {
        time: 374,
        text: "Good. It's all about adjusting your feet quickly.",
      },
      {
        time: 379,
        text: "Let's go back to just your right foot. Make it perfect.",
      },
      {
        time: 384,
        text: 'Now just your left. Just as good.',
      },
      {
        time: 389,
        text: "You're building great skills. Keep it up.",
      },
      {
        time: 394,
        text: "Final minute of this drill. Let's see your best one-touch passing.",
      },
      {
        time: 399,
        text: 'Thirty seconds left! Empty the tank. Keep that rally going.',
      },
      {
        time: 404,
        text: "Come on, a few more passes. Don't stop now.",
      },
      {
        time: 409,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 414,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Awesome!',
      },
      {
        time: 424,
        text: "No rest! Now for 'Two-Touch Passes'. When the ball comes back, use your first touch to stop or control it, then use your second touch to pass it back. Go!",
      },
      {
        time: 435,
        text: 'First touch to control... second touch to pass! Practice with your right foot.',
      },
      {
        time: 441,
        text: "The first touch should be soft and 'kill' the ball right in front of you. A good first touch makes the second touch easy!",
      },
      {
        time: 450,
        text: 'Good. Now try with your left foot. First touch... second touch! Keep the ball close.',
      },
      {
        time: 457,
        text: 'Keep your body low and over the ball on that first touch.',
      },
      {
        time: 462,
        text: "Let's see five good two-touch passes with your right foot.",
      },
      {
        time: 467,
        text: 'Now five with your left. Build that confidence.',
      },
      {
        time: 472,
        text: 'Great job controlling that ball! This is a crucial skill.',
      },
      {
        time: 477,
        text: 'This is how you make passes in a game! Control and pass.',
      },
      {
        time: 482,
        text: "Let's try to make your first touch directional. A little push to the side.",
      },
      {
        time: 488,
        text: 'Good. Now pass it back. This creates space from a defender.',
      },
      {
        time: 493,
        text: "Let's see that again. Control with a directional touch, then pass.",
      },
      {
        time: 499,
        text: "Excellent. You're thinking like a player.",
      },
      {
        time: 504,
        text: 'Now back to the simple two-touch. Make it perfect.',
      },
      {
        time: 509,
        text: 'Keep that focus. Every touch matters.',
      },
      {
        time: 514,
        text: "Let's see another directional touch. Push it to your left.",
      },
      {
        time: 519,
        text: 'Good. Now push it to your right.',
      },
      {
        time: 524,
        text: "You're learning to control the ball into space. This is a big step.",
      },
      {
        time: 529,
        text: "Keep practicing that. It's a great habit to build.",
      },
      {
        time: 534,
        text: "Let's see one more perfect two-touch pass. Control, then pass.",
      },
      {
        time: 539,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 544,
        text: "Final minute of this drill. Let's see your best two-touch passing.",
      },
      {
        time: 549,
        text: 'Thirty seconds left! Empty the tank. Finish strong!',
      },
      {
        time: 554,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 559,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 564,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time! Excellent!',
      },
      {
        time: 574,
        text: "No break! Let's work on 'Changing Distance'. Start a few big steps back from the wall. Go!",
      },
      {
        time: 582,
        text: 'From here, we need a powerful pass. Strike the ball hard so it comes right back to you.',
      },
      {
        time: 589,
        text: 'Good. Control it with a soft touch. Now, take a few steps closer to the wall.',
      },
      {
        time: 595,
        text: "Now that you're close, the passes have to be quick! Let's see some one-touch passes. Fast feet!",
      },
      {
        time: 602,
        text: 'Quick, quick, quick! Good. Now, take a few steps back again to where you started.',
      },
      {
        time: 609,
        text: 'Back to power! A big, strong pass to the wall. Control the rebound.',
      },
      {
        time: 615,
        text: "That's the pattern: Far away is for power, close up is for speed. Let's do it again. Move closer!",
      },
      {
        time: 622,
        text: 'Quick passes! One-touch! Keep the rally going.',
      },
      {
        time: 628,
        text: 'Good. Now move back again. Get ready for a power pass.',
      },
      {
        time: 634,
        text: 'Strike it! Good power. Control it.',
      },
      {
        time: 640,
        text: "You're doing great. This is how you learn to play at different speeds.",
      },
      {
        time: 646,
        text: "Let's do the whole sequence again. Move closer to the wall.",
      },
      {
        time: 652,
        text: "Quick one-touch passes. Let's see five in a row.",
      },
      {
        time: 658,
        text: 'Good. Now move back. Big pass coming up.',
      },
      {
        time: 664,
        text: 'Power pass! Excellent. Control it.',
      },
      {
        time: 670,
        text: 'Two minutes in! Your passes are looking great at both distances.',
      },
      {
        time: 676,
        text: "Let's focus on the movement. Quick steps in, quick steps back.",
      },
      {
        time: 682,
        text: "Let's go. Move in close. Quick passes now!",
      },
      {
        time: 688,
        text: 'Keep it going, faster, faster!',
      },
      {
        time: 694,
        text: 'Good. Now move back out. Get set.',
      },
      {
        time: 700,
        text: 'And a powerful pass! Strike it clean.',
      },
      {
        time: 706,
        text: "Awesome. You're really getting the hang of this.",
      },
      {
        time: 712,
        text: "Let's try it with your weak foot now. Move in close.",
      },
      {
        time: 718,
        text: "Quick one-touch passes with your other foot. It's a challenge!",
      },
      {
        time: 724,
        text: 'Good. Now move back out. A powerful pass with your weak foot.',
      },
      {
        time: 730,
        text: "Excellent effort! That's how you get better.",
      },
      {
        time: 736,
        text: 'Halfway! Back to your strong foot. Move in close.',
      },
      {
        time: 742,
        text: "Let's see your fastest one-touch passes yet. Go!",
      },
      {
        time: 748,
        text: "Keep that rally alive! Don't let it stop.",
      },
      {
        time: 754,
        text: 'Good. Now move back out. Biggest pass of the day.',
      },
      {
        time: 760,
        text: 'Wow! Great power. Control that rebound.',
      },
      {
        time: 766,
        text: "Let's do that sequence one more time. Move in.",
      },
      {
        time: 772,
        text: 'Quick passes. Go, go, go!',
      },
      {
        time: 778,
        text: 'And back out. Power pass!',
      },
      {
        time: 784,
        text: "Excellent. You're a machine.",
      },
      {
        time: 790,
        text: "Keep that focus. We're in the final stretch.",
      },
      {
        time: 796,
        text: "Let's see another powerful pass from far away.",
      },
      {
        time: 802,
        text: 'Now quickly move in for some fast passes.',
      },
      {
        time: 808,
        text: "You're adapting so well. This is a great skill.",
      },
      {
        time: 814,
        text: 'Last minute! Your choice. Start close or start far, but keep moving between them.',
      },
      {
        time: 821,
        text: 'Show me that change of pace. Quick passes, then powerful passes.',
      },
      {
        time: 827,
        text: 'Thirty seconds left! Empty the tank. Keep moving and passing.',
      },
      {
        time: 833,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 839,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 845,
        text: "Five, four, three, two, one... and time! You've got it!",
      },
      {
        time: 850,
        text: "No break! Let's add some movement with 'Pass and Move'. Pass to the wall with your right foot.",
      },
      {
        time: 857,
        text: 'As it comes back, take one touch to your right with the inside of your foot.',
      },
      {
        time: 863,
        text: 'Now, from your new spot, pass it back to the wall. Good!',
      },
      {
        time: 868,
        text: "So it's: Pass, touch to the side, pass again. You're moving in a little line.",
      },
      {
        time: 875,
        text: 'Now try it going the other way. Pass with your left, touch to your left, and pass again.',
      },
      {
        time: 882,
        text: 'This is how you find open space in a game. Keep it up.',
      },
      {
        time: 887,
        text: "Let's see three good reps to the right. Pass, touch, pass.",
      },
      {
        time: 892,
        text: 'Now three to the left. Keep that quality high.',
      },
      {
        time: 897,
        text: 'The touch to the side should be soft and controlled.',
      },
      {
        time: 902,
        text: "Don't push it too far away. Just a little touch to create a new angle.",
      },
      {
        time: 908,
        text: "You're doing great. This is a smart way to play.",
      },
      {
        time: 913,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 918,
        text: 'Excellent. Keep that focus.',
      },
      {
        time: 923,
        text: "Let's try to speed it up now. A quicker touch, a quicker pass.",
      },
      {
        time: 928,
        text: "That's it! The faster you can do this, the better.",
      },
      {
        time: 933,
        text: "Keep that intensity. We're in the final stretch of this exercise.",
      },
      {
        time: 938,
        text: "Final minute of this drill. Let's see your best passing and moving.",
      },
      {
        time: 943,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 948,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 953,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 958,
        text: 'Five, four, three, two, one... and time! Awesome!',
      },
      {
        time: 963,
        text: "No break! Last drill! 'Wall Control Challenge'. This is a fun game to finish. Pass the ball to the wall.",
      },
      {
        time: 970,
        text: "As the ball comes back, I'm going to shout an instruction! You have to react and do what I say. Ready? Pass the ball!",
      },
      {
        time: 978,
        text: 'RIGHT FOOT!',
      },
      {
        time: 981,
        text: 'Good. Pass it again.',
      },
      {
        time: 984,
        text: 'LEFT FOOT!',
      },
      {
        time: 987,
        text: 'Nice. Pass it again.',
      },
      {
        time: 990,
        text: 'STOP IT DEAD!',
      },
      {
        time: 993,
        text: 'Perfect. Pass it again.',
      },
      {
        time: 996,
        text: 'ONE-TOUCH!',
      },
      {
        time: 999,
        text: "Awesome! Keep passing. Let's go again.",
      },
      {
        time: 1002,
        text: 'LEFT FOOT!',
      },
      {
        time: 1005,
        text: 'Good. Pass it.',
      },
      {
        time: 1008,
        text: 'RIGHT FOOT!',
      },
      {
        time: 1011,
        text: 'Nice. Pass it.',
      },
      {
        time: 1014,
        text: 'ONE-TOUCH!',
      },
      {
        time: 1017,
        text: 'Keep it going! Pass it back.',
      },
      {
        time: 1020,
        text: 'STOP IT DEAD!',
      },
      {
        time: 1023,
        text: "Excellent reactions! You're thinking like a soccer player.",
      },
      {
        time: 1028,
        text: "Keep this rally going. I'll keep calling them out. Pass the ball.",
      },
      {
        time: 1032,
        text: 'RIGHT FOOT!',
      },
      {
        time: 1035,
        text: 'Pass it.',
      },
      {
        time: 1038,
        text: 'ONE-TOUCH!',
      },
      {
        time: 1041,
        text: 'Pass it.',
      },
      {
        time: 1044,
        text: 'LEFT FOOT!',
      },
      {
        time: 1047,
        text: 'Pass it.',
      },
      {
        time: 1050,
        text: 'STOP IT DEAD!',
      },
      {
        time: 1053,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1058,
        text: "Final minute of the drill. Let's go rapid-fire. Pass it!",
      },
      {
        time: 1062,
        text: 'RIGHT FOOT!',
      },
      {
        time: 1065,
        text: 'LEFT FOOT!',
      },
      {
        time: 1068,
        text: 'ONE-TOUCH!',
      },
      {
        time: 1071,
        text: 'STOP IT DEAD!',
      },
      {
        time: 1074,
        text: 'RIGHT FOOT!',
      },
      {
        time: 1077,
        text: 'Thirty seconds left! Keep your feet ready!',
      },
      {
        time: 1081,
        text: 'LEFT FOOT!',
      },
      {
        time: 1084,
        text: 'ONE-TOUCH!',
      },
      {
        time: 1087,
        text: 'STOP IT DEAD!',
      },
      {
        time: 1090,
        text: 'RIGHT FOOT!',
      },
      {
        time: 1093,
        text: 'Come on, final push!',
      },
      {
        time: 1096,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time!',
      },
      {
        time: 1106,
        text: "Fantastic work today! You practiced stationary passes, one-touch passes, two-touch passes, and even added more power to your kicks. You finished with a super fun challenge and did great! Keep practicing, and you'll be a passing master in no time!",
      },
    ],
  },
  {
    id: 2018,
    name: 'Intermediate Wall Kicking',
    categories: ['Passing', 'Wall Kicking', 'intermediate'],
    sports: ['soccer'],
    difficulty: 'intermediate',
    skill: 'Wall Kicking',
    points: 40,
    instructions:
      'This drill will improve your passing accuracy and first touch under more dynamic conditions. You will learn to control and pass the ball while moving, simulate game situations, and build speed of play.',
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstar, ready to level up your skills? Let's start with a super fun move I call 'Messi's Weave & Escape'! Let's go!",
      },
      {
        time: 8,
        text: "First, let's practice the weave. Imagine a straight line in front of you. We're going to dribble in a zig-zag pattern right over that line.",
      },
      {
        time: 16,
        text: 'Use the outside of your right foot to push the ball to the right. Then, use the outside of your left foot to push it back to the left. Little touches!',
      },
      {
        time: 25,
        text: "Outside right, outside left. Keep the ball super close, like Messi does. Imagine it's glued to your feet!",
      },
      {
        time: 32,
        text: "Let's see that zig-zag. Weave, weave, weave. Keep your knees bent and your body low.",
      },
      {
        time: 39,
        text: "Good! Now, let's try the weave using the inside of your feet. Use the inside of your right foot to go left, and the inside of your left to go right.",
      },
      {
        time: 48,
        text: "This feels different, right? It's great for protecting the ball. Inside right, inside left. Keep that weave going.",
      },
      {
        time: 56,
        text: "Awesome! Now, back to using the outside of your feet. That's the Messi style. Keep it close and quick.",
      },
      {
        time: 63,
        text: "One minute in! Your weave is looking fantastic. Now for the 'escape' part!",
      },
      {
        time: 70,
        text: 'After you do three or four weaves, I want you to take a bigger touch with the outside of your foot and EXPLODE forward!',
      },
      {
        time: 78,
        text: "So it's: Weave, weave, weave... then ESCAPE! A big touch and a two-step sprint. Let's try it.",
      },
      {
        time: 86,
        text: "That's it! You're faking out the defender with the slow weave, then surprising them with your speed.",
      },
      {
        time: 93,
        text: "Let's see it again. Slow, controlled weave... now EXPLODE! Push that ball and go!",
      },
      {
        time: 100,
        text: "Remember to use the outside of your foot for that escape touch. It's the fastest way to get away.",
      },
      {
        time: 107,
        text: "Let's see three good ones in a row. Weave, weave, escape! Good. Reset and go again.",
      },
      {
        time: 114,
        text: "You're doing great. The change of speed is what makes this move so tricky for defenders.",
      },
      {
        time: 120,
        text: "Two minutes in! Let's make the weave even tighter. Smaller, quicker touches.",
      },
      {
        time: 127,
        text: "Imagine the defender's feet are there. You have to dribble right around them.",
      },
      {
        time: 133,
        text: 'Good, tight weave... now ESCAPE! A big, powerful touch.',
      },
      {
        time: 139,
        text: "Let's try to escape to the left. Weave, then use the outside of your left foot to explode.",
      },
      {
        time: 146,
        text: 'Awesome! Now escape to the right. Use the outside of your right foot.',
      },
      {
        time: 152,
        text: 'You need to be able to go both ways to be a super tricky dribbler.',
      },
      {
        time: 158,
        text: 'Halfway! Keep that quality high. Every weave should be tight, every escape should be fast.',
      },
      {
        time: 165,
        text: "Let's try the inside-of-the-foot weave again. Inside right, inside left...",
      },
      {
        time: 172,
        text: 'And now, can you escape with the inside of your foot? A little push forward into space.',
      },
      {
        time: 179,
        text: "That's a different feel, but it's another great option to have. Keep practicing it.",
      },
      {
        time: 185,
        text: "Now back to the Messi weave. Outside of the feet. Let's see it.",
      },
      {
        time: 191,
        text: 'Weave, weave, weave... ESCAPE! Excellent speed.',
      },
      {
        time: 197,
        text: "Remember to keep your head up during the weave. A quick peek to see the space you're going to run into.",
      },
      {
        time: 205,
        text: 'This is how you think like a real soccer player. You see the space, then you attack it.',
      },
      {
        time: 212,
        text: "Let's see another one. Weave with your head up... now go!",
      },
      {
        time: 218,
        text: "You're looking like a pro. Keep that focus.",
      },
      {
        time: 223,
        text: "Let's challenge your weak foot. Try to do the whole escape with your other foot. Weave, then escape.",
      },
      {
        time: 231,
        text: 'Good effort! The more you use that foot, the stronger it gets.',
      },
      {
        time: 237,
        text: 'Prepare for the final minute. Get ready to push your speed.',
      },
      {
        time: 240,
        text: "Last minute! Let's see your fastest weaves and your most explosive escapes!",
      },
      {
        time: 247,
        text: "Don't worry about making mistakes. Just focus on going as fast as you can while keeping the ball close.",
      },
      {
        time: 255,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 261,
        text: 'Faster, sharper, more explosive!',
      },
      {
        time: 266,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 271,
        text: 'Final fifteen seconds. Give me your best one yet.',
      },
      {
        time: 276,
        text: 'Weave, weave, ESCAPE! Leave them in the dust!',
      },
      {
        time: 281,
        text: 'Almost there, keep working!',
      },
      {
        time: 286,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 291,
        text: "Next, let's learn a super cool move from a legend: 'Ronaldo's Chop & Explode'!",
      },
      {
        time: 299,
        text: 'This move is all about a surprise change of direction. Start by dribbling forward with a little bit of speed.',
      },
      {
        time: 306,
        text: "Now, as you're running, jump forward slightly on your left foot. As you jump, swing your right leg and use the inside of your foot to 'chop' the ball behind your standing left leg.",
      },
      {
        time: 318,
        text: "It's a jump and a chop at the same time! The ball should go sharply to the side. Let's practice that motion.",
      },
      {
        time: 325,
        text: 'Dribble, jump, chop! Good. Now try it with your other foot. Jump on your right foot and chop with your left.',
      },
      {
        time: 334,
        text: "This move is so good because it fakes like you're going to shoot, but then you disappear in another direction!",
      },
      {
        time: 342,
        text: "Let's see five good chops with your right foot. Dribble, jump, chop. Reset and go again.",
      },
      {
        time: 350,
        text: 'Now five with your left. The more you practice, the sharper that chop will get.',
      },
      {
        time: 358,
        text: "One minute in! Your chop is looking good. Now let's add the 'explode' part.",
      },
      {
        time: 365,
        text: 'After you chop the ball, your very next step should be a big, powerful sprint in that new direction.',
      },
      {
        time: 373,
        text: "So it's: Dribble, jump, chop, and EXPLODE! Get away from that imaginary defender.",
      },
      {
        time: 381,
        text: "Let's see it. A sharp chop and a quick getaway. Awesome!",
      },
      {
        time: 387,
        text: "Remember to sell the fake. Swing your arms like you're about to shoot the ball.",
      },
      {
        time: 394,
        text: 'The bigger the fake, the more the defender will be tricked.',
      },
      {
        time: 400,
        text: "Let's see that big fake now. Dribble, fake, chop, explode!",
      },
      {
        time: 407,
        text: "Excellent! You're looking like a real trickster out there.",
      },
      {
        time: 412,
        text: 'Keep that focus. Every repetition makes the move faster.',
      },
      {
        time: 417,
        text: "Two minutes in! Let's try to do the chop while dribbling a little bit faster.",
      },
      {
        time: 424,
        text: 'A faster dribble makes the chop even more surprising.',
      },
      {
        time: 430,
        text: 'Keep the ball under control, then a quick jump and chop. Good!',
      },
      {
        time: 436,
        text: "Let's see it with your other foot at speed. Dribble fast, then chop!",
      },
      {
        time: 442,
        text: "You're doing great. This is a very advanced skill.",
      },
      {
        time: 447,
        text: "Halfway! Let's focus on the power of the chop. It needs to be a sharp, crisp touch.",
      },
      {
        time: 454,
        text: "Really snap your foot through the ball. Don't just push it.",
      },
      {
        time: 460,
        text: "Let's see that snap. Dribble, then a powerful chop. Yes!",
      },
      {
        time: 466,
        text: 'The sharper the chop, the more space you create.',
      },
      {
        time: 471,
        text: "Let's see three powerful chops in a row with your right foot.",
      },
      {
        time: 477,
        text: 'Now three with your left. Make them just as powerful.',
      },
      {
        time: 483,
        text: "You're really starting to master this move. Keep it up.",
      },
      {
        time: 488,
        text: "Let's see another one. Dribble with pace, and a sharp, powerful chop.",
      },
      {
        time: 495,
        text: "Excellent. You're looking like Ronaldo out there.",
      },
      {
        time: 500,
        text: 'Keep that quality high. Every move should be your best one.',
      },
      {
        time: 506,
        text: "Let's see one more with your weak foot. A really sharp chop.",
      },
      {
        time: 512,
        text: 'Awesome! Get ready for the final minute.',
      },
      {
        time: 516,
        text: "Last minute! Let's put it all together. Speed, deception, and power!",
      },
      {
        time: 523,
        text: 'Dribble in fast, give a big fake with your body, a sharp chop with your foot, and explode away.',
      },
      {
        time: 531,
        text: "This is the game-winning move. Let's see it!",
      },
      {
        time: 536,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 542,
        text: 'Faster, sharper, more explosive!',
      },
      {
        time: 547,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 552,
        text: 'Final fifteen seconds. Give me your best one yet.',
      },
      {
        time: 557,
        text: 'Dribble, fake, chop, EXPLODE! Leave them in the dust!',
      },
      {
        time: 562,
        text: 'Almost there, keep working!',
      },
      {
        time: 567,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 572,
        text: 'That was a fantastic second exercise. Get ready for the next one!',
      },
      {
        time: 572,
        text: "Next up, a super sneaky move to trick defenders called 'The L-Turn'!",
      },
      {
        time: 580,
        text: 'This move is perfect for turning away from pressure. Start with the ball slightly in front of you.',
      },
      {
        time: 587,
        text: 'Put your right foot on the ball and drag it back towards your body. As it passes your standing leg, use the inside of the SAME foot to push it behind your standing leg.',
      },
      {
        time: 599,
        text: "The ball makes an 'L' shape. Drag back, push sideways. It's a quick 90-degree turn.",
      },
      {
        time: 607,
        text: "Let's practice that motion. Drag, push. Drag, push. Now try it with your left foot.",
      },
      {
        time: 615,
        text: "It's a two-part move, but it should feel like one smooth action. Let's break it down.",
      },
      {
        time: 621,
        text: "Focus on the 'drag' first. A soft pull with the sole of your foot.",
      },
      {
        time: 627,
        text: "Now the 'push'. A sharp tap with the inside of your foot behind your leg.",
      },
      {
        time: 633,
        text: "Let's put it together. Drag, push. Good. Reset.",
      },
      {
        time: 638,
        text: 'Now with the left foot. Drag, push. Keep your balance.',
      },
      {
        time: 643,
        text: 'The key is to hop on your standing foot as you do the push. It creates space.',
      },
      {
        time: 649,
        text: "Let's see five in a row with your right foot. Make them clean.",
      },
      {
        time: 654,
        text: 'Good. Now five with your left. The repetition is important.',
      },
      {
        time: 659,
        text: 'One minute in. Your turns are getting sharper.',
      },
      {
        time: 664,
        text: 'Try to keep your head up as you do it. A quick glance.',
      },
      {
        time: 669,
        text: 'This move is great for shielding the ball from a defender on your back.',
      },
      {
        time: 675,
        text: "Let's see a bit more speed now. A quicker drag, a quicker push.",
      },
      {
        time: 681,
        text: "That's it! The speed makes it much more effective.",
      },
      {
        time: 686,
        text: 'Keep that quality high. Prepare for the two-minute mark.',
      },
      {
        time: 690,
        text: "Two minutes in! The L-Turn should be one fluid motion. It's a great way to shield the ball while turning.",
      },
      {
        time: 698,
        text: 'Use your body to protect the ball as you execute the turn.',
      },
      {
        time: 703,
        text: "Let's try to do it after a few dribbling steps now. Dribble, L-Turn, accelerate.",
      },
      {
        time: 710,
        text: "Good. The move is much more effective when you're already moving.",
      },
      {
        time: 715,
        text: 'Keep practicing that sequence. Dribble, turn, go.',
      },
      {
        time: 720,
        text: "Let's see it with your left foot now. Dribble, L-Turn left.",
      },
      {
        time: 725,
        text: 'The more you practice, the more natural it will feel in a game.',
      },
      {
        time: 730,
        text: 'Keep that focus. Every repetition counts.',
      },
      {
        time: 735,
        text: "Let's see a bit more speed on the entry dribble.",
      },
      {
        time: 740,
        text: 'Good. Now a sharp turn. Be decisive.',
      },
      {
        time: 745,
        text: "Excellent. You're almost at the halfway point.",
      },
      {
        time: 750,
        text: 'Prepare to add the dribbling element.',
      },
      {
        time: 753,
        text: 'Halfway point! Now, try to do it after a few dribbling steps. Dribble, then execute a sharp L-Turn to change direction.',
      },
      {
        time: 761,
        text: 'This makes it game-realistic. Dribble at a defender, then turn away into space.',
      },
      {
        time: 767,
        text: "Let's see a good, sharp turn now. No lazy movements.",
      },
      {
        time: 772,
        text: 'The first touch after the turn should be an explosion of speed.',
      },
      {
        time: 777,
        text: "Let's see five perfect ones. Dribble, turn, explode. Go.",
      },
      {
        time: 782,
        text: "Keep the quality high. Don't get sloppy.",
      },
      {
        time: 787,
        text: "That's it. You're looking like a real player.",
      },
      {
        time: 792,
        text: 'Keep your head up. Scan for space before you turn.',
      },
      {
        time: 797,
        text: 'This is how you create space for yourself on the field.',
      },
      {
        time: 802,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 807,
        text: 'Excellent. Prepare for the final minute.',
      },
      {
        time: 812,
        text: 'Get ready to push the speed.',
      },
      {
        time: 815,
        text: "Last minute! Let's see some speed. How quickly can you execute the turn and accelerate into a new space?",
      },
      {
        time: 822,
        text: 'The first step after the turn should be an explosion. Go!',
      },
      {
        time: 827,
        text: 'Come on, push the pace. Faster turns, faster acceleration.',
      },
      {
        time: 832,
        text: 'Thirty seconds left. Empty the tank on this drill.',
      },
      {
        time: 837,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 842,
        text: 'Five, four, three, two, one... and time! Excellent control.',
      },
      {
        time: 847,
        text: 'Great work on that sneaky turn! Next up, a legendary move called the Cruyff Turn!',
      },
      {
        time: 853,
        text: 'This is a classic fake. Start by shaping your body like you are going to kick or pass the ball forward.',
      },
      {
        time: 860,
        text: "But instead of kicking it, bring your kicking foot behind your standing leg and use the inside of your foot to 'chop' the ball behind you.",
      },
      {
        time: 869,
        text: "As you chop it, you pivot your body and explode in the new direction. It's a 180-degree turn.",
      },
      {
        time: 877,
        text: "Let's practice that. Dribble forward, fake the kick, and chop it behind you. It's all about the surprise!",
      },
      {
        time: 885,
        text: 'Try with your other foot. Fake with the left, chop with the left.',
      },
      {
        time: 890,
        text: 'The fake is the most important part. You have to make the imaginary defender believe you are shooting.',
      },
      {
        time: 897,
        text: "Swing your arms and body like you're about to shoot.",
      },
      {
        time: 902,
        text: "The 'chop' needs to be precise, tucking the ball right behind your heel so you can run onto it.",
      },
      {
        time: 909,
        text: "Let's break it down. Dribble, plant your standing foot, fake the kick, chop, and pivot. All one motion.",
      },
      {
        time: 917,
        text: 'Good. The pivot is just as important as the chop. You have to turn your body quickly.',
      },
      {
        time: 923,
        text: "Let's see five good ones with your right foot. Focus on the fake.",
      },
      {
        time: 928,
        text: 'Now five with your left. Make the fake just as convincing.',
      },
      {
        time: 933,
        text: "Excellent. You're getting the hang of it. Let's add some speed.",
      },
      {
        time: 938,
        text: "Two minutes in. Let's see some dynamic Cruyff turns. A big fake and a quick escape. Make it look smooth.",
      },
      {
        time: 946,
        text: 'Dribble with a bit of pace before you execute the turn.',
      },
      {
        time: 951,
        text: 'The change of direction should be sharp and sudden.',
      },
      {
        time: 956,
        text: 'Keep practicing. The more you do it, the more it becomes a natural reaction.',
      },
      {
        time: 1001,
        text: "Let's see three in a row at speed. Go.",
      },
      {
        time: 1006,
        text: 'Awesome. Keep that quality high.',
      },
      {
        time: 1011,
        text: "Remember to accelerate after the turn. That's the most important part.",
      },
      {
        time: 1017,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 1022,
        text: 'Good effort. Keep working on it.',
      },
      {
        time: 1027,
        text: 'Prepare for the halfway mark.',
      },
      {
        time: 1030,
        text: "Halfway! Let's see five perfect Cruyff turns in a row. Dribble, fake, chop, explode.",
      },
      {
        time: 1037,
        text: 'The explosion after the turn is what makes the move so effective. Create that space.',
      },
      {
        time: 1043,
        text: "Don't just turn; turn with a purpose. Accelerate into the new space.",
      },
      {
        time: 1049,
        text: "Let's count them. One... good explosion. Two... nice fake.",
      },
      {
        time: 1055,
        text: 'Three... keep it up. Four... almost there.',
      },
      {
        time: 1100,
        text: "Five! Excellent. You've mastered the sequence.",
      },
      {
        time: 1105,
        text: "Let's see a few more at your own pace. Make them perfect.",
      },
      {
        time: 1110,
        text: 'Keep that focus. Every detail matters.',
      },
      {
        time: 1115,
        text: "Let's see one more. Make it your best one yet.",
      },
      {
        time: 1120,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1123,
        text: 'Last minute! Now at game speed. Dribble with pace, execute the turn sharply, and accelerate away.',
      },
      {
        time: 1130,
        text: 'Come on, push the tempo. This is how you beat players in a match.',
      },
      {
        time: 1135,
        text: "Thirty seconds left. Give me everything you've got.",
      },
      {
        time: 1140,
        text: 'Faster, cleaner, sharper!',
      },
      {
        time: 1145,
        text: "Final fifteen seconds. Don't stop now.",
      },
      {
        time: 1150,
        text: 'Five, four, three, two, one... and time! Beautiful turn.',
      },
      {
        time: 1155,
        text: 'Great work on that legendary move! Straight into the next drill called Weave & Explode!',
      },
      {
        time: 1200,
        text: 'Imagine you are dribbling through a line of shoes.',
      },
      {
        time: 1205,
        text: 'As you make your final turn out of the weave, you must take a big, explosive touch and accelerate forward.',
      },
      {
        time: 1213,
        text: "This drill combines tight control with a sudden change of pace. You're lulling the defender to sleep in the weave, then exploding past them.",
      },
      {
        time: 1221,
        text: "So it's: Weave, weave, weave, EXPLODE! Then dribble back and do it again.",
      },
      {
        time: 1226,
        text: "Let's focus on the weave first. Soft, outside-of-the-foot touches to navigate the turns.",
      },
      {
        time: 1232,
        text: "Keep the ball close. Don't let it get away from you in the turns.",
      },
      {
        time: 1237,
        text: "Now, the explosion. It's a powerful push forward into space.",
      },
      {
        time: 1242,
        text: "Let's see the full sequence. Slow, controlled weave, then a sudden burst of speed.",
      },
      {
        time: 1248,
        text: "Let's see three good ones in a row. Weave and explode!",
      },
      {
        time: 1253,
        text: 'One... good. Two... nice and sharp.',
      },
      {
        time: 1258,
        text: 'Three! Excellent. Keep that rhythm.',
      },
      {
        time: 1303,
        text: "Let's try to use the inside of your feet for the weave now. A different feel.",
      },
      {
        time: 1309,
        text: 'Good. Now explode out of it. Awesome.',
      },
      {
        time: 1314,
        text: 'Back to the outside-of-the-foot weave. The Messi style.',
      },
      {
        time: 1319,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1324,
        text: 'Two minutes in. The explosive touch should be with the outside of your foot, pushing the ball into space.',
      },
      {
        time: 1331,
        text: "Let's see five good repetitions. The contrast between slow and fast should be obvious.",
      },
      {
        time: 1337,
        text: 'Keep the quality high. Slow and controlled, then fast and powerful.',
      },
      {
        time: 1342,
        text: 'This is a great way to develop your change of pace, a key skill for any dribbler.',
      },
      {
        time: 1348,
        text: "Let's focus on the transition. The last touch of the weave should be the first touch of the explosion.",
      },
      {
        time: 1354,
        text: 'No extra touches in between. Make it seamless.',
      },
      {
        time: 1359,
        text: "Keep your head up during the weave so you can see the space you're about to attack.",
      },
      {
        time: 1365,
        text: "Let's see a bit more speed in the weave now. Quicker feet, quicker turns.",
      },
      {
        time: 1370,
        text: 'Good. Now a bigger explosion. Really push that ball out and sprint.',
      },
      {
        time: 1375,
        text: "You're doing great. This is a tiring drill, but it's worth it.",
      },
      {
        time: 1380,
        text: "Let's see one with your weak foot leading the weave.",
      },
      {
        time: 1385,
        text: 'Excellent. Prepare for the halfway mark.',
      },
      {
        time: 1388,
        text: "Halfway! Let's see a real difference in speed. Slow and controlled in the weave, then a lightning-fast burst.",
      },
      {
        time: 1395,
        text: "Try to keep your head up during the weave to see the space you're going to attack.",
      },
      {
        time: 1400,
        text: "This is a great drill for game situations where you're in a crowded area and then suddenly see an opening.",
      },
      {
        time: 1407,
        text: "Let's see three perfect reps in a row. Weave, explode. Reset.",
      },
      {
        time: 1412,
        text: "The quality can't drop. Stay focused on every touch.",
      },
      {
        time: 1417,
        text: 'Keep your body low to the ground. It helps with your balance and acceleration.',
      },
      {
        time: 1422,
        text: "Let's see a bit more creativity in the weave. Use different parts of your feet.",
      },
      {
        time: 1427,
        text: 'Awesome. Get ready for the final minute.',
      },
      {
        time: 1432,
        text: 'Last minute! Challenge yourself. How quickly can you get through the weave and how fast is your explosion?',
      },
      {
        time: 1439,
        text: 'Push the pace now. Every repetition should be at your maximum speed.',
      },
      {
        time: 1444,
        text: 'Thirty seconds left. Empty the tank on this drill!',
      },
      {
        time: 1449,
        text: 'Come on, one more good one! Weave and explode!',
      },
      {
        time: 1454,
        text: 'Ten seconds! Keep concentrating! And TIME! Great change of pace.',
      },
      {
        time: 1459,
        text: 'Excellent work! Get ready for the final exercise.',
      },
      {
        time: 1459,
        text: 'Excellent work! Get ready for the final exercise.',
      },
      {
        time: 1464,
        text: "It's time for the 'Skill Move Combo'!",
      },
      {
        time: 1470,
        text: 'This is your time to be a creator. For the next five minutes, you must link the skills we learned today into combinations.',
      },
      {
        time: 1479,
        text: 'Try a step-over that flows right into an L-Turn. Or a Cruyff turn that sets you up for a weave.',
      },
      {
        time: 1487,
        text: "There are no rules here except to be creative and fluid. Don't just do one move and stop. Make the moves flow together.",
      },
      {
        time: 1496,
        text: 'How many different moves can you link together in one continuous dribble? Challenge yourself!',
      },
      {
        time: 1503,
        text: "Let's start with a simple two-move combo. Step-over into an L-Turn. Go.",
      },
      {
        time: 1509,
        text: 'Good. Now try a Cruyff turn into a step-over.',
      },
      {
        time: 1514,
        text: 'The key is to make the last touch of the first move the setup for the second move.',
      },
      {
        time: 1520,
        text: "Let's see a double step-over into a burst of speed.",
      },
      {
        time: 1525,
        text: "Keep your head up. You're a playmaker, you need to see the field.",
      },
      {
        time: 1530,
        text: "Don't be afraid to try your weak foot. A weak foot L-Turn is a great skill.",
      },
      {
        time: 1536,
        text: 'The ball is your dance partner. Lead the dance.',
      },
      {
        time: 1541,
        text: 'Keep that intensity. Your feet should be constantly moving, even between moves.',
      },
      {
        time: 1547,
        text: "Let's see a L-Turn into a Cruyff turn. A double turn!",
      },
      {
        time: 1552,
        text: "That's it! You're thinking like a creative player.",
      },
      {
        time: 1557,
        text: 'Keep the ball close. The best players have the ball on a string.',
      },
      {
        time: 1562,
        text: "Let's see another combo. Your choice this time. Surprise me.",
      },
      {
        time: 1567,
        text: 'Excellent. The more you practice linking moves, the more unpredictable you become.',
      },
      {
        time: 1573,
        text: 'Keep your body low and your center of gravity down. It helps with quick changes of direction.',
      },
      {
        time: 1579,
        text: "Let's see a step-over with your weak foot now.",
      },
      {
        time: 1584,
        text: 'Good. Now link it with another move.',
      },
      {
        time: 1589,
        text: 'Prepare for the one-minute mark. Keep that quality high.',
      },
      {
        time: 1592,
        text: 'One minute in. The transition between moves is key. Your last touch of one move should be the first touch of the next.',
      },
      {
        time: 1600,
        text: "Let's see a combo. Cruyff turn into an L-Turn. Make it smooth.",
      },
      {
        time: 1607,
        text: 'Good. Now a step-over into a burst of speed. Explode out of the move.',
      },
      {
        time: 1613,
        text: "Keep your head up. Even when you're linking moves, you need to see the field.",
      },
      {
        time: 1620,
        text: "Don't be afraid to try your weak foot. A weak foot step-over is a great tool.",
      },
      {
        time: 1627,
        text: 'The ball is your dance partner. Lead the dance.',
      },
      {
        time: 1635,
        text: 'Keep that intensity. Your feet should be constantly moving.',
      },
      {
        time: 1645,
        text: "Let's see a double step-over now. Quick feet!",
      },
      {
        time: 1655,
        text: "Two minutes in. Try to perform the moves at game speed. Don't be afraid to make mistakes. That's how you learn.",
      },
      {
        time: 1663,
        text: "Mistakes are proof that you're trying. Push your limits.",
      },
      {
        time: 1668,
        text: "Let's link three moves. Step-over, L-Turn, explode. Go.",
      },
      {
        time: 1678,
        text: 'Keep your body low and balanced as you transition between the moves.',
      },
      {
        time: 1688,
        text: "Now, freestyle again. What's your favorite combo? Show it to me.",
      },
      {
        time: 1698,
        text: 'Sell the fakes! Make the imaginary defender look silly.',
      },
      {
        time: 1708,
        text: "The quality can't drop, even when you're tired. Stay sharp.",
      },
      {
        time: 1713,
        text: 'Halfway! This is where you build the muscle memory to use these skills in a real game without thinking.',
      },
      {
        time: 1721,
        text: 'Every repetition is programming your brain and your feet to work together.',
      },
      {
        time: 1728,
        text: "Let's see a Cruyff turn with your weak foot. Challenge yourself.",
      },
      {
        time: 1735,
        text: "Good. Now back to your strong foot. Feel the difference? That's practice paying off.",
      },
      {
        time: 1743,
        text: 'Combine a turn with a straight-line dribble. Turn, then explode.',
      },
      {
        time: 1753,
        text: 'Keep that ball on a string. Perfect control.',
      },
      {
        time: 1763,
        text: 'Prepare for the final push. Deep breaths.',
      },
      {
        time: 1768,
        text: 'Last minute! Empty the tank! Show me your best combination of the day.',
      },
      {
        time: 1775,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1782,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1788,
        text: "Thirty seconds! Let's see some real flair and confidence!",
      },
      {
        time: 1793,
        text: 'Link three moves together now! Go!',
      },
      {
        time: 1798,
        text: "Keep it going! Don't stop moving!",
      },
      {
        time: 1803,
        text: 'Final fifteen seconds! Make it a masterpiece!',
      },
      {
        time: 1808,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1818,
        text: "Incredible work! You've added some serious weapons to your dribbling arsenal. Be proud of that effort.",
      },
      {
        time: 1828,
        text: "Let's cool down. Walk around, shake out your legs. You've earned your rest. Amazing job today!",
      },
    ],
    timeTotal: 955,
    equipment: 'Soccer ball, wall',
  },
  {
    id: 2019,
    name: 'Advanced Wall Kicking',
    categories: ['Passing', 'Wall Kicking', 'advanced'],
    sports: ['soccer'],
    difficulty: 'advanced',
    skill: 'Wall Kicking',
    points: 60,
    instructions:
      'This drill will fine-tune your passing and receiving to a professional level. We will focus on high-speed combinations, aerial control, and deceptive movements to make your play faster, more precise, and more effective in a game.',
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstar, ready for a pro-level challenge? We're going to learn a move I call 'De Bruyne's Driven Double Pass'! Stand about ten big steps from the wall. Let's go!",
      },
      {
        time: 10,
        text: 'This is all about power and control. First, hit a super powerful pass against the wall. Use your laces, the top of your foot, to drive it low and fast!',
      },
      {
        time: 19,
        text: 'That ball is going to come back fast! Your job is to control it with one perfect, soft touch. Be a ball magnet!',
      },
      {
        time: 27,
        text: "With your very next touch, I want you to hit another driven pass right back at the wall. So it's: POWER PASS, soft touch, POWER PASS!",
      },
      {
        time: 36,
        text: "Let's try it with your right foot. Drive it, control it, drive it again! Awesome!",
      },
      {
        time: 43,
        text: "Now let's try it with your left foot. Drive, control, drive! It's tricky, but you can do it.",
      },
      {
        time: 50,
        text: 'The first touch is the secret. It has to be perfect to set up your second pass. Imagine your foot is a pillow.',
      },
      {
        time: 58,
        text: "Let's see that powerful pass again. Keep your body over the ball and follow through.",
      },
      {
        time: 64,
        text: "One minute in! Your passes are looking powerful. Let's see five good double passes in a row with your right foot.",
      },
      {
        time: 72,
        text: 'Now five with your left. Build that confidence in your other foot!',
      },
      {
        time: 78,
        text: 'This is how the best midfielders in the world, like Kevin De Bruyne, play. Fast, powerful, and with perfect control.',
      },
      {
        time: 87,
        text: "Let's focus on the speed between your control touch and your second pass. It should be super quick. Touch-pass!",
      },
      {
        time: 95,
        text: "Let's see that quick transition now. Drive, control, and get that second pass off instantly!",
      },
      {
        time: 102,
        text: "Excellent! You're thinking fast and playing fast.",
      },
      {
        time: 107,
        text: 'Keep your eyes on the ball. Watch it all the way onto your foot for that control touch.',
      },
      {
        time: 113,
        text: "Let's see another one. Make it your best one yet. Power and control.",
      },
      {
        time: 120,
        text: "Two minutes in! You're looking like a real pro. Keep that quality high.",
      },
      {
        time: 126,
        text: "Let's add a challenge. After your second driven pass, the rebound will come back. Try to control that one too.",
      },
      {
        time: 134,
        text: "So it's a continuous rally. Drive, control, drive, control. See how many you can get.",
      },
      {
        time: 141,
        text: 'This is tough! It requires a lot of focus. Keep working hard.',
      },
      {
        time: 147,
        text: "Let's go back to the double pass. Drive, control, drive. Make it perfect.",
      },
      {
        time: 154,
        text: "Halfway! Let's focus on your body shape. After you hit the first pass, don't just stand there. Be on your toes, ready to move.",
      },
      {
        time: 163,
        text: 'A good athletic stance will help you control that fast rebound.',
      },
      {
        time: 168,
        text: "Let's see it now. Good pass, good stance, good control, good pass back. Excellent.",
      },
      {
        time: 175,
        text: "Let's see three perfect reps in a row with your right foot. Go.",
      },
      {
        time: 180,
        text: "Now three with your left. Don't let that quality drop.",
      },
      {
        time: 185,
        text: "You're building amazing habits. This is how you train to be the best.",
      },
      {
        time: 191,
        text: "Let's see another one. A little more power on that first pass. Challenge yourself.",
      },
      {
        time: 198,
        text: 'Great control! That was a rocket! Now a powerful pass back.',
      },
      {
        time: 204,
        text: "You're doing great. Keep that energy high.",
      },
      {
        time: 209,
        text: "Let's see another one with your weak foot. A powerful, clean strike.",
      },
      {
        time: 215,
        text: "Awesome! You're making both feet into weapons.",
      },
      {
        time: 220,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 225,
        text: "Let's see one more perfect double pass. Make it your best one yet.",
      },
      {
        time: 231,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 236,
        text: "Get ready to give it everything you've got.",
      },
      {
        time: 240,
        text: "Last minute! Let's see your most powerful and controlled passes. Game speed!",
      },
      {
        time: 247,
        text: "Imagine you're hitting a game-winning pass. That's the quality I want to see.",
      },
      {
        time: 253,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 259,
        text: 'Faster, sharper, more powerful!',
      },
      {
        time: 264,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 269,
        text: 'Final fifteen seconds. Give me your best pass yet.',
      },
      {
        time: 274,
        text: "Let's see that power!",
      },
      {
        time: 279,
        text: 'Almost there, keep working!',
      },
      {
        time: 284,
        text: 'Five, four, three, two, one... and time! Excellent work.',
      },
      {
        time: 291,
        text: 'That was a tough drill and you crushed it! Get ready for the next one.',
      },
      {
        time: 295,
        text: "Next, let's learn a super tricky move from a master trickster, a move called Neymar's No-Look Wall Combo!",
      },
      {
        time: 300,
        text: "This is all about deception! Start by passing the ball to the wall with your right foot. Now, as the ball is coming back to you, quickly look all the way to your left. Don't look at the ball! Give it a try.",
      },
      {
        time: 306,
        text: 'Now, as you receive the ball, take a soft touch and pass it back to the wall. Great job and keep practicing!',
      },
      {
        time: 313,
        text: 'Trust your feet! Receive the ball with a soft touch from your right foot without looking at it. Then stop.',
      },
      {
        time: 320,
        text: "Let's try that again. Pass with your right, look away to the left, and control it blind! It's a magic trick!",
      },
      {
        time: 328,
        text: "Now let's try the other way. Pass with your left foot, look all the way to your right, and control it with your left foot.",
      },
      {
        time: 337,
        text: "This is tough, but it teaches you to feel the ball without seeing it. Let's see five good no-look controls with your right foot.",
      },
      {
        time: 345,
        text: 'Now five with your left. The bigger you look away, the more you trick the defender!',
      },
      {
        time: 352,
        text: "One minute in! Your no-look control is getting better. Now let's add the combo.",
      },
      {
        time: 359,
        text: 'After you control the ball without looking, your very next touch should be a quick pass back to the wall.',
      },
      {
        time: 367,
        text: "So it's: Pass, look away, control, pass back! It's a quick two-touch sequence.",
      },
      {
        time: 375,
        text: "Let's see it. Pass right, look left, control-pass with your right. Good!",
      },
      {
        time: 382,
        text: 'Now the other way. Pass left, look right, control-pass with your left. Excellent!',
      },
      {
        time: 390,
        text: "This is how you play fast and trick your opponents. They'll never know where you're going.",
      },
      {
        time: 397,
        text: 'Keep that control touch soft. It has to set you up perfectly for the pass back.',
      },
      {
        time: 403,
        text: "Let's see three sharp combos in a row with your right foot.",
      },
      {
        time: 408,
        text: 'Now three with your left. Keep that quality high.',
      },
      {
        time: 413,
        text: "Two minutes in! Let's make it even trickier. Your control touch must be directional.",
      },
      {
        time: 420,
        text: 'Pass with your right foot, look away left. As you control it with your right, push the ball slightly to your right.',
      },
      {
        time: 429,
        text: "This is a no-look, directional touch! It's a superstar move. Then pass it back.",
      },
      {
        time: 436,
        text: "Let's see it again. Pass, look away, take a touch into space, then pass back. Awesome!",
      },
      {
        time: 444,
        text: 'Now try it with your left foot. Pass left, look right, take a touch to your left, and pass back.',
      },
      {
        time: 452,
        text: "You're not just controlling the ball; you're moving it into a new space, all without looking.",
      },
      {
        time: 459,
        text: 'This is how you create space and time for yourself on the field.',
      },
      {
        time: 465,
        text: 'Halfway! Keep that focus. The deception is in your eyes and your body.',
      },
      {
        time: 472,
        text: "Really exaggerate that look away. Make the imaginary defender think you've lost the ball.",
      },
      {
        time: 480,
        text: "Then, a quick touch and pass before they know what happened. You're a magician!",
      },
      {
        time: 487,
        text: "Let's see five good deceptive combos in a row. Go for it.",
      },
      {
        time: 493,
        text: 'Keep that quality high. A good pass, a big look away, a clean touch, and a sharp pass back.',
      },
      {
        time: 501,
        text: "You're doing great. This is a very advanced skill.",
      },
      {
        time: 506,
        text: "Let's see one more with your weak foot. Challenge yourself.",
      },
      {
        time: 511,
        text: 'Excellent effort! Keep working on it.',
      },
      {
        time: 516,
        text: "Now back to your strong foot. Let's make it perfect.",
      },
      {
        time: 521,
        text: 'The more you practice this, the more it will become a natural reaction in a game.',
      },
      {
        time: 528,
        text: 'Prepare for the final minute. Get ready to push the speed.',
      },
      {
        time: 532,
        text: "Last minute! Let's see this move at game speed. Fast, deceptive, and clean!",
      },
      {
        time: 539,
        text: 'A firm pass to the wall, a quick look away, and an even quicker touch-pass combo.',
      },
      {
        time: 546,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 552,
        text: 'Faster, sharper, more deceptive!',
      },
      {
        time: 557,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 562,
        text: 'Final fifteen seconds. Give me your best one yet.',
      },
      {
        time: 567,
        text: 'Look away, control, pass! Perfect!',
      },
      {
        time: 572,
        text: 'Almost there, keep working!',
      },
      {
        time: 577,
        text: 'Five, four, three, two, one... and time! Excellent work.',
      },
      {
        time: 582,
        text: 'That was some next-level deception! Take a quick breather.',
      },
      {
        time: 590,
        text: "Let's test your reactions with a super fun drill I call The Wall Reaction Gauntlet! Start by passing the ball firmly against the wall. As it comes back, your job is to control it with one soft touch and stop it dead.",
      },
      {
        time: 598,
        text: "Pass, and a perfect trap! Let's see it again. Pass, trap! Make that ball your best friend.",
      },
      {
        time: 605,
        text: "Now, let's switch it up. Pass to the wall, but this time, play it back with one touch. A one-touch pass!",
      },
      {
        time: 613,
        text: 'Good! Now back to a two-touch trap. Pass, then control.',
      },
      {
        time: 619,
        text: 'Now, one-touch pass again! You have to be ready to switch between them. This is how you keep defenders guessing.',
      },
      {
        time: 627,
        text: "Let's get a rhythm. Two-touch, one-touch, two-touch, one-touch. Go!",
      },
      {
        time: 634,
        text: "Keep your feet buzzing and your mind sharp. You never know what's coming next in a game.",
      },
      {
        time: 641,
        text: "Let's see five good reps of that pattern. You've got this.",
      },
      {
        time: 646,
        text: "One minute in! You're looking sharp. Now for a new challenge.",
      },
      {
        time: 652,
        text: 'Pass the ball to the wall. As it comes back, control it with your thigh, then pass it back.',
      },
      {
        time: 659,
        text: "Good! Thigh control, then pass. Let's see it again.",
      },
      {
        time: 664,
        text: 'Now, pass to the wall and control the rebound with your chest! Big chest, soft touch.',
      },
      {
        time: 672,
        text: 'Excellent! Now back to your feet. A quick one-touch pass.',
      },
      {
        time: 678,
        text: "You have to be ready to use any part of your body. Let's see another thigh control.",
      },
      {
        time: 684,
        text: 'Good. Now a chest control. Perfect.',
      },
      {
        time: 689,
        text: 'Now a simple two-touch with your feet. Control, then pass.',
      },
      {
        time: 695,
        text: "You're a complete player, using your whole body. Keep it up.",
      },
      {
        time: 700,
        text: 'Two minutes in! Your reactions are getting faster.',
      },
      {
        time: 705,
        text: "Let's make it a reaction game. I'll call out the body part. Pass to the wall and listen for my call. Ready? Pass!",
      },
      {
        time: 713,
        text: 'THIGH!',
      },
      {
        time: 716,
        text: 'Good. Pass it again.',
      },
      {
        time: 719,
        text: 'FOOT!',
      },
      {
        time: 722,
        text: 'Nice. Pass it again.',
      },
      {
        time: 725,
        text: 'CHEST!',
      },
      {
        time: 728,
        text: 'Perfect. Pass it again.',
      },
      {
        time: 731,
        text: 'ONE-TOUCH FOOT!',
      },
      {
        time: 734,
        text: "Awesome! Keep passing. Let's go again.",
      },
      {
        time: 737,
        text: 'THIGH!',
      },
      {
        time: 740,
        text: 'Good. Pass it.',
      },
      {
        time: 743,
        text: 'CHEST!',
      },
      {
        time: 746,
        text: 'Nice. Pass it.',
      },
      {
        time: 749,
        text: 'FOOT!',
      },
      {
        time: 752,
        text: 'Keep it going! Pass it back.',
      },
      {
        time: 755,
        text: 'ONE-TOUCH FOOT!',
      },
      {
        time: 758,
        text: "Excellent reactions! You're thinking and playing at the same time.",
      },
      {
        time: 763,
        text: "Halfway! Let's add one more command: 'TURN!'",
      },
      {
        time: 768,
        text: 'When I say turn, control the ball with your foot and take your first touch away from the wall into space.',
      },
      {
        time: 776,
        text: "Ready? Let's play the game again. Pass the ball!",
      },
      {
        time: 780,
        text: 'THIGH!',
      },
      {
        time: 783,
        text: 'Good. Pass it.',
      },
      {
        time: 786,
        text: 'TURN!',
      },
      {
        time: 789,
        text: 'Excellent turn! Now pass it back.',
      },
      {
        time: 793,
        text: 'CHEST!',
      },
      {
        time: 796,
        text: 'Good. Pass it.',
      },
      {
        time: 799,
        text: 'ONE-TOUCH!',
      },
      {
        time: 802,
        text: 'Awesome! Keep that brain working.',
      },
      {
        time: 807,
        text: "Let's see another TURN! Be sharp.",
      },
      {
        time: 811,
        text: 'Good. Pass it back.',
      },
      {
        time: 814,
        text: 'This is how you prepare for the chaos of a real game.',
      },
      {
        time: 819,
        text: "Keep that focus. You're doing great.",
      },
      {
        time: 824,
        text: 'Prepare for the final minute.',
      },
      {
        time: 828,
        text: "Last minute! Let's go rapid-fire. I'll call it fast, you react fast. Pass it!",
      },
      {
        time: 833,
        text: 'FOOT!',
      },
      {
        time: 836,
        text: 'THIGH!',
      },
      {
        time: 839,
        text: 'TURN!',
      },
      {
        time: 842,
        text: 'CHEST!',
      },
      {
        time: 845,
        text: 'ONE-TOUCH!',
      },
      {
        time: 848,
        text: 'Thirty seconds left! Keep your feet ready!',
      },
      {
        time: 852,
        text: 'TURN!',
      },
      {
        time: 855,
        text: 'FOOT!',
      },
      {
        time: 858,
        text: 'CHEST!',
      },
      {
        time: 861,
        text: 'ONE-TOUCH!',
      },
      {
        time: 864,
        text: 'Come on, final push!',
      },
      {
        time: 867,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... and time!',
      },
      {
        time: 877,
        text: 'Fantastic reactions! That was a tough drill and you were amazing. Get ready for the next one.',
      },
      {
        time: 883,
        text: "Next one is special... a move of pure genius. The great soccer master Zidane invented it and it's called The Zidane Roulette! This is a super cool 360-degree spin.",
      },
      {
        time: 888,
        text: 'Start by dribbling slowly. Place the bottom of your right foot on the ball to stop it.',
      },
      {
        time: 892,
        text: "As you do that, you're going to spin your body clockwise, all the way around the ball, using the ball as your center point.",
      },
      {
        time: 896,
        text: "As you complete the spin, use your other foot, your left foot, to pull the ball away and escape. It's a spin and a pull!",
      },
      {
        time: 900,
        text: "Let's practice that motion. Foot on the ball, spin, pull! Foot on the ball, spin, pull! Foot on the ball, spin, pull! Foot on the ball, spin, pull!",
      },
      {
        time: 909,
        text: 'Try again. Foot on the ball, spin, pull! Foot on the ball, spin, pull!',
      },
      {
        time: 917,
        text: 'The key is a smooth, continuous motion, keeping the ball connected to you the whole time.',
      },
      {
        time: 924,
        text: "Now let's try it the other way. Put your left foot on the ball, spin counter-clockwise, and pull it away with your right.",
      },
      {
        time: 933,
        text: "This move is pure deception and close control. You're shielding the ball with your body as you spin.",
      },
      {
        time: 940,
        text: 'Keep your body low and balanced through the spin. It helps you stay in control.',
      },
      {
        time: 946,
        text: "Let's see five good spins to the right. Go.",
      },
      {
        time: 951,
        text: 'Now five to the left. Build that muscle memory.',
      },
      {
        time: 956,
        text: "You're looking like a magician out there. Keep it up.",
      },
      {
        time: 1001,
        text: 'The pull with the second foot has to be quick and sharp.',
      },
      {
        time: 1006,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 1011,
        text: "Excellent. You're mastering the move.",
      },
      {
        time: 1016,
        text: "Let's try it after a few dribbling touches. Dribble, then roulette.",
      },
      {
        time: 1021,
        text: 'This makes it more game-like. Good.',
      },
      {
        time: 1026,
        text: 'Keep practicing that sequence. Dribble, spin, pull.',
      },
      {
        time: 1031,
        text: "Let's see one with your weak foot initiating the spin.",
      },
      {
        time: 1036,
        text: 'Excellent effort. Keep challenging yourself.',
      },
      {
        time: 1041,
        text: 'Two minutes in! Your spins are looking fluid.',
      },
      {
        time: 1046,
        text: "Let's focus on the exit. After you pull the ball, your first touch away should be an explosion of speed.",
      },
      {
        time: 1053,
        text: "So it's: Dribble, spin, pull, and accelerate! Go.",
      },
      {
        time: 1059,
        text: "That's it! Create that separation from the defender.",
      },
      {
        time: 1064,
        text: "Let's see three more with that explosive exit.",
      },
      {
        time: 1069,
        text: 'Keep the quality high. Every part of the move matters.',
      },
      {
        time: 1074,
        text: "You're doing great. This is a top-tier skill.",
      },
      {
        time: 1079,
        text: "Halfway! Let's see it at game speed now.",
      },
      {
        time: 1084,
        text: 'Dribble in with pace, execute a fast roulette, and explode out.',
      },
      {
        time: 1090,
        text: "Don't let the ball get away from you during the spin. Keep it tight.",
      },
      {
        time: 1095,
        text: "Let's see another one at game speed. Come on, push the tempo.",
      },
      {
        time: 1100,
        text: "Excellent. You're looking sharp.",
      },
      {
        time: 1105,
        text: "Keep that focus. We're building muscle memory.",
      },
      {
        time: 1110,
        text: "Let's see one more perfect roulette before the final minute.",
      },
      {
        time: 1115,
        text: 'Awesome. Prepare to empty the tank.',
      },
      {
        time: 1120,
        text: "Last minute of this drill. Let's see it at your absolute top speed.",
      },
      {
        time: 1125,
        text: 'Thirty seconds left! Empty the tank. Quick spins, fast exits!',
      },
      {
        time: 1130,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1135,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1140,
        text: 'Five, four, three, two, one... and time! Fantastic!',
      },
      {
        time: 1145,
        text: 'That was a dizzying display of skill! Get ready for the next one.',
      },
      {
        time: 1145,
        text: "No break! Now for 'Volley & Half-Volley Wall Passes'. Pass the ball hard against a wall. As it returns, try to hit it back to the wall on the first touch, a volley, or let it bounce once and hit it back, a half-volley. Go!",
      },
      {
        time: 1159,
        text: 'Pass, and volley back! Pass, and half-volley back! Keep your eye on the ball and your touch clean.',
      },
      {
        time: 1166,
        text: 'Good. Try to hit the ball back with both your laces for power and your instep for control.',
      },
      {
        time: 1173,
        text: 'This is how you build quick feet and passing accuracy!',
      },
      {
        time: 1178,
        text: "Let's see a volley with your right foot. Good.",
      },
      {
        time: 1183,
        text: 'Now a half-volley with your right. Excellent.',
      },
      {
        time: 1188,
        text: 'Now your left foot. A volley. Good.',
      },
      {
        time: 1193,
        text: 'And a half-volley with your left. Awesome.',
      },
      {
        time: 1198,
        text: "Get your body over the ball for a clean strike! Don't lean back.",
      },
      {
        time: 1203,
        text: "Let's see how many you can get in a row. Keep the rally going.",
      },
      {
        time: 1208,
        text: 'This is a great way to improve your speed of play.',
      },
      {
        time: 1213,
        text: 'Keep that focus. Every pass matters.',
      },
      {
        time: 1218,
        text: "Let's see a powerful laces volley now. Ping it!",
      },
      {
        time: 1223,
        text: 'Good. Now a controlled instep half-volley. Soft touch.',
      },
      {
        time: 1228,
        text: "You're mastering both types of passes. Excellent.",
      },
      {
        time: 1233,
        text: "Keep that rally going. Don't let the ball stop.",
      },
      {
        time: 1238,
        text: 'Two minutes in. Your reactions are getting quicker.',
      },
      {
        time: 1243,
        text: "Let's try to move a little closer to the wall. This makes it even faster!",
      },
      {
        time: 1249,
        text: 'Good. Now a little further away. This challenges your power.',
      },
      {
        time: 1254,
        text: "You're adapting well. This is a sign of a great player.",
      },
      {
        time: 1259,
        text: "Let's focus on weak foot only for 30 seconds. Go!",
      },
      {
        time: 1289,
        text: 'Excellent work. Back to both feet. Keep that rally alive.',
      },
      {
        time: 1294,
        text: "Halfway! Let's see a streak of ten in a row. Your choice of volley or half-volley.",
      },
      {
        time: 1300,
        text: 'Come on, you can do it. Focus on a clean strike every time.',
      },
      {
        time: 1305,
        text: "Keep that quality high. Don't get sloppy, even when you're tired.",
      },
      {
        time: 1310,
        text: "Let's see another good rally. Push yourself.",
      },
      {
        time: 1315,
        text: 'Remember to adjust your feet. A good volley starts with good footwork.',
      },
      {
        time: 1321,
        text: "Let's see a powerful half-volley now. Let it bounce, then strike!",
      },
      {
        time: 1327,
        text: 'Good. Now a quick volley. No bounce!',
      },
      {
        time: 1332,
        text: "You're thinking and reacting so fast. This is excellent.",
      },
      {
        time: 1337,
        text: "Keep that focus. We're in the final stretch of this drill.",
      },
      {
        time: 1342,
        text: "Let's see one more good rally. How many can you get?",
      },
      {
        time: 1347,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1351,
        text: "Last minute of this drill. Let's see your best passes yet.",
      },
      {
        time: 1356,
        text: 'Thirty seconds left! Empty the tank. Quick feet, quick passes!',
      },
      {
        time: 1401,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1406,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1411,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 1416,
        text: 'That was some high-speed work! Great job!',
      },
      {
        time: 1419,
        text: "This is our last drill! The 'Ultimate Control Combination'. Let's combine everything we've learned. Start with the ball.",
      },
      {
        time: 1416,
        text: "Last drill! The 'Ultimate Control Combination'. Let's combine everything we've learned. Start with the ball.",
      },
      {
        time: 1423,
        text: "Let's start with a De Bruyne Double Pass. Remember, that's a powerful driven pass to the wall, a soft touch to control the rebound, and another powerful pass back! Go!",
      },
      {
        time: 1433,
        text: "Good. Now, control the rebound and go right into a Zidane Roulette. That's the foot on the ball, a full 360-degree spin, and pull the ball away with your other foot!",
      },
      {
        time: 1443,
        text: "Excellent! Now, from that move, dribble and let's see a Neymar No-Look control from the wall.",
      },
      {
        time: 1450,
        text: 'Remember, pass to the wall, look completely away, and control the rebound with a soft touch without seeing it. Trust your feet!',
      },
      {
        time: 1459,
        text: "Pass, look away, and a soft touch. You're linking the moves together like a pro!",
      },
      {
        time: 1466,
        text: "Now, let's see a volley off the wall. A sharp, clean strike.",
      },
      {
        time: 1472,
        text: "Control the volley, and go right into the Wall Reaction Gauntlet. Pass and I'll call it... THIGH!",
      },
      {
        time: 1479,
        text: 'Good. Pass it again... CHEST!',
      },
      {
        time: 1483,
        text: "Awesome! You're using all your skills. This is the ultimate test.",
      },
      {
        time: 1489,
        text: "Let's try another combination. Your choice this time. Start with any move we've learned.",
      },
      {
        time: 1496,
        text: 'Now link it to a second move. Make it smooth.',
      },
      {
        time: 1501,
        text: 'Now a third! Can you link three moves together?',
      },
      {
        time: 1506,
        text: "Excellent! That's what the best players in the world do. They don't just do one trick; they flow from one to the next.",
      },
      {
        time: 1514,
        text: "Two minutes in! Let's keep that intensity high. Your brain and your feet are working together.",
      },
      {
        time: 1521,
        text: "Let's see a powerful De Bruyne pass... into a soft no-look control.",
      },
      {
        time: 1528,
        text: "That's a great combination of power and deception. Keep it up.",
      },
      {
        time: 1534,
        text: 'Now a Zidane Roulette... right into a volley off the wall.',
      },
      {
        time: 1540,
        text: "Remember, that's the full spin with the ball, then a clean volley!",
      },
      {
        time: 1546,
        text: "This is your time to be creative. Show me what you've learned.",
      },
      {
        time: 1551,
        text: "Halfway! Keep that quality high, even when you're getting tired.",
      },
      {
        time: 1557,
        text: "Let's challenge your weak foot. A De Bruyne double pass with your other foot.",
      },
      {
        time: 1564,
        text: 'Good effort! Now a Zidane Roulette starting with your weak foot.',
      },
      {
        time: 1570,
        text: "The more you practice this, the more unstoppable you'll be.",
      },
      {
        time: 1576,
        text: 'Back to your strong foot. Show me your favorite combination of the day.',
      },
      {
        time: 1582,
        text: 'Make it sharp, make it fast, make it clean.',
      },
      {
        time: 1587,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1592,
        text: 'Prepare for the final minute. This is the last push.',
      },
      {
        time: 1597,
        text: 'Last minute! Empty the tank! Show me your most creative, most amazing combination of the day.',
      },
      {
        time: 1605,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1610,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1616,
        text: "Thirty seconds! Let's see a masterpiece!",
      },
      {
        time: 1621,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1626,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1631,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1636,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1646,
        text: 'Fantastic work today! You put it all together: powerful passes, deceptive touches, and incredible combinations. You trained like a champion!',
      },
      {
        time: 1656,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths. You've earned it. Great job today!",
      },
    ],
    timeTotal: 990,
    equipment: 'Soccer ball, wall',
  },
  {
    id: 2020,
    name: 'Ultimate Solo Mastery',
    categories: ['Ball Control', 'Passing', 'Wall Kicking', 'advanced'],
    sports: ['soccer'],
    difficulty: 'advanced',
    skill: 'Everyday Solo Drill',
    points: 90,
    instructions:
      'This is a high-repetition, continuous-flow drill designed for everyday practice. The goal is to move seamlessly from one skill to the next, using the wall as a central partner for passing, control, and volleys.',
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstar, ready to train like a champion? Let's kick things off with a drill I call 'Messi's Maze & Burst'! For the next five minutes, we're going to be moving. Let's go!",
      },
      {
        time: 9,
        text: "Imagine a line right in front of you. Let's start by dribbling side-to-side across that line with tiny, quick touches. Like your feet are dancing!",
      },
      {
        time: 18,
        text: "Use the inside and outside of your feet. Inside, outside, inside, outside. Keep the ball so close it's like it's glued to your shoe, just like Messi!",
      },
      {
        time: 28,
        text: "That's it! Fast feet, fast feet! Now, after a few of those quick touches, I want you to EXPLODE forward in a sprint for three big steps!",
      },
      {
        time: 37,
        text: "So it's: Quick maze dribble... then BURST! Let's see it. Dribble, dribble, dribble... and GO!",
      },
      {
        time: 45,
        text: 'Awesome! Now bring it back and do it again. The change from slow to fast is our secret weapon.',
      },
      {
        time: 52,
        text: 'Keep those touches in the maze super tight. Your head should be up, looking for that space to burst into.',
      },
      {
        time: 60,
        text: "One minute in! You're looking sharp. Let's focus on that explosion. The first touch out of your maze should be bigger and into space.",
      },
      {
        time: 69,
        text: "Let's see that powerful first touch. Quick, quick, quick... then PUSH and sprint!",
      },
      {
        time: 76,
        text: "Excellent! Now let's try the maze with your other foot. It might feel a bit funny, but this is how we get better.",
      },
      {
        time: 84,
        text: 'Good. Now explode out of that weak-foot maze. You can do it!',
      },
      {
        time: 90,
        text: "Back to your favorite foot. Let's see that perfect combination. The maze, then the burst.",
      },
      {
        time: 97,
        text: 'Remember to stay low with your knees bent. It helps you explode faster.',
      },
      {
        time: 103,
        text: "Let's see it again. Tight dribble... and GO! Leave that imaginary defender behind.",
      },
      {
        time: 110,
        text: "You're doing great. Keep that energy high.",
      },
      {
        time: 115,
        text: "Let's see another one. Make it your fastest burst yet.",
      },
      {
        time: 120,
        text: 'Two minutes in! Your change of pace is looking amazing.',
      },
      {
        time: 126,
        text: "Let's add a little trick to the maze. Try a sole roll across the line, then another one back, then explode.",
      },
      {
        time: 135,
        text: "So it's: Roll right, roll left... then BURST! This keeps the defender guessing.",
      },
      {
        time: 142,
        text: 'Awesome! Now back to the inside-outside touches for the maze. Keep it sharp.',
      },
      {
        time: 148,
        text: 'And... BURST! Good explosion.',
      },
      {
        time: 153,
        text: "Halfway! Let's focus on your body fake. Before you burst, dip your shoulder like you're going to keep dribbling sideways.",
      },
      {
        time: 162,
        text: 'A good fake will freeze the defender. Dribble, dip your shoulder, and then GO!',
      },
      {
        time: 170,
        text: "That's it! The fake makes your burst even more powerful.",
      },
      {
        time: 176,
        text: "Let's see three good fakes in a row. Dribble, fake, burst!",
      },
      {
        time: 182,
        text: "You're looking like a real trickster out there. Keep it up.",
      },
      {
        time: 188,
        text: "Let's see that weak foot again. A quick maze and a powerful burst.",
      },
      {
        time: 195,
        text: "Excellent! You're building that confidence.",
      },
      {
        time: 200,
        text: 'Now back to your strong foot. Show me that perfect maze and burst.',
      },
      {
        time: 207,
        text: 'Keep pushing yourself. Every sprint makes you faster.',
      },
      {
        time: 213,
        text: "Let's see another powerful explosion. Leave everyone behind!",
      },
      {
        time: 219,
        text: 'Good. Now back to the controlled dribble. Master both speeds.',
      },
      {
        time: 224,
        text: "You're doing great. Keep that focus all the way to the end.",
      },
      {
        time: 230,
        text: "Let's see another sole roll and explode. Make it tricky.",
      },
      {
        time: 236,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 240,
        text: 'Last minute! Your choice now. Freestyle! Mix the inside-outside dribbles with the sole rolls. But always explode out!',
      },
      {
        time: 249,
        text: 'Be creative. Show me your best combination of quick feet and explosive speed.',
      },
      {
        time: 255,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 261,
        text: 'Faster, sharper, more powerful!',
      },
      {
        time: 266,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 271,
        text: 'Final fifteen seconds. Give me your best sprint yet.',
      },
      {
        time: 276,
        text: "Let's see that power!",
      },
      {
        time: 281,
        text: 'Almost there, keep working!',
      },
      {
        time: 286,
        text: 'Five, four, three, two, one... and time! Excellent work.',
      },
      {
        time: 291,
        text: 'That was a tough first exercise and you did great! Take a quick breather.',
      },
      {
        time: 296,
        text: "Next up, we're going to practice our touch like a superstar with 'Ronaldo's Laces Rally'!",
      },
      {
        time: 299,
        text: "This is all about juggling, but we're only using the top of our feet, right on the shoelaces. Let's see how many you can get. Go!",
      },
      {
        time: 307,
        text: 'Tap with your laces, tap with your laces! The trick is to point your toes down and keep your ankle locked. It gives you a flat surface.',
      },
      {
        time: 316,
        text: "Let's try to get five in a row with your right foot. Soft, little taps. Don't kick it too high!",
      },
      {
        time: 323,
        text: "Good! Now let's try five with your left foot. This is how you build two superstar feet.",
      },
      {
        time: 330,
        text: "Awesome! Now, let's try to alternate. Right foot, left foot, right foot, left foot. Find a rhythm.",
      },
      {
        time: 338,
        text: 'Keep your eyes on the ball and your body balanced. Use your arms to help you stay steady.',
      },
      {
        time: 345,
        text: "This is tough, but it's how you get a perfect touch. Let's see a new record. Can you get to ten?",
      },
      {
        time: 352,
        text: 'If it drops, no worries! The best players in the world drop it too. Just pick it up and start again.',
      },
      {
        time: 360,
        text: 'One minute in! Your touch is looking cleaner already. Keep that focus.',
      },
      {
        time: 366,
        text: "Let's try to keep the ball below your waist. That's a sign of amazing control.",
      },
      {
        time: 373,
        text: "The lower you keep it, the more control you have. Let's see those low taps.",
      },
      {
        time: 380,
        text: 'Excellent! Now back to your normal juggle. Feels a bit easier, right?',
      },
      {
        time: 386,
        text: "Let's see another record attempt. Go for it! Count them out loud.",
      },
      {
        time: 392,
        text: 'Come on, keep it alive! Soft touches, quick feet.',
      },
      {
        time: 398,
        text: "You're doing great. This is hard work, but it makes you a much better player.",
      },
      {
        time: 404,
        text: "Let's see another good streak. How many can you get this time?",
      },
      {
        time: 411,
        text: "Two minutes in! Let's add a little movement. While you're juggling, try to take one small step forward.",
      },
      {
        time: 419,
        text: 'Good! Now take a small step back. Keep the juggle going!',
      },
      {
        time: 425,
        text: 'This is how you learn to move and control the ball at the same time.',
      },
      {
        time: 431,
        text: "Let's go back to standing still. Let's go for a new personal best. Go!",
      },
      {
        time: 437,
        text: "Keep that ball dancing on your laces. You're in control.",
      },
      {
        time: 443,
        text: "Halfway! Let's challenge that weak foot. For the next 30 seconds, only use your other foot. Go!",
      },
      {
        time: 473,
        text: 'Awesome job! Now back to both feet. Keep that rally going.',
      },
      {
        time: 479,
        text: "Let's see another record attempt. Come on, push that number higher!",
      },
      {
        time: 485,
        text: "Keep that focus. You're in the zone.",
      },
      {
        time: 490,
        text: "Let's try the low juggles again. Below your knees! This is the pro level.",
      },
      {
        time: 497,
        text: 'Excellent control. Now back to normal height.',
      },
      {
        time: 502,
        text: "You're building an amazing touch. This is what makes great players so special.",
      },
      {
        time: 509,
        text: 'Keep your eyes on the ball. Watch it all the way onto your laces.',
      },
      {
        time: 515,
        text: "Let's see another good streak. Don't let it drop.",
      },
      {
        time: 520,
        text: 'Excellent focus. Prepare for the final minute.',
      },
      {
        time: 525,
        text: 'Get ready for one last push.',
      },
      {
        time: 530,
        text: "Last minute of this drill. Let's see your best juggling yet. Final record attempt!",
      },
      {
        time: 537,
        text: 'Push yourself. Can you get one more than last time?',
      },
      {
        time: 542,
        text: 'Thirty seconds left! Keep that ball dancing.',
      },
      {
        time: 547,
        text: "Don't let it drop! Come on, fight for it!",
      },
      {
        time: 552,
        text: 'Final push! How many can you get?',
      },
      {
        time: 557,
        text: 'This is where champions are made. Keep working.',
      },
      {
        time: 562,
        text: 'Come on, a few more seconds!',
      },
      {
        time: 567,
        text: "Don't stop now, almost there!",
      },
      {
        time: 572,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 577,
        text: 'Get ready for the countdown!',
      },
      {
        time: 582,
        text: 'Five, four, three, two, one... and catch! Great work!',
      },
      {
        time: 588,
        text: 'That was some superstar juggling! Take a quick breather.',
      },
      {
        time: 594,
        text: "It's time to use your whole body like the legendary Ronaldinho with 'Ronaldinho's Full Body Flow'!",
      },
      {
        time: 597,
        text: "The goal is to keep the ball in the air using your feet, your thighs, and your chest. Let's start with a simple combo. Toss the ball up, control it with your thigh, then tap it with your foot and catch. Go!",
      },
      {
        time: 609,
        text: 'Thigh, foot, catch! Thigh, foot, catch! The thigh touch has to be super soft to set up your foot.',
      },
      {
        time: 617,
        text: "Let's try it on your other leg. Left thigh, left foot, catch! You're a two-footed superstar.",
      },
      {
        time: 624,
        text: "Now, let's try a new combo. Toss it up, control it with your chest, and then tap it with your foot and catch.",
      },
      {
        time: 632,
        text: 'Remember to make a big chest and lean back a little. Let the ball bounce off you like a trampoline.',
      },
      {
        time: 640,
        text: "Chest, foot, catch! Chest, foot, catch! You're controlling the ball with your whole body.",
      },
      {
        time: 648,
        text: "One minute in! Let's try to link them together. Foot to thigh, back to foot. How many can you get?",
      },
      {
        time: 656,
        text: "Foot, thigh, foot, thigh... keep that rhythm going. It's like you're dancing with the ball.",
      },
      {
        time: 663,
        text: "Now let's try foot to chest and back to foot. This is tricky!",
      },
      {
        time: 669,
        text: 'The key is a soft touch every single time. Be a pillow for the ball.',
      },
      {
        time: 675,
        text: "You're doing great. This is how the best players in the world practice.",
      },
      {
        time: 681,
        text: "Let's see another good streak. Foot, thigh, foot, thigh. Go for a record!",
      },
      {
        time: 688,
        text: 'Excellent! Keep that focus. Watch the ball all the way.',
      },
      {
        time: 693,
        text: "Two minutes in! Let's get your head involved. Toss the ball up and try to get a gentle header back to your hands.",
      },
      {
        time: 701,
        text: 'Use your forehead, right on your hairline. A little pop, not a big header.',
      },
      {
        time: 708,
        text: 'Good! Now, can you do a header to your thigh, then control it with your foot? Header, thigh, foot, catch.',
      },
      {
        time: 717,
        text: "This is the ultimate test of control! You're using your whole body.",
      },
      {
        time: 723,
        text: "Don't worry if it's not perfect. Just trying this is making you a better player.",
      },
      {
        time: 730,
        text: "Let's go back to the foot-thigh combo. Make it perfect.",
      },
      {
        time: 736,
        text: 'Now the foot-chest combo. Make it just as good.',
      },
      {
        time: 742,
        text: "You're looking like a freestyle master. Keep it up.",
      },
      {
        time: 748,
        text: 'Halfway! Time to go for a new record. How many touches can you get in a row using your feet, thighs, and chest? No hands!',
      },
      {
        time: 758,
        text: "Count them out loud! Let's see if you can beat ten!",
      },
      {
        time: 763,
        text: 'Keep it alive! A soft touch is a good touch.',
      },
      {
        time: 768,
        text: 'If it drops, no problem. The record starts over. Go again!',
      },
      {
        time: 774,
        text: 'This is how you build an amazing first touch. Keep working.',
      },
      {
        time: 780,
        text: "Let's see a combo. Foot, thigh, chest, thigh, foot. Can you do it?",
      },
      {
        time: 787,
        text: "Awesome! That's a pro-level combination.",
      },
      {
        time: 792,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 798,
        text: "Let's see another record attempt. Come on, push that number!",
      },
      {
        time: 804,
        text: "You're in the zone. Keep that ball up.",
      },
      {
        time: 809,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 814,
        text: 'Get ready for one last push.',
      },
      {
        time: 818,
        text: 'Last minute of this drill. Show me your best all-around control. Freestyle time!',
      },
      {
        time: 825,
        text: 'Use your feet, thighs, chest, head, shoulders! Anything goes, just keep it off the ground.',
      },
      {
        time: 833,
        text: 'Thirty seconds left! Empty the tank. Keep it up!',
      },
      {
        time: 838,
        text: "Come on, a few more touches. Don't stop now.",
      },
      {
        time: 843,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 848,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 853,
        text: 'Five, four, three, two, one... and catch! Excellent!',
      },
      {
        time: 859,
        text: "That was some amazing full-body control! Take a quick rest as next up you will practice one of the most famous moves ever, 'The Maradona Spin'!.",
      },
      {
        time: 866,
        text: 'The Maradona Spin is a super cool 360-degree spin that protects the ball. Start by dribbling forward slowly.',
      },
      {
        time: 873,
        text: "Now, put the bottom of your right foot on the ball to stop it. As you do that, you're going to spin your body all the way around the ball.",
      },
      {
        time: 882,
        text: "As you complete the spin, use your other foot, your left foot, to pull the ball away and escape. It's a stop, a spin, and a pull!",
      },
      {
        time: 891,
        text: "Let's practice that motion. Foot on the ball, spin, pull! Foot on the ball, spin, pull!",
      },
      {
        time: 899,
        text: 'The key is a smooth, continuous motion, keeping the ball connected to you the whole time.',
      },
      {
        time: 906,
        text: "Now let's try it the other way. Put your left foot on the ball, spin the other way, and pull it away with your right.",
      },
      {
        time: 915,
        text: 'This move is pure magic! You shield the ball with your body as you spin.',
      },
      {
        time: 921,
        text: 'Keep your body low and balanced through the spin. It helps you stay in control.',
      },
      {
        time: 927,
        text: "Let's see five good spins to the right. Go.",
      },
      {
        time: 932,
        text: 'Now five to the left. Build that muscle memory.',
      },
      {
        time: 937,
        text: "You're looking like a magician out there. Keep it up.",
      },
      {
        time: 942,
        text: 'The pull with the second foot has to be quick and sharp.',
      },
      {
        time: 947,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 952,
        text: "Excellent. You're mastering the move.",
      },
      {
        time: 957,
        text: "Two minutes in! Let's try it after a few dribbling touches. Dribble, then Maradona.",
      },
      {
        time: 964,
        text: 'This makes it more game-like. Good.',
      },
      {
        time: 969,
        text: 'Keep practicing that sequence. Dribble, stop, spin, pull.',
      },
      {
        time: 975,
        text: "Let's see one with your weak foot initiating the spin.",
      },
      {
        time: 980,
        text: 'Excellent effort. Keep challenging yourself.',
      },
      {
        time: 985,
        text: "Your spins are looking fluid. Let's focus on the exit.",
      },
      {
        time: 990,
        text: 'After you pull the ball, your first touch away should be an explosion of speed.',
      },
      {
        time: 997,
        text: "So it's: Dribble, spin, pull, and accelerate! Go.",
      },
      {
        time: 1003,
        text: "That's it! Create that separation from the defender.",
      },
      {
        time: 1008,
        text: "Let's see three more with that explosive exit.",
      },
      {
        time: 1013,
        text: 'Keep the quality high. Every part of the move matters.',
      },
      {
        time: 1018,
        text: "You're doing great. This is a top-tier skill.",
      },
      {
        time: 1023,
        text: "Halfway! Let's see it at game speed now.",
      },
      {
        time: 1028,
        text: 'Dribble in with pace, execute a fast spin, and explode out.',
      },
      {
        time: 1034,
        text: "Don't let the ball get away from you during the spin. Keep it tight.",
      },
      {
        time: 1039,
        text: "Let's see another one at game speed. Come on, push the tempo.",
      },
      {
        time: 1044,
        text: "Excellent. You're looking sharp.",
      },
      {
        time: 1049,
        text: "Keep that focus. We're building muscle memory.",
      },
      {
        time: 1054,
        text: "Let's see one more perfect spin before the final minute.",
      },
      {
        time: 1059,
        text: 'Awesome. Prepare to empty the tank.',
      },
      {
        time: 1064,
        text: "Last minute of this drill. Let's see it at your absolute top speed.",
      },
      {
        time: 1069,
        text: 'Thirty seconds left! Empty the tank. Quick spins, fast exits!',
      },
      {
        time: 1074,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1079,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1084,
        text: 'Five, four, three, two, one... and time! Fantastic!',
      },
      {
        time: 1089,
        text: 'That was a dizzying display of skill! Get ready for the next one.',
      },
      {
        time: 1089,
        text: "No break! Let's get straight into some high-speed action with 'Neymar's Volley Challenge'! Time to work on your power and reactions.",
      },
      {
        time: 1098,
        text: "First, we'll practice the half-volley. Pass the ball hard against the wall. Let it bounce once, and then strike it back against the wall before it bounces again. Go!",
      },
      {
        time: 1109,
        text: "Pass, bounce, strike! Pass, bounce, strike! The key is to hit the ball right as it's coming up from the bounce.",
      },
      {
        time: 1117,
        text: "Keep your eye on the ball and get your body over it. This helps keep your shot low and powerful. Let's see it.",
      },
      {
        time: 1125,
        text: "Good! Now, let's try a full volley. Pass it to the wall, and this time, hit it back BEFORE it bounces. One touch!",
      },
      {
        time: 1134,
        text: 'Pass, volley! Pass, volley! Keep your ankle locked and your body balanced. This is all about timing.',
      },
      {
        time: 1142,
        text: "Let's alternate. One half-volley, then one full volley. This will really test your brain and your feet!",
      },
      {
        time: 1150,
        text: 'Half-volley... good. Now a full volley! Excellent reaction!',
      },
      {
        time: 1156,
        text: "One minute in! Your strikes are looking clean. Let's try to use both your laces for power and the inside of your foot for control.",
      },
      {
        time: 1205,
        text: "Let's see a power shot with your laces! Ping!",
      },
      {
        time: 1210,
        text: 'Now a controlled volley with the inside of your foot. Nice and smooth.',
      },
      {
        time: 1216,
        text: 'This is how you build quick feet and passing accuracy on a moving ball!',
      },
      {
        time: 1222,
        text: "Let's see a half-volley with your weak foot now. Challenge yourself!",
      },
      {
        time: 1228,
        text: 'Good effort! Now a full volley with your weak foot. You can do it!',
      },
      {
        time: 1234,
        text: 'The more you practice with that foot, the stronger it will get.',
      },
      {
        time: 1240,
        text: "Two minutes in! Let's see how many you can get in a row. Keep a rally going. Your choice: volley or half-volley.",
      },
      {
        time: 1248,
        text: "Count them out loud! Let's see if you can get to ten!",
      },
      {
        time: 1253,
        text: "Keep that rally alive! Don't let the ball stop.",
      },
      {
        time: 1258,
        text: 'This is a great way to improve your speed of play.',
      },
      {
        time: 1263,
        text: 'Keep that focus. Every pass matters.',
      },
      {
        time: 1268,
        text: 'If it drops, no worries, just start the count again. Go for a new record.',
      },
      {
        time: 1274,
        text: "You're doing great. Your reactions are getting faster.",
      },
      {
        time: 1279,
        text: "Halfway! Let's change the distance. Take two big steps closer to the wall. The ball will come back much faster!",
      },
      {
        time: 1288,
        text: "Let's see those quick one-touch volleys from up close. Fast feet, fast reactions!",
      },
      {
        time: 1295,
        text: 'Good! Now, take four big steps back from where you were. Far away now!',
      },
      {
        time: 1301,
        text: "From back here, you need power. Let's see a powerful half-volley. Strike it!",
      },
      {
        time: 1308,
        text: 'Excellent! Now move back in close. Quick volleys!',
      },
      {
        time: 1314,
        text: 'And back out again! Power half-volley!',
      },
      {
        time: 1320,
        text: "You're adapting to the distance perfectly. This is a top-level skill.",
      },
      {
        time: 1326,
        text: "Let's stay back here for a few more power shots. Really drive that ball.",
      },
      {
        time: 1332,
        text: 'Remember to get your body over the ball to keep it from flying up.',
      },
      {
        time: 1338,
        text: 'Awesome power! Prepare for the final minute.',
      },
      {
        time: 1343,
        text: 'Last minute of this drill. Your choice of distance. Show me your best volleys and half-volleys.',
      },
      {
        time: 1351,
        text: 'Mix it up! A few from close, a few from far. Be unpredictable.',
      },
      {
        time: 1357,
        text: 'Thirty seconds left! Empty the tank. Quick feet, clean strikes!',
      },
      {
        time: 1363,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1368,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1373,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 1379,
        text: 'That was some high-speed work! Get ready for the final exercise.',
      },
      {
        time: 1089,
        text: "Get amped beecause we are going into overdrive mode! Let's get straight into some high-speed action with 'Neymar's Volley Challenge'! Time to work on your power and reactions.",
      },
      {
        time: 1098,
        text: "First, we'll practice the half-volley. Pass the ball hard against the wall. Let it bounce once, and then strike it back against the wall before it bounces again. Go!",
      },
      {
        time: 1109,
        text: "Pass, bounce, strike! Pass, bounce, strike! The key is to hit the ball right as it's coming up from the bounce.",
      },
      {
        time: 1117,
        text: "Keep your eye on the ball and get your body over it. This helps keep your shot low and powerful. Let's see it.",
      },
      {
        time: 1125,
        text: "Good! Now, let's try a full volley. Pass it to the wall, and this time, hit it back BEFORE it bounces. One touch!",
      },
      {
        time: 1134,
        text: 'Pass, volley! Pass, volley! Keep your ankle locked and your body balanced. This is all about timing.',
      },
      {
        time: 1142,
        text: "Let's alternate. One half-volley, then one full volley. This will really test your brain and your feet!",
      },
      {
        time: 1150,
        text: 'Half-volley... good. Now a full volley! Excellent reaction!',
      },
      {
        time: 1156,
        text: "One minute in! Your strikes are looking clean. Let's try to use both your laces for power and the inside of your foot for control.",
      },
      {
        time: 1205,
        text: "Let's see a power shot with your laces! Ping!",
      },
      {
        time: 1210,
        text: 'Now a controlled volley with the inside of your foot. Nice and smooth.',
      },
      {
        time: 1216,
        text: 'This is how you build quick feet and passing accuracy on a moving ball!',
      },
      {
        time: 1222,
        text: "Let's see a half-volley with your weak foot now. Challenge yourself!",
      },
      {
        time: 1228,
        text: 'Good effort! Now a full volley with your weak foot. You can do it!',
      },
      {
        time: 1234,
        text: 'The more you practice with that foot, the stronger it will get.',
      },
      {
        time: 1240,
        text: "Two minutes in! Let's see how many you can get in a row. Keep a rally going. Your choice: volley or half-volley.",
      },
      {
        time: 1248,
        text: "Count them out loud! Let's see if you can get to ten!",
      },
      {
        time: 1253,
        text: "Keep that rally alive! Don't let the ball stop.",
      },
      {
        time: 1258,
        text: 'This is a great way to improve your speed of play.',
      },
      {
        time: 1263,
        text: 'Keep that focus. Every pass matters.',
      },
      {
        time: 1268,
        text: 'If it drops, no worries, just start the count again. Go for a new record.',
      },
      {
        time: 1274,
        text: "You're doing great. Your reactions are getting faster.",
      },
      {
        time: 1279,
        text: "Halfway! Let's change the distance. Take two big steps closer to the wall. The ball will come back much faster!",
      },
      {
        time: 1288,
        text: "Let's see those quick one-touch volleys from up close. Fast feet, fast reactions!",
      },
      {
        time: 1295,
        text: 'Good! Now, take four big steps back from where you were. Far away now!',
      },
      {
        time: 1301,
        text: "From back here, you need power. Let's see a powerful half-volley. Strike it!",
      },
      {
        time: 1308,
        text: 'Excellent! Now move back in close. Quick volleys!',
      },
      {
        time: 1314,
        text: 'And back out again! Power half-volley!',
      },
      {
        time: 1320,
        text: "You're adapting to the distance perfectly. This is a top-level skill.",
      },
      {
        time: 1326,
        text: "Let's stay back here for a few more power shots. Really drive that ball.",
      },
      {
        time: 1332,
        text: 'Remember to get your body over the ball to keep it from flying up.',
      },
      {
        time: 1338,
        text: 'Awesome power! Prepare for the final minute.',
      },
      {
        time: 1343,
        text: 'Last minute of this drill. Your choice of distance. Show me your best volleys and half-volleys.',
      },
      {
        time: 1351,
        text: 'Mix it up! A few from close, a few from far. Be unpredictable.',
      },
      {
        time: 1357,
        text: 'Thirty seconds left! Empty the tank. Quick feet, clean strikes!',
      },
      {
        time: 1363,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1368,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1373,
        text: "Five, four, three, two, one... and time! Awesome job... now let's get ready for the final drill!",
      },
      {
        time: 1379,
        text: "Let's combine everything we've learned and begin the 'Ultimate Control Combination'. Place the ball in front of you.",
      },
      {
        time: 1386,
        text: "We will perform a De Bruyne Double Pass. Remember, that's a powerful driven pass to the wall, a soft touch to control the rebound, and another powerful pass back! Go!",
      },
      {
        time: 1396,
        text: "Good. Now, control the rebound and go right into a Zidane Roulette. That's the foot on the ball, a full 360-degree spin, and pull the ball away with your other foot!",
      },
      {
        time: 1406,
        text: "Excellent! Now, from that move, dribble and let's see a Neymar No-Look control from the wall.",
      },
      {
        time: 1413,
        text: 'Remember, pass to the wall, look completely away, and control the rebound with a soft touch without seeing it. Trust your feet!',
      },
      {
        time: 1422,
        text: "Pass, look away, and a soft touch. You're linking the moves together like a pro!",
      },
      {
        time: 1429,
        text: "Now, let's see a volley off the wall. A sharp, clean strike.",
      },
      {
        time: 1435,
        text: "Control the volley, and go right into the Wall Reaction Gauntlet. Pass and I'll call it... THIGH!",
      },
      {
        time: 1442,
        text: 'Good. Pass it again... CHEST!',
      },
      {
        time: 1446,
        text: "Awesome! You're using all your skills. This is the ultimate test.",
      },
      {
        time: 1452,
        text: "Let's try another combination. Your choice this time. Start with any move we've learned.",
      },
      {
        time: 1459,
        text: 'Now link it to a second move. Make it smooth.',
      },
      {
        time: 1504,
        text: 'Now a third! Can you link three moves together?',
      },
      {
        time: 1509,
        text: "Excellent! That's what the best players in the world do. They don't just do one trick; they flow from one to the next.",
      },
      {
        time: 1517,
        text: "Two minutes in! Let's keep that intensity high. Your brain and your feet are working together.",
      },
      {
        time: 1524,
        text: "Let's see a powerful De Bruyne pass... into a soft no-look control.",
      },
      {
        time: 1531,
        text: "That's a great combination of power and deception. Keep it up.",
      },
      {
        time: 1537,
        text: 'Now a Zidane Roulette... right into a volley off the wall.',
      },
      {
        time: 1543,
        text: "Remember, that's the full spin with the ball, then a clean volley!",
      },
      {
        time: 1549,
        text: "This is your time to be creative. Show me what you've learned.",
      },
      {
        time: 1554,
        text: "Halfway! Keep that quality high, even when you're getting tired.",
      },
      {
        time: 1600,
        text: "Let's challenge your weak foot. A De Bruyne double pass with your other foot.",
      },
      {
        time: 1607,
        text: 'Good effort! Now a Zidane Roulette starting with your weak foot.',
      },
      {
        time: 1613,
        text: "The more you practice this, the more unstoppable you'll be.",
      },
      {
        time: 1619,
        text: 'Back to your strong foot. Show me your favorite combination of the day.',
      },
      {
        time: 1625,
        text: 'Make it sharp, make it fast, make it clean.',
      },
      {
        time: 1630,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1635,
        text: 'Prepare for the final minute. This is the last push.',
      },
      {
        time: 1640,
        text: 'Last minute! Empty the tank! Show me your most creative, most amazing combination of the day.',
      },
      {
        time: 1648,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1653,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1659,
        text: "Thirty seconds! Let's see a masterpiece!",
      },
      {
        time: 1664,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1669,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1674,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1679,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1689,
        text: 'Fantastic work today! You put it all together: powerful passes, deceptive touches, and incredible combinations. You trained like a champion!',
      },
      {
        time: 1699,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths. You've earned it. Great job today!",
      },
    ],
    timeTotal: 1285,
    equipment: 'Soccer ball, wall',
  },
  {
    id: 2021,
    name: 'The Ultimate Wall Master Challenge',
    categories: ['Ball Control', 'Wall Kicking', 'intermediate'],
    sports: ['soccer'],
    difficulty: 'intermediate',
    skill: 'Everyday Solo Drill',
    points: 90,
    instructions:
      'This is a high-repetition, continuous-flow drill designed for everyday practice. The goal is to move seamlessly from one skill to the next, using the wall as a central partner for passing, control, and volleys.',
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstar, ready to train like a champion? Let's kick things off with a drill I call 'Messi's Maze & Burst'! For the next five minutes, we're going to be moving. Let's go!",
      },
      {
        time: 9,
        text: "Imagine a line right in front of you. Let's start by dribbling side-to-side across that line with tiny, quick touches. Like your feet are dancing!",
      },
      {
        time: 18,
        text: "Use the inside and outside of your feet. Inside, outside, inside, outside. Keep the ball so close it's like it's glued to your shoe, just like Messi!",
      },
      {
        time: 28,
        text: "That's it! Fast feet, fast feet! Now, after a few of those quick touches, I want you to EXPLODE forward in a sprint for three big steps!",
      },
      {
        time: 37,
        text: "So it's: Quick maze dribble... then BURST! Let's see it. Dribble, dribble, dribble... and GO!",
      },
      {
        time: 45,
        text: 'Awesome! Now bring it back and do it again. The change from slow to fast is our secret weapon.',
      },
      {
        time: 52,
        text: 'Keep those touches in the maze super tight. Your head should be up, looking for that space to burst into.',
      },
      {
        time: 60,
        text: "One minute in! You're looking sharp. Let's focus on that explosion. The first touch out of your maze should be bigger and into space.",
      },
      {
        time: 69,
        text: "Let's see that powerful first touch. Quick, quick, quick... then PUSH and sprint!",
      },
      {
        time: 76,
        text: "Excellent! Now let's try the maze with your other foot. It might feel a bit funny, but this is how we get better.",
      },
      {
        time: 84,
        text: 'Good. Now explode out of that weak-foot maze. You can do it!',
      },
      {
        time: 90,
        text: "Back to your favorite foot. Let's see that perfect combination. The maze, then the burst.",
      },
      {
        time: 97,
        text: 'Remember to stay low with your knees bent. It helps you explode faster.',
      },
      {
        time: 103,
        text: "Let's see it again. Tight dribble... and GO! Leave that imaginary defender behind.",
      },
      {
        time: 110,
        text: "You're doing great. Keep that energy high.",
      },
      {
        time: 115,
        text: "Let's see another one. Make it your fastest burst yet.",
      },
      {
        time: 120,
        text: 'Two minutes in! Your change of pace is looking amazing.',
      },
      {
        time: 126,
        text: "Let's add a little trick to the maze. Try a sole roll across the line, then another one back, then explode.",
      },
      {
        time: 135,
        text: "So it's: Roll right, roll left... then BURST! This keeps the defender guessing.",
      },
      {
        time: 142,
        text: 'Awesome! Now back to the inside-outside touches for the maze. Keep it sharp.',
      },
      {
        time: 148,
        text: 'And... BURST! Good explosion.',
      },
      {
        time: 153,
        text: "Halfway! Let's focus on your body fake. Before you burst, dip your shoulder like you're going to keep dribbling sideways.",
      },
      {
        time: 162,
        text: 'A good fake will freeze the defender. Dribble, dip your shoulder, and then GO!',
      },
      {
        time: 170,
        text: "That's it! The fake makes your burst even more powerful.",
      },
      {
        time: 176,
        text: "Let's see three good fakes in a row. Dribble, fake, burst!",
      },
      {
        time: 182,
        text: "You're looking like a real trickster out there. Keep it up.",
      },
      {
        time: 188,
        text: "Let's see that weak foot again. A quick maze and a powerful burst.",
      },
      {
        time: 195,
        text: "Excellent! You're building that confidence.",
      },
      {
        time: 200,
        text: 'Now back to your strong foot. Show me that perfect maze and burst.',
      },
      {
        time: 207,
        text: 'Keep pushing yourself. Every sprint makes you faster.',
      },
      {
        time: 213,
        text: "Let's see another powerful explosion. Leave everyone behind!",
      },
      {
        time: 219,
        text: 'Good. Now back to the controlled dribble. Master both speeds.',
      },
      {
        time: 224,
        text: "You're doing great. Keep that focus all the way to the end.",
      },
      {
        time: 230,
        text: "Let's see another sole roll and explode. Make it tricky.",
      },
      {
        time: 236,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 240,
        text: 'Last minute! Your choice now. Freestyle! Mix the inside-outside dribbles with the sole rolls. But always explode out!',
      },
      {
        time: 249,
        text: 'Be creative. Show me your best combination of quick feet and explosive speed.',
      },
      {
        time: 255,
        text: 'Thirty seconds left! Empty the tank on this drill. Go, go, go!',
      },
      {
        time: 261,
        text: 'Faster, sharper, more powerful!',
      },
      {
        time: 266,
        text: "Come on, don't stop now. Push through it.",
      },
      {
        time: 271,
        text: 'Final fifteen seconds. Give me your best sprint yet.',
      },
      {
        time: 276,
        text: "Let's see that power!",
      },
      {
        time: 281,
        text: 'Almost there, keep working!',
      },
      {
        time: 286,
        text: 'Five, four, three, two, one... and time! Excellent work.',
      },
      {
        time: 291,
        text: 'That was a tough first exercise and you did great! Take a quick breather.',
      },
      {
        time: 296,
        text: "Next up, we're going to practice our touch like a superstar with 'Ronaldo's Laces Rally'!",
      },
      {
        time: 299,
        text: "This is all about juggling, but we're only using the top of our feet, right on the shoelaces. Let's see how many you can get. Go!",
      },
      {
        time: 307,
        text: 'Tap with your laces, tap with your laces! The trick is to point your toes down and keep your ankle locked. It gives you a flat surface.',
      },
      {
        time: 316,
        text: "Let's try to get five in a row with your right foot. Soft, little taps. Don't kick it too high!",
      },
      {
        time: 323,
        text: "Good! Now let's try five with your left foot. This is how you build two superstar feet.",
      },
      {
        time: 330,
        text: "Awesome! Now, let's try to alternate. Right foot, left foot, right foot, left foot. Find a rhythm.",
      },
      {
        time: 338,
        text: 'Keep your eyes on the ball and your body balanced. Use your arms to help you stay steady.',
      },
      {
        time: 345,
        text: "This is tough, but it's how you get a perfect touch. Let's see a new record. Can you get to ten?",
      },
      {
        time: 352,
        text: 'If it drops, no worries! The best players in the world drop it too. Just pick it up and start again.',
      },
      {
        time: 360,
        text: 'One minute in! Your touch is looking cleaner already. Keep that focus.',
      },
      {
        time: 366,
        text: "Let's try to keep the ball below your waist. That's a sign of amazing control.",
      },
      {
        time: 373,
        text: "The lower you keep it, the more control you have. Let's see those low taps.",
      },
      {
        time: 380,
        text: 'Excellent! Now back to your normal juggle. Feels a bit easier, right?',
      },
      {
        time: 386,
        text: "Let's see another record attempt. Go for it! Count them out loud.",
      },
      {
        time: 392,
        text: 'Come on, keep it alive! Soft touches, quick feet.',
      },
      {
        time: 398,
        text: "You're doing great. This is hard work, but it makes you a much better player.",
      },
      {
        time: 404,
        text: "Let's see another good streak. How many can you get this time?",
      },
      {
        time: 411,
        text: "Two minutes in! Let's add a little movement. While you're juggling, try to take one small step forward.",
      },
      {
        time: 419,
        text: 'Good! Now take a small step back. Keep the juggle going!',
      },
      {
        time: 425,
        text: 'This is how you learn to move and control the ball at the same time.',
      },
      {
        time: 431,
        text: "Let's go back to standing still. Let's go for a new personal best. Go!",
      },
      {
        time: 437,
        text: "Keep that ball dancing on your laces. You're in control.",
      },
      {
        time: 443,
        text: "Halfway! Let's challenge that weak foot. For the next 30 seconds, only use your other foot. Go!",
      },
      {
        time: 473,
        text: 'Awesome job! Now back to both feet. Keep that rally going.',
      },
      {
        time: 479,
        text: "Let's see another record attempt. Come on, push that number higher!",
      },
      {
        time: 485,
        text: "Keep that focus. You're in the zone.",
      },
      {
        time: 490,
        text: "Let's try the low juggles again. Below your knees! This is the pro level.",
      },
      {
        time: 497,
        text: 'Excellent control. Now back to normal height.',
      },
      {
        time: 502,
        text: "You're building an amazing touch. This is what makes great players so special.",
      },
      {
        time: 509,
        text: 'Keep your eyes on the ball. Watch it all the way onto your laces.',
      },
      {
        time: 515,
        text: "Let's see another good streak. Don't let it drop.",
      },
      {
        time: 520,
        text: 'Excellent focus. Prepare for the final minute.',
      },
      {
        time: 525,
        text: 'Get ready for one last push.',
      },
      {
        time: 530,
        text: "Last minute of this drill. Let's see your best juggling yet. Final record attempt!",
      },
      {
        time: 537,
        text: 'Push yourself. Can you get one more than last time?',
      },
      {
        time: 542,
        text: 'Thirty seconds left! Keep that ball dancing.',
      },
      {
        time: 547,
        text: "Don't let it drop! Come on, fight for it!",
      },
      {
        time: 552,
        text: 'Final push! How many can you get?',
      },
      {
        time: 557,
        text: 'This is where champions are made. Keep working.',
      },
      {
        time: 562,
        text: 'Come on, a few more seconds!',
      },
      {
        time: 567,
        text: "Don't stop now, almost there!",
      },
      {
        time: 572,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 577,
        text: 'Get ready for the countdown!',
      },
      {
        time: 582,
        text: 'Five, four, three, two, one... and catch! Great work!',
      },
      {
        time: 588,
        text: 'That was some superstar juggling! Take a quick breather.',
      },
      {
        time: 594,
        text: "It's time to use your whole body like the legendary Ronaldinho with 'Ronaldinho's Full Body Flow'!",
      },
      {
        time: 597,
        text: "The goal is to keep the ball in the air using your feet, your thighs, and your chest. Let's start with a simple combo. Toss the ball up, control it with your thigh, then tap it with your foot and catch. Go!",
      },
      {
        time: 609,
        text: 'Thigh, foot, catch! Thigh, foot, catch! The thigh touch has to be super soft to set up your foot.',
      },
      {
        time: 617,
        text: "Let's try it on your other leg. Left thigh, left foot, catch! You're a two-footed superstar.",
      },
      {
        time: 624,
        text: "Now, let's try a new combo. Toss it up, control it with your chest, and then tap it with your foot and catch.",
      },
      {
        time: 632,
        text: 'Remember to make a big chest and lean back a little. Let the ball bounce off you like a trampoline.',
      },
      {
        time: 640,
        text: "Chest, foot, catch! Chest, foot, catch! You're controlling the ball with your whole body.",
      },
      {
        time: 648,
        text: "One minute in! Let's try to link them together. Foot to thigh, back to foot. How many can you get?",
      },
      {
        time: 656,
        text: "Foot, thigh, foot, thigh... keep that rhythm going. It's like you're dancing with the ball.",
      },
      {
        time: 663,
        text: "Now let's try foot to chest and back to foot. This is tricky!",
      },
      {
        time: 669,
        text: 'The key is a soft touch every single time. Be a pillow for the ball.',
      },
      {
        time: 675,
        text: "You're doing great. This is how the best players in the world practice.",
      },
      {
        time: 681,
        text: "Let's see another good streak. Foot, thigh, foot, thigh. Go for a record!",
      },
      {
        time: 688,
        text: 'Excellent! Keep that focus. Watch the ball all the way.',
      },
      {
        time: 693,
        text: "Two minutes in! Let's get your head involved. Toss the ball up and try to get a gentle header back to your hands.",
      },
      {
        time: 701,
        text: 'Use your forehead, right on your hairline. A little pop, not a big header.',
      },
      {
        time: 708,
        text: 'Good! Now, can you do a header to your thigh, then control it with your foot? Header, thigh, foot, catch.',
      },
      {
        time: 717,
        text: "This is the ultimate test of control! You're using your whole body.",
      },
      {
        time: 723,
        text: "Don't worry if it's not perfect. Just trying this is making you a better player.",
      },
      {
        time: 730,
        text: "Let's go back to the foot-thigh combo. Make it perfect.",
      },
      {
        time: 736,
        text: 'Now the foot-chest combo. Make it just as good.',
      },
      {
        time: 742,
        text: "You're looking like a freestyle master. Keep it up.",
      },
      {
        time: 748,
        text: 'Halfway! Time to go for a new record. How many touches can you get in a row using your feet, thighs, and chest? No hands!',
      },
      {
        time: 758,
        text: "Count them out loud! Let's see if you can beat ten!",
      },
      {
        time: 763,
        text: 'Keep it alive! A soft touch is a good touch.',
      },
      {
        time: 768,
        text: 'If it drops, no problem. The record starts over. Go again!',
      },
      {
        time: 774,
        text: 'This is how you build an amazing first touch. Keep working.',
      },
      {
        time: 780,
        text: "Let's see a combo. Foot, thigh, chest, thigh, foot. Can you do it?",
      },
      {
        time: 787,
        text: "Awesome! That's a pro-level combination.",
      },
      {
        time: 792,
        text: 'Keep that focus. Every touch is a chance to get better.',
      },
      {
        time: 798,
        text: "Let's see another record attempt. Come on, push that number!",
      },
      {
        time: 804,
        text: "You're in the zone. Keep that ball up.",
      },
      {
        time: 809,
        text: 'Excellent work. Prepare for the final minute.',
      },
      {
        time: 814,
        text: 'Get ready for one last push.',
      },
      {
        time: 818,
        text: 'Last minute of this drill. Show me your best all-around control. Freestyle time!',
      },
      {
        time: 825,
        text: 'Use your feet, thighs, chest, head, shoulders! Anything goes, just keep it off the ground.',
      },
      {
        time: 833,
        text: 'Thirty seconds left! Empty the tank. Keep it up!',
      },
      {
        time: 838,
        text: "Come on, a few more touches. Don't stop now.",
      },
      {
        time: 843,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 848,
        text: 'Keep it up, keep it up!',
      },
      {
        time: 853,
        text: 'Five, four, three, two, one... and catch! Excellent!',
      },
      {
        time: 859,
        text: "That was some amazing full-body control! Take a quick rest as next up you will practice one of the most famous moves ever, 'The Maradona Spin'!.",
      },
      {
        time: 866,
        text: 'The Maradona Spin is a super cool 360-degree spin that protects the ball. Start by dribbling forward slowly.',
      },
      {
        time: 873,
        text: "Now, put the bottom of your right foot on the ball to stop it. As you do that, you're going to spin your body all the way around the ball.",
      },
      {
        time: 882,
        text: "As you complete the spin, use your other foot, your left foot, to pull the ball away and escape. It's a stop, a spin, and a pull!",
      },
      {
        time: 891,
        text: "Let's practice that motion. Foot on the ball, spin, pull! Foot on the ball, spin, pull!",
      },
      {
        time: 899,
        text: 'The key is a smooth, continuous motion, keeping the ball connected to you the whole time.',
      },
      {
        time: 906,
        text: "Now let's try it the other way. Put your left foot on the ball, spin the other way, and pull it away with your right.",
      },
      {
        time: 915,
        text: 'This move is pure magic! You shield the ball with your body as you spin.',
      },
      {
        time: 921,
        text: 'Keep your body low and balanced through the spin. It helps you stay in control.',
      },
      {
        time: 927,
        text: "Let's see five good spins to the right. Go.",
      },
      {
        time: 932,
        text: 'Now five to the left. Build that muscle memory.',
      },
      {
        time: 937,
        text: "You're looking like a magician out there. Keep it up.",
      },
      {
        time: 942,
        text: 'The pull with the second foot has to be quick and sharp.',
      },
      {
        time: 947,
        text: "Let's see another one. Make it your smoothest one yet.",
      },
      {
        time: 952,
        text: "Excellent. You're mastering the move.",
      },
      {
        time: 957,
        text: "Two minutes in! Let's try it after a few dribbling touches. Dribble, then Maradona.",
      },
      {
        time: 964,
        text: 'This makes it more game-like. Good.',
      },
      {
        time: 969,
        text: 'Keep practicing that sequence. Dribble, stop, spin, pull.',
      },
      {
        time: 975,
        text: "Let's see one with your weak foot initiating the spin.",
      },
      {
        time: 980,
        text: 'Excellent effort. Keep challenging yourself.',
      },
      {
        time: 985,
        text: "Your spins are looking fluid. Let's focus on the exit.",
      },
      {
        time: 990,
        text: 'After you pull the ball, your first touch away should be an explosion of speed.',
      },
      {
        time: 997,
        text: "So it's: Dribble, spin, pull, and accelerate! Go.",
      },
      {
        time: 1003,
        text: "That's it! Create that separation from the defender.",
      },
      {
        time: 1008,
        text: "Let's see three more with that explosive exit.",
      },
      {
        time: 1013,
        text: 'Keep the quality high. Every part of the move matters.',
      },
      {
        time: 1018,
        text: "You're doing great. This is a top-tier skill.",
      },
      {
        time: 1023,
        text: "Halfway! Let's see it at game speed now.",
      },
      {
        time: 1028,
        text: 'Dribble in with pace, execute a fast spin, and explode out.',
      },
      {
        time: 1034,
        text: "Don't let the ball get away from you during the spin. Keep it tight.",
      },
      {
        time: 1039,
        text: "Let's see another one at game speed. Come on, push the tempo.",
      },
      {
        time: 1044,
        text: "Excellent. You're looking sharp.",
      },
      {
        time: 1049,
        text: "Keep that focus. We're building muscle memory.",
      },
      {
        time: 1054,
        text: "Let's see one more perfect spin before the final minute.",
      },
      {
        time: 1059,
        text: 'Awesome. Prepare to empty the tank.',
      },
      {
        time: 1064,
        text: "Last minute of this drill. Let's see it at your absolute top speed.",
      },
      {
        time: 1069,
        text: 'Thirty seconds left! Empty the tank. Quick spins, fast exits!',
      },
      {
        time: 1074,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1079,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1084,
        text: 'Five, four, three, two, one... and time! Fantastic!',
      },
      {
        time: 1089,
        text: 'That was a dizzying display of skill! Get ready for the next one.',
      },
      {
        time: 1089,
        text: "No break! Let's get straight into some high-speed action with 'Neymar's Volley Challenge'! Time to work on your power and reactions.",
      },
      {
        time: 1098,
        text: "First, we'll practice the half-volley. Pass the ball hard against the wall. Let it bounce once, and then strike it back against the wall before it bounces again. Go!",
      },
      {
        time: 1109,
        text: "Pass, bounce, strike! Pass, bounce, strike! The key is to hit the ball right as it's coming up from the bounce.",
      },
      {
        time: 1117,
        text: "Keep your eye on the ball and get your body over it. This helps keep your shot low and powerful. Let's see it.",
      },
      {
        time: 1125,
        text: "Good! Now, let's try a full volley. Pass it to the wall, and this time, hit it back BEFORE it bounces. One touch!",
      },
      {
        time: 1134,
        text: 'Pass, volley! Pass, volley! Keep your ankle locked and your body balanced. This is all about timing.',
      },
      {
        time: 1142,
        text: "Let's alternate. One half-volley, then one full volley. This will really test your brain and your feet!",
      },
      {
        time: 1150,
        text: 'Half-volley... good. Now a full volley! Excellent reaction!',
      },
      {
        time: 1156,
        text: "One minute in! Your strikes are looking clean. Let's try to use both your laces for power and the inside of your foot for control.",
      },
      {
        time: 1205,
        text: "Let's see a power shot with your laces! Ping!",
      },
      {
        time: 1210,
        text: 'Now a controlled volley with the inside of your foot. Nice and smooth.',
      },
      {
        time: 1216,
        text: 'This is how you build quick feet and passing accuracy on a moving ball!',
      },
      {
        time: 1222,
        text: "Let's see a half-volley with your weak foot now. Challenge yourself!",
      },
      {
        time: 1228,
        text: 'Good effort! Now a full volley with your weak foot. You can do it!',
      },
      {
        time: 1234,
        text: 'The more you practice with that foot, the stronger it will get.',
      },
      {
        time: 1240,
        text: "Two minutes in! Let's see how many you can get in a row. Keep a rally going. Your choice: volley or half-volley.",
      },
      {
        time: 1248,
        text: "Count them out loud! Let's see if you can get to ten!",
      },
      {
        time: 1253,
        text: "Keep that rally alive! Don't let the ball stop.",
      },
      {
        time: 1258,
        text: 'This is a great way to improve your speed of play.',
      },
      {
        time: 1263,
        text: 'Keep that focus. Every pass matters.',
      },
      {
        time: 1268,
        text: 'If it drops, no worries, just start the count again. Go for a new record.',
      },
      {
        time: 1274,
        text: "You're doing great. Your reactions are getting faster.",
      },
      {
        time: 1279,
        text: "Halfway! Let's change the distance. Take two big steps closer to the wall. The ball will come back much faster!",
      },
      {
        time: 1288,
        text: "Let's see those quick one-touch volleys from up close. Fast feet, fast reactions!",
      },
      {
        time: 1295,
        text: 'Good! Now, take four big steps back from where you were. Far away now!',
      },
      {
        time: 1301,
        text: "From back here, you need power. Let's see a powerful half-volley. Strike it!",
      },
      {
        time: 1308,
        text: 'Excellent! Now move back in close. Quick volleys!',
      },
      {
        time: 1314,
        text: 'And back out again! Power half-volley!',
      },
      {
        time: 1320,
        text: "You're adapting to the distance perfectly. This is a top-level skill.",
      },
      {
        time: 1326,
        text: "Let's stay back here for a few more power shots. Really drive that ball.",
      },
      {
        time: 1332,
        text: 'Remember to get your body over the ball to keep it from flying up.',
      },
      {
        time: 1338,
        text: 'Awesome power! Prepare for the final minute.',
      },
      {
        time: 1343,
        text: 'Last minute of this drill. Your choice of distance. Show me your best volleys and half-volleys.',
      },
      {
        time: 1351,
        text: 'Mix it up! A few from close, a few from far. Be unpredictable.',
      },
      {
        time: 1357,
        text: 'Thirty seconds left! Empty the tank. Quick feet, clean strikes!',
      },
      {
        time: 1363,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1368,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1373,
        text: 'Five, four, three, two, one... and time! Awesome job!',
      },
      {
        time: 1379,
        text: 'That was some high-speed work! Get ready for the final exercise.',
      },
      {
        time: 1089,
        text: "Get amped beecause we are going into overdrive mode! Let's get straight into some high-speed action with 'Neymar's Volley Challenge'! Time to work on your power and reactions.",
      },
      {
        time: 1098,
        text: "First, we'll practice the half-volley. Pass the ball hard against the wall. Let it bounce once, and then strike it back against the wall before it bounces again. Go!",
      },
      {
        time: 1109,
        text: "Pass, bounce, strike! Pass, bounce, strike! The key is to hit the ball right as it's coming up from the bounce.",
      },
      {
        time: 1117,
        text: "Keep your eye on the ball and get your body over it. This helps keep your shot low and powerful. Let's see it.",
      },
      {
        time: 1125,
        text: "Good! Now, let's try a full volley. Pass it to the wall, and this time, hit it back BEFORE it bounces. One touch!",
      },
      {
        time: 1134,
        text: 'Pass, volley! Pass, volley! Keep your ankle locked and your body balanced. This is all about timing.',
      },
      {
        time: 1142,
        text: "Let's alternate. One half-volley, then one full volley. This will really test your brain and your feet!",
      },
      {
        time: 1150,
        text: 'Half-volley... good. Now a full volley! Excellent reaction!',
      },
      {
        time: 1156,
        text: "One minute in! Your strikes are looking clean. Let's try to use both your laces for power and the inside of your foot for control.",
      },
      {
        time: 1205,
        text: "Let's see a power shot with your laces! Ping!",
      },
      {
        time: 1210,
        text: 'Now a controlled volley with the inside of your foot. Nice and smooth.',
      },
      {
        time: 1216,
        text: 'This is how you build quick feet and passing accuracy on a moving ball!',
      },
      {
        time: 1222,
        text: "Let's see a half-volley with your weak foot now. Challenge yourself!",
      },
      {
        time: 1228,
        text: 'Good effort! Now a full volley with your weak foot. You can do it!',
      },
      {
        time: 1234,
        text: 'The more you practice with that foot, the stronger it will get.',
      },
      {
        time: 1240,
        text: "Two minutes in! Let's see how many you can get in a row. Keep a rally going. Your choice: volley or half-volley.",
      },
      {
        time: 1248,
        text: "Count them out loud! Let's see if you can get to ten!",
      },
      {
        time: 1253,
        text: "Keep that rally alive! Don't let the ball stop.",
      },
      {
        time: 1258,
        text: 'This is a great way to improve your speed of play.',
      },
      {
        time: 1263,
        text: 'Keep that focus. Every pass matters.',
      },
      {
        time: 1268,
        text: 'If it drops, no worries, just start the count again. Go for a new record.',
      },
      {
        time: 1274,
        text: "You're doing great. Your reactions are getting faster.",
      },
      {
        time: 1279,
        text: "Halfway! Let's change the distance. Take two big steps closer to the wall. The ball will come back much faster!",
      },
      {
        time: 1288,
        text: "Let's see those quick one-touch volleys from up close. Fast feet, fast reactions!",
      },
      {
        time: 1295,
        text: 'Good! Now, take four big steps back from where you were. Far away now!',
      },
      {
        time: 1301,
        text: "From back here, you need power. Let's see a powerful half-volley. Strike it!",
      },
      {
        time: 1308,
        text: 'Excellent! Now move back in close. Quick volleys!',
      },
      {
        time: 1314,
        text: 'And back out again! Power half-volley!',
      },
      {
        time: 1320,
        text: "You're adapting to the distance perfectly. This is a top-level skill.",
      },
      {
        time: 1326,
        text: "Let's stay back here for a few more power shots. Really drive that ball.",
      },
      {
        time: 1332,
        text: 'Remember to get your body over the ball to keep it from flying up.',
      },
      {
        time: 1338,
        text: 'Awesome power! Prepare for the final minute.',
      },
      {
        time: 1343,
        text: 'Last minute of this drill. Your choice of distance. Show me your best volleys and half-volleys.',
      },
      {
        time: 1351,
        text: 'Mix it up! A few from close, a few from far. Be unpredictable.',
      },
      {
        time: 1357,
        text: 'Thirty seconds left! Empty the tank. Quick feet, clean strikes!',
      },
      {
        time: 1363,
        text: "Come on, a few more reps. Don't stop now.",
      },
      {
        time: 1368,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1373,
        text: "Five, four, three, two, one... and time! Awesome job... now let's get ready for the final drill!",
      },
      {
        time: 1379,
        text: "Let's combine everything we've learned and begin the 'Ultimate Control Combination'. Place the ball in front of you.",
      },
      {
        time: 1386,
        text: "We will perform a De Bruyne Double Pass. Remember, that's a powerful driven pass to the wall, a soft touch to control the rebound, and another powerful pass back! Go!",
      },
      {
        time: 1396,
        text: "Good. Now, control the rebound and go right into a Zidane Roulette. That's the foot on the ball, a full 360-degree spin, and pull the ball away with your other foot!",
      },
      {
        time: 1406,
        text: "Excellent! Now, from that move, dribble and let's see a Neymar No-Look control from the wall.",
      },
      {
        time: 1413,
        text: 'Remember, pass to the wall, look completely away, and control the rebound with a soft touch without seeing it. Trust your feet!',
      },
      {
        time: 1422,
        text: "Pass, look away, and a soft touch. You're linking the moves together like a pro!",
      },
      {
        time: 1429,
        text: "Now, let's see a volley off the wall. A sharp, clean strike.",
      },
      {
        time: 1435,
        text: "Control the volley, and go right into the Wall Reaction Gauntlet. Pass and I'll call it... THIGH!",
      },
      {
        time: 1442,
        text: 'Good. Pass it again... CHEST!',
      },
      {
        time: 1446,
        text: "Awesome! You're using all your skills. This is the ultimate test.",
      },
      {
        time: 1452,
        text: "Let's try another combination. Your choice this time. Start with any move we've learned.",
      },
      {
        time: 1459,
        text: 'Now link it to a second move. Make it smooth.',
      },
      {
        time: 1504,
        text: 'Now a third! Can you link three moves together?',
      },
      {
        time: 1509,
        text: "Excellent! That's what the best players in the world do. They don't just do one trick; they flow from one to the next.",
      },
      {
        time: 1517,
        text: "Two minutes in! Let's keep that intensity high. Your brain and your feet are working together.",
      },
      {
        time: 1524,
        text: "Let's see a powerful De Bruyne pass... into a soft no-look control.",
      },
      {
        time: 1531,
        text: "That's a great combination of power and deception. Keep it up.",
      },
      {
        time: 1537,
        text: 'Now a Zidane Roulette... right into a volley off the wall.',
      },
      {
        time: 1543,
        text: "Remember, that's the full spin with the ball, then a clean volley!",
      },
      {
        time: 1549,
        text: "This is your time to be creative. Show me what you've learned.",
      },
      {
        time: 1554,
        text: "Halfway! Keep that quality high, even when you're getting tired.",
      },
      {
        time: 1600,
        text: "Let's challenge your weak foot. A De Bruyne double pass with your other foot.",
      },
      {
        time: 1607,
        text: 'Good effort! Now a Zidane Roulette starting with your weak foot.',
      },
      {
        time: 1613,
        text: "The more you practice this, the more unstoppable you'll be.",
      },
      {
        time: 1619,
        text: 'Back to your strong foot. Show me your favorite combination of the day.',
      },
      {
        time: 1625,
        text: 'Make it sharp, make it fast, make it clean.',
      },
      {
        time: 1630,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1635,
        text: 'Prepare for the final minute. This is the last push.',
      },
      {
        time: 1640,
        text: 'Last minute! Empty the tank! Show me your most creative, most amazing combination of the day.',
      },
      {
        time: 1648,
        text: 'This is where it counts. Push through the fatigue.',
      },
      {
        time: 1653,
        text: "Let's see your fastest, cleanest moves. Game-winning stuff!",
      },
      {
        time: 1659,
        text: "Thirty seconds! Let's see a masterpiece!",
      },
      {
        time: 1664,
        text: 'Keep that intensity! Link the moves. Make it flow!',
      },
      {
        time: 1669,
        text: "Don't get sloppy now. Every touch matters, even when you're tired.",
      },
      {
        time: 1674,
        text: 'Final fifteen seconds! Show me your best combination!',
      },
      {
        time: 1679,
        text: "Ten, nine, eight, seven, six, five, four, three, two, one... AND YOU'VE DONE IT! DRILL COMPLETE!",
      },
      {
        time: 1689,
        text: 'Fantastic work today! You put it all together: powerful passes, deceptive touches, and incredible combinations. You trained like a champion!',
      },
      {
        time: 1699,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths. You've earned it. Great job today!",
      },
    ],
    timeTotal: 1285,
    equipment: 'Soccer ball, wall',
  },
  {
    id: 2022,
    name: 'Intermediate Small Ball Wall Mastery',
    categories: [
      'Ball Control',
      'Wall Kicking',
      'intermediate',
      'Individual Drill',
    ],
    sports: ['soccer'],
    difficulty: 'intermediate',
    skill: 'Precision Wall Kicking & First Touch',
    points: 50,
    instructions:
      'This 10-minute individual drill uses a small foam or tennis ball and a wall to sharpen your first touch, passing accuracy, and foot-eye coordination. The smaller ball demands more precision, making this a perfect challenge to take your skills to the next level.',
    equipment: '1 small foam ball or tennis ball, 1 wall',
    timeTotal: 600,
    timedInstructions: [
      {
        time: 0,
        text: "Alright superstar, ready for a big challenge with a small ball? Let's start the 'Wall Master' drill! For ten minutes, it's just you, the ball, and the wall. Let's go!",
      },
      {
        time: 10,
        text: "First, let's get a feel for this small ball. Stand a few steps from the wall. Let's do a two-touch pass. Pass with your right foot, and control the rebound with a soft touch. Go!",
      },
      {
        time: 21,
        text: "The foam ball comes back fast, right? Your first touch has to be extra soft, like a pillow. Let's see it again. Pass, and a perfect, soft stop.",
      },
      {
        time: 31,
        text: "Now, let's try one-touch passing. As soon as it comes back, send it right back to the wall with one touch. Find a rhythm.",
      },
      {
        time: 40,
        text: "It's like a rapid-fire rally! Let's see ten in a row with your right foot. Count them out!",
      },
      {
        time: 48,
        text: "Awesome! Now switch to your left foot. Let's start with two-touch first. Pass with your left, control with your left.",
      },
      {
        time: 57,
        text: "Good. Now, let's try the one-touch rally with your left foot. This is a great challenge!",
      },
      {
        time: 65,
        text: "One minute in! You're doing great. Let's alternate now. One-touch pass with your right, then one-touch with your left. Right, left, right, left.",
      },
      {
        time: 76,
        text: 'This is amazing for your coordination. Your brain and feet are working together perfectly.',
      },
      {
        time: 84,
        text: "Keep that rally going! Don't let the ball stop. You're a machine!",
      },
      {
        time: 91,
        text: "Let's go back to two-touch, but make your first touch a directional touch. Take it to the side before you pass.",
      },
      {
        time: 100,
        text: 'Pass to the wall, control it into space, then pass again. This is how you create space in a game.',
      },
      {
        time: 109,
        text: "Let's see that again. A sharp touch to the side. Excellent.",
      },
      {
        time: 115,
        text: "Two minutes in! Your control is looking sharp. Let's move on to aerial control.",
      },
      {
        time: 122,
        text: 'Toss the ball against the wall so it bounces back in the air. Control it with your thigh before it hits the ground. Go!',
      },
      {
        time: 131,
        text: "Good! The foam ball is tricky, so that thigh has to be extra soft. Let's see it again. Toss, and a perfect thigh trap.",
      },
      {
        time: 140,
        text: 'Now, after the thigh trap, let the ball drop and volley it back to the wall with one touch.',
      },
      {
        time: 148,
        text: "So it's: Toss, thigh control, and a sharp volley! Awesome combo.",
      },
      {
        time: 156,
        text: "Let's try that with your other leg. Left thigh control, then a left-foot volley. You can do it!",
      },
      {
        time: 164,
        text: "You're building your skills on both sides. This is fantastic.",
      },
      {
        time: 170,
        text: "Let's see that combo again. Make it your smoothest one yet.",
      },
      {
        time: 176,
        text: "Three minutes in! Let's try controlling the aerial ball with your chest.",
      },
      {
        time: 183,
        text: 'Toss it a little higher. Make a big chest, lean back, and let the ball cushion off you and drop to your feet.',
      },
      {
        time: 192,
        text: 'Perfect! Now, after the chest control, can you hit a half-volley? Let it bounce once, then strike it!',
      },
      {
        time: 201,
        text: "So it's: Toss, chest control, bounce, half-volley! A pro-level skill.",
      },
      {
        time: 209,
        text: "You're looking like a real superstar. Keep that focus.",
      },
      {
        time: 215,
        text: "Let's go back to the thigh control and volley. Keep those skills sharp.",
      },
      {
        time: 222,
        text: 'Excellent. Now another chest control and half-volley.',
      },
      {
        time: 228,
        text: "Four minutes in! You're doing amazing. Let's add some deception.",
      },
      {
        time: 235,
        text: 'Pass the ball to the wall. As it comes back, let it go through your legs! A sneaky dummy!',
      },
      {
        time: 243,
        text: "After it goes through, turn quickly, collect the ball, and pass it back to the wall. Let's see that trick.",
      },
      {
        time: 252,
        text: "Awesome! Now let's try a no-look pass. Look one way, but pass the ball to a different spot on the wall.",
      },
      {
        time: 260,
        text: 'This is all about tricking the defender. You have to be clever.',
      },
      {
        time: 266,
        text: "Let's see the dummy again. Make it a big fake.",
      },
      {
        time: 272,
        text: 'Now the no-look. Pure magic!',
      },
      {
        time: 277,
        text: 'Halfway! Five minutes down, five to go. Keep that energy high.',
      },
      {
        time: 283,
        text: "Let's learn a new move: 'The V-Turn Escape'. It's a great way to create space.",
      },
      {
        time: 290,
        text: 'Pass to the wall. As the ball comes back, stop it with the sole of your right foot. Now, pull it back towards you slightly.',
      },
      {
        time: 299,
        text: "With the inside of that SAME right foot, push the ball forward and to the side, making a 'V' shape. Then accelerate!",
      },
      {
        time: 308,
        text: "So it's: Wall pass, control, pull-push, and explode! Let's see it.",
      },
      {
        time: 315,
        text: 'Now try it with your left foot. Wall pass, control with your left, pull-push with your left. Awesome.',
      },
      {
        time: 324,
        text: 'This move is deadly in tight spaces. It invites the defender in, then you disappear.',
      },
      {
        time: 332,
        text: "Let's see five good V-Turns with your right foot. Go for it.",
      },
      {
        time: 338,
        text: 'Now five with your left. Build that confidence.',
      },
      {
        time: 344,
        text: "Seven minutes in! Let's combine our skills. Pass to the wall, control it with a directional touch.",
      },
      {
        time: 351,
        text: 'Then, do a quick Zidane spin, that 360-degree turn we practiced. Then pass back to the wall.',
      },
      {
        time: 359,
        text: "So it's: Wall pass, directional control, Zidane spin, wall pass. A full combo!",
      },
      {
        time: 367,
        text: "This is a huge challenge, but you're ready for it. Let's see it.",
      },
      {
        time: 374,
        text: "Awesome! Now let's try another combo. A wall pass, control it, then a Ronaldo Chop, and pass back.",
      },
      {
        time: 383,
        text: "You're linking your skills together. This is what the best players do.",
      },
      {
        time: 390,
        text: "Eight minutes in! Let's go back to the rapid-fire one-touch rally. How many can you get in a row?",
      },
      {
        time: 398,
        text: "Let's try to beat your record. Go for it!",
      },
      {
        time: 404,
        text: 'Keep that ball moving. Quick feet, soft touches.',
      },
      {
        time: 410,
        text: "Now, let's try a volley rally. Toss it up and try to keep it going with one-touch volleys.",
      },
      {
        time: 418,
        text: 'This is a great test of your control. Keep it up.',
      },
      {
        time: 424,
        text: "Nine minutes in! Let's get that weak foot working again.",
      },
      {
        time: 430,
        text: "A one-touch rally with your weak foot only. Let's see ten in a row.",
      },
      {
        time: 437,
        text: 'Excellent! Now a two-touch rally with your weak foot. Control, then pass.',
      },
      {
        time: 444,
        text: "You're making that other foot a real weapon. Great work.",
      },
      {
        time: 450,
        text: "Final minute! The final stretch. It's time for the 'Freestyle Finale'!",
      },
      {
        time: 457,
        text: "For this last part, you are the coach. Combine any of the skills we've worked on. Be creative!",
      },
      {
        time: 465,
        text: 'Show me a wall pass into a trick. Or an aerial control into a combo.',
      },
      {
        time: 472,
        text: "This is your time to shine. Show me what you've learned.",
      },
      {
        time: 478,
        text: "Let's see some of that flair. A little bit of magic!",
      },
      {
        time: 484,
        text: "Keep that energy high. You're almost at the end.",
      },
      {
        time: 490,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 496,
        text: 'Faster, sharper, more creative! Come on!',
      },
      {
        time: 502,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 508,
        text: 'Show me your best combination of the day!',
      },
      {
        time: 513,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one... AND TIME! You are a wall master!',
      },
      {
        time: 523,
        text: 'That was an incredible workout. Your touch and control with that small ball are amazing. Be proud of your hard work.',
      },
      {
        time: 532,
        text: "Let's do a quick cool down. Just walk around and shake out your legs. Take some deep breaths. You've earned it. Great job today!",
      },
    ],
  },
];

export default soccerDrills;
